import { useState, useMemo, useEffect, useCallback, Fragment, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { ENABLE_SSO, CLIENT_ID, TENANT_ID, AUTHORITY, SCOPES } from "./authConfig.js";
import { PublicClientApplication } from "@azure/msal-browser";
import * as XLSX from "xlsx-js-style";

// ============================================================================
// FirstParty INNsight — App.jsx
// Version: 1.13
// Branding: Innocean Data+Craft
// Date: April 2026
//
// v1.13 Changes:
// - Added Salesmatch 2025 tab using corrected dataset (salesmatch_corrected_2025)
//   - DM matchback fixed: dates derived from campaign names, matched via treated_campaign
//   - Carlos's program classification: MSP REG / MSP SE / Launch / Alt Fuel
//   - Multi-channel fractional attribution now works (10,584 Email+DM VINs)
//   - Loads from salesmatch_2025.json (Process_Salesmatch_2025_Dashboard.py)
//   - Program summary cards (clickable filters), channel badges, source tags
//   - Filterable by Program, Channel, Model, VIN search
//
// v1.12 Changes:
// - Added Salesmatch QA tab for sales attribution matchback analysis
//   - Summary cards: Unique Households, VINs, Raw Matches, Campaign count
//   - Attribution Window breakdown (30/60/90/180-Day)
//   - Multiselect filters: Campaign Code (with MessageName descriptions),
//     Model, Model Year, Attribution Window, Sale Type
//   - View By toggle: Campaign, Model, Model Year
//   - Sortable data table with per-window columns in Campaign view
//   - Multi-touch distribution chart
//   - Monthly trend bar chart (Unique HH by send month)
//   - Loads from separate salesmatch_qa.json (generated by
//     Process_Salesmatch_QA_Dashboard.py notebook)
//
// v1.11 Changes:
// - Added Year filter in header (ALL / 2024 / 2025 / 2026 / etc.)
//   - Available years derived from campaign firstSendDate/lastSendDate
//   - Applied alongside brand filter on Campaigns, Journeys, Wave Performance,
//     Audience (unaffected), Deliverability, and Link Analysis tabs
//   - Summary banner recomputes totals from filtered campaigns when year != ALL
//   - Link Analysis filters links to those belonging to filtered campaign codes
//   - URL param support: ?year=2025
//
// v1.10 Changes:
// - Added SSO authentication with Entra ID (same as Campaign INNsight)
// - UserBadge in header shows logged-in user info
// - Sign out button
// - Debug tooltip shows token claims on hover
//
// v1.9 Changes:
// - **Campaign Detail Panel now has tabbed interface:**
//   - "Overview" tab: metrics, wave/audience/vehicle breakdowns
//   - "Click Activity" tab: full link tracking table (like SFMC Tracking view)
// - Click Activity shows: Link URL, Total Clicks, Unique Clicks, Net CTR
// - Sortable columns (click headers to sort)
// - Search box to filter links
// - Links summary card on Overview with "View Click Activity" button
// - Net CTR calculation: Unique Clicks / Delivered × 100
//
// v1.8 Changes:
// - Complete rewrite of Links tab → "Link Analysis" tab
// - Now shows individual link URLs with categorization (like Campaign INNsight)
// - URL categories: Offers & Deals, Test Drive, Vehicle Page, EV/Electrified, etc.
// - Placement extraction from URL (Hero, Header, Footer, CTA, etc.)
// - Clicks by Category horizontal bar chart
// - Clicks by Placement horizontal bar chart
// - Category dropdown filter
// - Search box for link URLs and campaign codes
// - Sortable table with all link data
// - Requires Process_SFMC_First_Party_Campaigns v1.4+ for `links` array in JSON
//
// v1.7 Changes:
// - Added Link Breakdown section to Campaign Detail Panel
// - Shows top 10 links with click counts when linkBreakdown data is available
//
// v1.6 Changes:
// - Updated to Data+Craft branding (matching Campaign INNsight)
// - Added HyundaiLogo and GenesisLogo SVG components
// - Brand filter now uses logo buttons (pill style like Campaign INNsight)
// - Header shows Data+Craft compass logo
// - Attribution window colors now respond to brand selection
//
// v1.5 Changes:
// - Added GMA support — dashboard now shows both HMA and GMA campaigns
// - Brand filter pills (ALL / HMA / GMA) in header
// - Brand badges on campaign table rows and detail panel
// - All tabs respect brand filter
//
// v1.4 Changes:
// - Added Wave Performance tab per Carlos's request
//   - Groups campaigns by Journey → Wave (EM1/EM2/EM3) → Campaign
//   - Expandable tree view matching SFMC report format
//   - Metrics: Delivered, Clicks, Click Rate, Opt-Outs, Opt-Out Rate
//   - Opens/Open Rate excluded per privacy policy (Apple Mail changes)
//   - Audience badges (CO/HR/PO) on campaign rows
//   - Grand Total row at bottom
//
// v1.3 Changes:
// - Added Deliverability tab with bounce rate analysis
//   - Summary stats: sent, delivered, bounced, delivery rate, bounce rate
//   - Monthly bounce rate trend chart
//   - Bounce rate distribution histogram
//   - Sortable campaign table by bounce metrics
// - Added Links tab with click performance analysis
//   - Summary stats: opened, clicked, click rate, click-to-open rate
//   - Monthly click-to-open trend chart
//   - Top 10 campaigns by clicks chart
//   - Sortable campaign table with CTO rate
//   - Note: Individual link URLs require dm.dih_hyperlink_clicked integration
//
// v1.2 Changes:
// - Added opt-out metrics per Carlos's feedback
// - Summary banner shows total opt-outs
// - Campaign table has Opt-out column (sortable)
// - Detail panel shows opt-outs in metrics row
//
// HMA First Party Email Campaign Analytics
// Data Source: Heroku PostgreSQL (SFMC / DIH)
//
// Tabs:
// - Campaigns: SFMC campaign list with metrics
// - Journeys: Wave progression analysis (EM1 → EM2 → EM3)
// - Audience: Acxiom demographic profile visualization
// - Deliverability: Bounce/domain analysis (planned)
// - Links: Click tracking detail (planned)
//
// Deployment:
// - S3 Bucket: s3://ius-campaign-dashboard/first-party/
// - Build: npm run build → copy build/ contents to S3
// - CloudFront: Same distribution, /first-party/* path pattern
// ============================================================================

// ─────────────────────────────────────────────────────────────────────────────
// THEME & COLORS
// ─────────────────────────────────────────────────────────────────────────────
const COLORS = {
  bg: "#0a0f1a",
  card: "#0f172a",
  cardBorder: "#1e3a5f",
  accent: "#0ea5e9",      // Sky blue - differentiates from Campaign INNsight
  accentDark: "#0284c7",
  text: "#f1f5f9",
  textMuted: "#94a3b8",
  textDim: "#64748b",
  success: "#22c55e",
  warning: "#f59e0b",
  error: "#ef4444",
  purple: "#8b5cf6",
};

// Data+Craft logo
const DATA_CRAFT_LOGO = "data:image/jpeg;base64,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";

// Brand color palettes — Hyundai blue, Genesis copper (same as Campaign INNsight)
const BRAND_PALETTE = {
  HMA: { primary:"#002C5F", secondary:"#0073CF", accent:"#00AAD2", light:"#B4D7E8", muted:"#5B8DB1", bg:"#E8F1F8" },
  GMA: { primary:"#C36F51", secondary:"#1A1A1A", accent:"#AF6249", light:"#AAA8A6", muted:"#AAA8A6", bg:"#1A1A1A" },
  ALL: { primary:"#0ea5e9", secondary:"#7c3aed", accent:"#059669", light:"#93c5fd", muted:"#94a3b8", bg:"#1e293b" },
};
const bp = (brandFilter) => BRAND_PALETTE[brandFilter] || BRAND_PALETTE.ALL;

// Hyundai logo component (inline SVG)
function HyundaiLogo({ height = 18, fill = "#1e293b" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="2 11 44 26"
      height={height} width="auto" style={{ display:"block", flexShrink:0 }} aria-label="Hyundai">
      <path fill={fill} d="M41.469,15.056c-1.203-0.68-2.468-1.23-3.747-1.732c-5.727-2.089-12.459-2.817-18.856-1.991 c-5.895,0.859-11.622,2.348-16.115,6.687c-2.148,2.121-3.427,5.31-2.376,8.37c1.371,3.724,5.027,6.201,8.287,7.642 c9.641,4.144,22.755,3.999,32.092-0.745c3.198-1.717,6.778-4.566,7.219-8.646C48.353,20.237,44.698,16.935,41.469,15.056z M19.877,16.732c0.806-1.685,2.236-3.664,4.213-3.696c2.612-0.02,5.131,0.233,7.57,0.694c0.092,0.018,0.184,0.034,0.275,0.053 c0.259,0.051,0.516,0.108,0.773,0.163c1.35,0.302,2.659,0.676,3.915,1.116c0.077,0.05,0.147,0.109,0.197,0.198 c0.122,0.278-0.122,0.458-0.259,0.638c-3.148,2.649-6.752,4.121-10.433,5.413c-1.871,0.491-3.757,1.374-5.84,0.883 c-0.487-0.131-0.928-0.474-1.201-0.982C18.569,19.675,19.269,18.073,19.877,16.732z M7.148,30.501 c-0.187,0.189-0.372,0.207-0.554,0.147c-1.754-1.15-3.542-2.775-4.328-4.909c-0.088-0.257-0.141-0.514-0.185-0.769 c-0.014-0.083-0.029-0.167-0.04-0.251c-0.03-0.251-0.045-0.501-0.037-0.747c0-0.029,0.003-0.058,0.003-0.086 c0.085-1.757,1.057-3.365,2.133-4.427c3.381-3.266,7.549-4.738,11.763-5.59c0.173,0,0.35,0.021,0.43,0.182l-0.061,0.245 C12.273,19.184,9.642,24.859,7.148,30.501z M27.573,32.382c-0.774,1.291-1.979,2.419-3.432,2.613 c-0.057,0-0.112,0.005-0.169,0.005c-0.541,0-1.078-0.017-1.614-0.037c-0.313-0.013-0.625-0.03-0.937-0.05 c-0.636-0.039-1.267-0.092-1.893-0.16c-0.554-0.061-1.106-0.128-1.653-0.212c-0.368-0.056-0.733-0.118-1.096-0.184 c-0.488-0.088-0.97-0.191-1.451-0.296c-1.366-0.304-2.691-0.677-3.959-1.125c-0.062-0.051-0.12-0.108-0.159-0.178 c-0.076-0.229,0.031-0.442,0.182-0.589c3.361-2.928,7.361-4.285,11.315-5.708c1.475-0.458,3.042-0.949,4.699-0.719 c0.684,0.098,1.368,0.606,1.612,1.293C29.382,29.063,28.394,30.747,27.573,32.382z M45.989,24.118 c-0.008,0.294-0.04,0.593-0.102,0.899c-0.024,0.11-0.057,0.217-0.087,0.325c-0.03,0.11-0.06,0.221-0.095,0.328 c-0.903,2.675-3.615,4.629-5.859,5.834c-2.149,1.092-4.583,1.94-7.177,2.531c-0.209,0.032-0.414,0.077-0.627,0.099 c-0.122-0.016-0.319,0.016-0.35-0.18l0.045-0.229c2.768-3.353,4.866-7.114,6.707-10.925c0.851-1.717,1.643-3.483,2.372-5.217 c0.106-0.131,0.213-0.196,0.335-0.245c0.117,0.011,0.222,0.054,0.329,0.091c1.366,0.925,3.142,2.409,4.005,4.302 c0.014,0.031,0.028,0.063,0.041,0.094c0.073,0.166,0.138,0.336,0.195,0.509c0.077,0.24,0.148,0.481,0.192,0.731 c0.006,0.032,0.006,0.065,0.012,0.096c0.042,0.267,0.064,0.539,0.064,0.817C45.992,24.024,45.99,24.071,45.989,24.118z"/>
    </svg>
  );
}

// Genesis logo component (inline SVG - wing emblem)
function GenesisLogo({ height = 22, fill = "#1e293b", maxWidth }) {
  return (
    <svg id="Wing" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 425.2 85.04"
      height={height} width="auto" style={{ display:"block", flexShrink:0, maxWidth: maxWidth || "100%" }} aria-label="Genesis">
      <path fill={fill} d="M212.6,85.04c.3,0,.79-.1,1.18-.3,23.9-11.91,35.05-22.83,49.08-45.47,5.23-8.66,8.2-13.09,20.93-14.96,19.95-2.85,110-9.15,127.78-11.61,7.6-1.08,9.68-2.26,13.23-6,.59-.59.59-1.08-.49-.98-2.27.2-92.13,4.04-146.04,6-13.23.69-17.18.39-28.24-3.25C233.63,3.44,218.82,0,212.6,0s-21.03,3.44-37.42,8.46c-11.06,3.64-15.01,3.94-28.24,3.25C93.02,9.74,3.16,5.91.89,5.71c-1.09-.1-1.09.39-.49.98,3.55,3.74,5.63,4.92,13.23,6,17.77,2.46,107.83,8.76,127.78,11.61,12.74,1.87,15.7,6.3,20.93,14.96,14.02,22.64,25.18,33.56,49.08,45.47.39.2.89.3,1.18.3Z"/>
      <path fill={fill} d="M241.43,75c15.6-2.56,27.35-5.02,49.57-10.33.79-.2.79-.39-.1-.49-9.78-.59-18.07-1.18-23.9-1.67-1.38-.1-1.48-.59-.89-1.38,1.58-1.97,3.26-2.36,7.41-2.36,3.85,0,13.53.1,23.3.2,19.35.1,38.41-4.82,62.21-12.7.3-.1.2-.3-.1-.3-33.28.79-73.66,1.18-80.18,1.18-.99,0-.89-.69-.2-1.48,1.48-1.77,3.16-2.66,8.69-3.05,7.7-.59,73.37-3.74,84.13-4.13,12.74-.49,20.54-2.46,31.01-10.43,3.06-2.26,7.11-5.71,10.76-9.35.59-.59.59-.98-.49-.89-19.95,1.77-121.55,11.22-126.29,11.81-8.69,1.08-12.64,3.44-15.9,8.56-10.76,16.73-15.5,24.9-29.52,36.02-.59.49-.59.98.49.79Z"/>
      <path fill="#000" d="M212.6,78.84c-.2,0-.49,0-.99-.3-19.45-11.61-39.3-29.53-43.35-50.39-1.38-7.87,1.58-9.35,6.52-11.12,8.89-3.25,29.33-9.55,37.82-9.55s28.93,6.3,37.82,9.55c4.94,1.77,7.9,3.25,6.52,11.12-4.05,20.87-23.9,38.78-43.35,50.39-.49.3-.79.3-.99.3Z"/>
      <path fill={fill} d="M183.77,75c1.09.2,1.09-.3.49-.79-14.02-11.12-18.76-19.29-29.52-36.02-3.26-5.12-7.21-7.48-15.9-8.56-4.74-.59-106.35-10.04-126.29-11.81-1.09-.1-1.09.3-.49.89,3.65,3.64,7.7,7.09,10.76,9.35,10.47,7.97,18.27,9.94,31.01,10.43,10.76.39,76.43,3.54,84.13,4.13,5.53.39,7.21,1.28,8.69,3.05.69.79.79,1.48-.2,1.48-6.52,0-46.9-.39-80.18-1.18-.3,0-.39.2-.1.3,23.8,7.87,42.86,12.8,62.21,12.7,9.78-.1,19.45-.2,23.3-.2,4.15,0,5.83.39,7.41,2.36.59.79.49,1.28-.89,1.38-5.83.49-14.12,1.08-23.9,1.67-.89.1-.89.3-.1.49,22.22,5.31,33.97,7.78,49.57,10.33Z"/>
    </svg>
  );
}

// ── Microsoft Entra SSO via MSAL ────────────────────────────────────────
const ssoEnabled = ENABLE_SSO && CLIENT_ID !== "YOUR-CLIENT-ID-HERE";

const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: AUTHORITY,
    redirectUri: window.location.origin,
  },
  cache: { cacheLocation: "sessionStorage" },
};

const msalInstance = ssoEnabled ? new PublicClientApplication(msalConfig) : null;

// ── User extraction from MSAL account ──
function extractUser(account) {
  const claims = account.idTokenClaims || {};
  return {
    name: account.name,
    email: account.username,
    claims: claims,
  };
}

// ── Auth Gate component ──
function AuthGate({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!msalInstance) { setReady(true); return; }

    msalInstance.initialize()
      .then(() => msalInstance.handleRedirectPromise())
      .then((response) => {
        if (response && response.account) {
          setUser(extractUser(response.account));
        } else {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            setUser(extractUser(accounts[0]));
          }
        }
        setReady(true);
      })
      .catch((err) => {
        console.error("MSAL init error:", err);
        setError(err.message);
        setReady(true);
      });
  }, []);

  const login = useCallback(() => {
    if (!msalInstance) return;
    msalInstance.loginRedirect({ scopes: SCOPES }).catch((err) => {
      console.error("Login error:", err);
      setError(err.message);
    });
  }, []);

  const logout = useCallback(() => {
    if (!msalInstance) return;
    msalInstance.logoutRedirect().catch((err) => {
      console.error("Logout error:", err);
    });
  }, []);

  if (!ready) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#1e293b", color:"#f1f5f9" }}>
        <div style={{ color:"#94a3b8", fontSize:16 }}>Connecting to Innocean USA...</div>
      </div>
    );
  }

  // SSO disabled — render without auth
  if (!msalInstance) return children({ user: null, logout: null });

  // Not logged in — show login screen
  if (!user) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", fontFamily:"'DM Sans', sans-serif", padding:20, color:"#f1f5f9" }}>
        <div style={{ textAlign:"center", maxWidth:420 }}>
          <div style={{ marginBottom:20 }}>
            <img src={DATA_CRAFT_LOGO} alt="Data+Craft" style={{ width:80, height:80 }} />
          </div>
          <h1 style={{ fontSize:26, fontWeight:700, marginBottom:8 }}>FirstParty INNsight</h1>
          <p style={{ color:"#94a3b8", marginBottom:24 }}>HMA + GMA Direct Email Analytics</p>
          {error && <div style={{ background:"rgba(239,68,68,0.1)", border:"1px solid #ef4444", borderRadius:8, padding:12, marginBottom:16, fontSize:13, color:"#fca5a5" }}>{error}</div>}
          <button onClick={login}
            style={{ padding:"14px 32px", fontSize:15, fontWeight:600, border:"none", borderRadius:8, cursor:"pointer", background:"#2563eb", color:"#f1f5f9", transition:"all 0.2s" }}
            onMouseOver={e => e.target.style.background = "#1d4ed8"}
            onMouseOut={e => e.target.style.background = "#2563eb"}>
            Sign in with your Innocean account
          </button>
          <p style={{ color:"#64748b", fontSize:12, marginTop:16 }}>Use your Innocean USA credentials</p>
        </div>
      </div>
    );
  }

  return children({ user, logout });
}

// ── User Badge component ──
function UserBadge({ user, logout }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((e) => {
    e.stopPropagation();
    const claims = user?.claims || {};
    const text = [
      `Name:    ${claims.name || user.name}`,
      `Email:   ${claims.preferred_username || user.email}`,
      `Tenant:  ${claims.tid || "—"}`,
      `Issued:  ${claims.iat  ? new Date(claims.iat  * 1000).toLocaleString() : "—"}`,
      `Expires: ${claims.exp  ? new Date(claims.exp  * 1000).toLocaleString() : "—"}`,
      ``,
      `Full claims:`,
      JSON.stringify(claims, null, 2),
    ].join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [user]);

  const claims = user?.claims || {};

  const tooltipRows = [
    ["Name",    claims.name || user.name],
    ["Email",   claims.preferred_username || user.email],
    ["Tenant",  claims.tid || "—"],
    ["Issued",  claims.iat  ? new Date(claims.iat  * 1000).toLocaleString() : "—"],
    ["Expires", claims.exp  ? new Date(claims.exp  * 1000).toLocaleString() : "—"],
  ];

  return (
    <div style={{ position:"relative", display:"flex", alignItems:"center", gap:8 }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}>
      <div style={{ width:28, height:28, borderRadius:14, background:"#2563eb", display:"flex",
        alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#f1f5f9",
        cursor:"default", userSelect:"none" }}>
        {user.name.split(" ").map(n=>n[0]).join("").slice(0,2)}
      </div>
      <span style={{ fontSize:12, color:"#cbd5e1" }}>{user.name}</span>
      {logout && (
        <button onClick={logout}
          style={{ padding:"4px 10px", background:"none", border:"1px solid #475569",
            borderRadius:4, fontSize:11, color:"#64748b", cursor:"pointer" }}>
          Sign out
        </button>
      )}
      {showTooltip && (
        <div style={{ position:"absolute", top:"calc(100% + 8px)", right:0, zIndex:9999,
          background:"#1e293b", border:"1px solid #334155", borderRadius:8,
          padding:"12px 14px", minWidth:300, boxShadow:"0 8px 24px rgba(0,0,0,0.5)",
          fontFamily:"'DM Mono', 'Fira Code', monospace", fontSize:11, lineHeight:1.7 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
            <div style={{ fontSize:10, fontWeight:700, color:"#94a3b8", textTransform:"uppercase",
              letterSpacing:"0.08em" }}>Token Claims</div>
            <button onClick={handleCopy}
              style={{ padding:"2px 8px", background: copied ? "rgba(52,211,153,0.15)" : "rgba(255,255,255,0.06)",
                border: copied ? "1px solid #34d399" : "1px solid #334155",
                borderRadius:4, fontSize:10, color: copied ? "#34d399" : "#94a3b8",
                cursor:"pointer", transition:"all 0.2s", fontFamily:"inherit" }}>
              {copied ? "✓ Copied" : "Copy"}
            </button>
          </div>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <tbody>
              {tooltipRows.map(([label, value]) => (
                <tr key={label}>
                  <td style={{ color:"#94a3b8", paddingRight:12, whiteSpace:"nowrap",
                    verticalAlign:"top", paddingBottom:2 }}>{label}</td>
                  <td style={{ color:"#cbd5e1", wordBreak:"break-all" }}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const AUDIENCE_COLORS = {
  HR: "#22c55e",  // Hand Raisers - Green
  CO: "#f59e0b",  // Current Owners - Amber
  PO: "#8b5cf6",  // Previous Owners - Purple
  Other: "#64748b"
};


// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// SALESMATCH QA TAB — v3.2
// VIN-level fact table → true COUNT(DISTINCT) via JS Sets
// Expandable trees: Model→Trim, CampaignType→Campaign, CampaignGroup→Campaign
// Dual classification source: Heroku / Chalsea toggle
// Attribution window mode: Exclusive (bucket) or Cumulative (≤N days)
// Fractional attribution column
// ─────────────────────────────────────────────────────────────────────────────

function SmMultiSelect({ selected, onChange, options, noun = "Items", labelFn, mobile }) {
  const ref = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(""); } };
    document.addEventListener("mousedown", h); document.addEventListener("touchstart", h);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("touchstart", h); };
  }, [open]);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);
  const toggle = (opt) => { const n = new Set(selected); n.has(opt) ? n.delete(opt) : n.add(opt); onChange(n); };
  const clearAll = (e) => { e.stopPropagation(); onChange(new Set()); };
  const filtered = options.filter(o => String(labelFn ? labelFn(o) : o).toLowerCase().includes(search.toLowerCase()));
  const count = selected.size;
  const label = count === 0 ? `All ${noun}` : count === 1 ? String(labelFn ? labelFn([...selected][0]) : [...selected][0]).slice(0,30) : `${count} ${noun}`;
  return (
    <div ref={ref} style={{ position:"relative", userSelect:"none" }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 10px", background: count > 0 ? COLORS.accent : "#0f172a", border:`1px solid ${count > 0 ? COLORS.accent : "#334155"}`, borderRadius:6, fontSize:13, color:"#f1f5f9", cursor:"pointer", minWidth:100, whiteSpace:"nowrap" }}>
        <span style={{ flex:1, textAlign:"left", maxWidth:180, overflow:"hidden", textOverflow:"ellipsis" }}>{label}</span>
        {count > 0 && <span onClick={clearAll} style={{ display:"flex", alignItems:"center", justifyContent:"center", width:16, height:16, borderRadius:"50%", background:"rgba(255,255,255,0.25)", fontSize:10, fontWeight:700, flexShrink:0 }}>✕</span>}
        <span style={{ fontSize:10, opacity:0.7, marginLeft:2 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, zIndex:200, background:"#1e293b", border:"1px solid #334155", borderRadius:10, boxShadow:"0 8px 32px rgba(0,0,0,0.5)", width: mobile ? "calc(100vw - 32px)" : 280, maxWidth:340, overflow:"hidden" }}>
          <div style={{ padding:"10px 10px 6px", borderBottom:"1px solid #334155" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, background:"#0f172a", border:"1px solid #334155", borderRadius:6, padding:"5px 10px" }}>
              <span style={{ color:"#64748b", fontSize:13 }}>🔍</span>
              <input ref={inputRef} value={search} onChange={e=>setSearch(e.target.value)} placeholder={`Search ${noun.toLowerCase()}…`} style={{ background:"transparent", border:"none", outline:"none", color:"#f1f5f9", fontSize:13, flex:1, minWidth:0 }} />
              {search && <span onClick={()=>setSearch("")} style={{ color:"#64748b", cursor:"pointer", fontSize:12 }}>✕</span>}
            </div>
          </div>
          <div style={{ maxHeight:260, overflowY:"auto", padding:"6px 8px", display:"flex", flexWrap:"wrap", gap:6 }}>
            {filtered.length === 0 && <div style={{ color:"#64748b", fontSize:12, padding:"8px 4px", width:"100%" }}>No matches</div>}
            {filtered.map(opt => {
              const active = selected.has(opt);
              return (<button key={opt} onClick={() => toggle(opt)} style={{ padding:"5px 12px", borderRadius:20, background: active ? COLORS.accent : "#0f172a", border:`1px solid ${active ? COLORS.accent : "#334155"}`, color: active ? "#fff" : "#94a3b8", fontSize:12, fontWeight: active ? 600 : 400, cursor:"pointer", whiteSpace:"nowrap", maxWidth:260, overflow:"hidden", textOverflow:"ellipsis" }}>{labelFn ? labelFn(opt) : opt}</button>);
            })}
          </div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", borderTop:"1px solid #334155", background:"#162032" }}>
            <span style={{ fontSize:11, color:"#64748b" }}>{count > 0 ? `${count} selected` : "All showing"}</span>
            <div style={{ display:"flex", gap:8 }}>
              {count > 0 && <button onClick={()=>onChange(new Set())} style={{ fontSize:11, color:"#94a3b8", background:"none", border:"none", cursor:"pointer" }}>Clear</button>}
              <button onClick={()=>{ setOpen(false); setSearch(""); }} style={{ fontSize:12, fontWeight:600, color:"#fff", background:COLORS.accent, border:"none", borderRadius:5, padding:"4px 14px", cursor:"pointer" }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SalesmatchQATab({ data, mobile, variant, brandFilter: parentBrandFilter }) {
  const is2025 = variant === "2025";
  const [campaignFilter, setCampaignFilter] = useState(new Set());
  const [modelFilter, setModelFilter] = useState(new Set());
  const [yearFilter, setYearFilter] = useState(new Set());
  const [trimFilter, setTrimFilter] = useState(new Set());
  const [messageFilter, setMessageFilter] = useState(new Set());
  const [purposeFilter, setPurposeFilter] = useState(new Set());
  const [typeFilter, setTypeFilter] = useState(new Set());
  const [channelFilter, setChannelFilter] = useState(new Set());
  const [windowFilter, setWindowFilter] = useState("ALL");
  const [windowMode, setWindowMode] = useState("cumulative"); // "exclusive" or "cumulative"
  const [sentStart, setSentStart] = useState("");
  const [sentEnd, setSentEnd] = useState("");
  const [purchaseStart, setPurchaseStart] = useState("");
  const [purchaseEnd, setPurchaseEnd] = useState("");
  const [viewBy, setViewBy] = useState("campaign");
  const [sortCol, setSortCol] = useState("sentDate");
  const [sortDir, setSortDir] = useState(1);
  const [expanded, setExpanded] = useState(new Set());
  const [classSource, setClassSource] = useState("heroku");
  const [showInfo, setShowInfo] = useState(false);
  const [showTech, setShowTech] = useState(false);
  const [expandedDates, setExpandedDates] = useState(null); // campaign code whose dates are shown
  const [salesOnly, setSalesOnly] = useState(is2025 ? false : true);
  const [selectedCampCode, setSelectedCampCode] = useState("");

  const ready = !!(data && data.v >= 3);
  const rows = (ready && data.rows) || [];
  const fv = (ready && data.filterValues) || { campaigns:[], messageNames:[], models:[], modelYears:[], trims:[], windows:[] };
  const summary = (ready && data.summary) || {};
  const headlines = (ready && data.headlines) || {};
  const campClass = (ready && data.campaignClass) || {};
  const ctrlData = (ready && data.ctrl) || {};  // { code: [treated_unique_hh, treated_purchased_hh, control_pop, control_purchased_hh] }
  const segCompData = (ready && data.segComp) || [];  // [[seg, decile, t_pop, c_pop, c_purch], ...]
  const campSegData = (ready && data.campSeg) || {};  // { code: { "CO|D1-2": treated_hh, ... } }
  const campCtrlSegData = (ready && data.campCtrlSeg) || {};  // { code: { "CO|D1-2": c_pop, ... } }
  const campCtrlSegPurchData = (ready && data.campCtrlSegPurch) || {};  // { code: { "CO|D1-2": c_purch, ... } }
  const campTreatedSegPurchData = (ready && data.campTreatedSegPurch) || {};  // { code: { "CO|D1-2": t_purch, ... } }

  // Window thresholds for cumulative mode
  const WIN_THRESHOLDS = { "30-Day": 30, "60-Day": 60, "90-Day": 90, "180-Day": 180 };

  const C = useMemo(() => { const m = {}; ((ready && data.cols) || []).forEach((c, i) => m[c] = i); return m; }, [ready, data]);
  const campLabel = useMemo(() => (ready && data.campaignLookup && typeof data.campaignLookup === "object" && !Array.isArray(data.campaignLookup)) ? data.campaignLookup : {}, [ready, data]);

  // Brand-filtered rows — the base dataset before any other filters
  // All filter option lists and headline counts derive from this
  const brandRows = useMemo(() => {
    if (!ready) return [];
    if (!parentBrandFilter || parentBrandFilter === "ALL" || C.brand === undefined) return rows;
    return rows.filter(r => r[C.brand] === parentBrandFilter);
  }, [ready, rows, C, parentBrandFilter]);

  // Derive filter options from brand-filtered rows (so dropdowns only show relevant values)
  const brandFilterOptions = useMemo(() => {
    if (!ready) return { campaigns: [], models: [], modelYears: [], trims: [], windows: [] };
    const campaigns = new Set();
    const models = new Set();
    const modelYears = new Set();
    const trims = new Set();
    const windows = new Set();
    for (const r of brandRows) {
      (r[C.cc] || "").split("|").forEach(c => { if (c) campaigns.add(c); });
      if (r[C.model]) models.add(r[C.model]);
      if (r[C.my]) modelYears.add(r[C.my]);
      if (r[C.trim]) trims.add(r[C.trim]);
      (r[C.aw] || "").split("|").forEach(w => { if (w) windows.add(w); });
    }
    return {
      campaigns: [...campaigns].sort(),
      models: [...models].sort(),
      modelYears: [...modelYears].sort(),
      trims: [...trims].sort(),
      windows: [...windows].sort(),
    };
  }, [ready, brandRows, C]);

  // Brand-aware headline counts (VINs/HH/Groups per attribution window)
  const brandHeadlines = useMemo(() => {
    if (!ready) return {};
    // If no brand filter or no brand column, use the original headlines from JSON
    if (!parentBrandFilter || parentBrandFilter === "ALL" || C.brand === undefined) return headlines;
    // Recompute from brandRows
    const hl = {};
    const vinsByWindow = {};
    const hhByWindow = {};
    const grpByWindow = {};
    const rawByWindow = {};
    for (const r of brandRows) {
      const awList = (r[C.aw] || "").split("|");
      for (const w of awList) {
        if (!w) continue;
        if (!vinsByWindow[w]) { vinsByWindow[w] = new Set(); hhByWindow[w] = new Set(); grpByWindow[w] = new Set(); rawByWindow[w] = 0; }
        vinsByWindow[w].add(r[C.vin]);
        hhByWindow[w].add(r[C.hh]);
        grpByWindow[w].add(r[C.grp]);
        rawByWindow[w]++;
      }
    }
    for (const w of Object.keys(vinsByWindow)) {
      hl[w] = { vins: vinsByWindow[w].size, hh: hhByWindow[w].size, grp: grpByWindow[w].size, raw: rawByWindow[w] };
    }
    return hl;
  }, [ready, brandRows, C, parentBrandFilter, headlines]);

  // Clear filters that might reference values not present in new brand's dataset
  const prevBrandRef = useRef(parentBrandFilter);
  useEffect(() => {
    if (prevBrandRef.current !== parentBrandFilter) {
      prevBrandRef.current = parentBrandFilter;
      setCampaignFilter(new Set());
      setModelFilter(new Set());
      setYearFilter(new Set());
      setTrimFilter(new Set());
      setMessageFilter(new Set());
      setPurposeFilter(new Set());
      setTypeFilter(new Set());
      setChannelFilter(new Set());
      setWindowFilter("ALL");
      setSentStart("");
      setSentEnd("");
      setPurchaseStart("");
      setPurchaseEnd("");
    }
  }, [parentBrandFilter]);

  const codeClass = useMemo(() => {
    const m = {};
    Object.entries(campClass).forEach(([code, cl]) => {
      if (classSource === "chalsea" && cl.c) {
        m[code] = { purpose: cl.c[0], type: cl.c[1], group: (cl.h || [])[2] || "Unknown", isSales: (cl.h || [])[3] !== false, channel: (cl.h || [])[4] || "Email" };
      } else {
        m[code] = { purpose: (cl.h || [])[0] || "Unknown", type: (cl.h || [])[1] || "Unknown", group: (cl.h || [])[2] || "Unknown", isSales: (cl.h || [])[3] !== false, channel: (cl.h || [])[4] || "Email" };
      }
    });
    return m;
  }, [campClass, classSource]);

  const purposeOptions = useMemo(() => {
    const brandCodes = new Set();
    for (const r of brandRows) (r[C.cc] || "").split("|").forEach(c => brandCodes.add(c));
    return [...new Set([...brandCodes].map(c => (codeClass[c] || {}).purpose).filter(Boolean))].sort();
  }, [codeClass, brandRows, C]);
  const typeOptions = useMemo(() => {
    const brandCodes = new Set();
    for (const r of brandRows) (r[C.cc] || "").split("|").forEach(c => brandCodes.add(c));
    return [...new Set([...brandCodes].map(c => (codeClass[c] || {}).type).filter(Boolean))].sort();
  }, [codeClass, brandRows, C]);
  const channelOptions = useMemo(() => {
    const brandCodes = new Set();
    for (const r of brandRows) (r[C.cc] || "").split("|").forEach(c => brandCodes.add(c));
    return [...new Set([...brandCodes].map(c => (codeClass[c] || {}).channel).filter(Boolean))].sort();
  }, [codeClass, brandRows, C]);

  const matchesCampClass = useMemo(() => {
    if (purposeFilter.size === 0 && typeFilter.size === 0 && channelFilter.size === 0 && !salesOnly) return null;
    return (ccStr) => {
      const codes = (ccStr || "").split("|");
      return codes.some(code => {
        const cl = codeClass[code];
        if (!cl) return false;
        if (salesOnly && !cl.isSales) return false;
        if (purposeFilter.size > 0 && !purposeFilter.has(cl.purpose)) return false;
        if (typeFilter.size > 0 && !typeFilter.has(cl.type)) return false;
        if (channelFilter.size > 0 && !channelFilter.has(cl.channel)) return false;
        return true;
      });
    };
  }, [purposeFilter, typeFilter, channelFilter, salesOnly, codeClass]);

  // Filter VIN-level rows — window logic differs by mode
  const filtered = useMemo(() => {
    if (!ready) return [];
    return rows.filter(r => {
      // Brand filter — from parent header pills (v4+ data has brand column)
      if (parentBrandFilter && parentBrandFilter !== "ALL" && C.brand !== undefined) {
        if (r[C.brand] !== parentBrandFilter) return false;
      }
      if (campaignFilter.size > 0) { if (!(r[C.cc]||"").split("|").some(c => campaignFilter.has(c))) return false; }
      if (messageFilter.size > 0) { if (!(r[C.mn]||"").split("|").some(m => messageFilter.has(m))) return false; }
      if (matchesCampClass && !matchesCampClass(r[C.cc])) return false;
      if (modelFilter.size > 0 && !modelFilter.has(r[C.model])) return false;
      if (yearFilter.size > 0 && !yearFilter.has(r[C.my])) return false;
      if (trimFilter.size > 0 && !trimFilter.has(r[C.trim])) return false;
      // Attribution window filter
      if (windowFilter !== "ALL") {
        const md = r[C.md] || 999;
        if (windowMode === "cumulative") {
          const threshold = WIN_THRESHOLDS[windowFilter] || 180;
          if (md > threshold) return false;
        } else {
          if (windowFilter === "30-Day" && !(md <= 30)) return false;
          if (windowFilter === "60-Day" && !(md > 30 && md <= 60)) return false;
          if (windowFilter === "90-Day" && !(md > 60 && md <= 90)) return false;
          if (windowFilter === "180-Day" && !(md > 90 && md <= 180)) return false;
        }
      }
      // Sent date filtering is NOT applied at the VIN level.
      // It is applied per-campaign during aggregation via filterCodes.
      // This ensures fractional values remain stable regardless of date range selection.
      if (purchaseStart && r[C.pd] < purchaseStart) return false;
      if (purchaseEnd && r[C.pd] > purchaseEnd) return false;
      return true;
    });
  }, [ready, rows, C, campaignFilter, messageFilter, matchesCampClass, modelFilter, yearFilter, trimFilter, windowFilter, windowMode, purchaseStart, purchaseEnd, parentBrandFilter]);

  // Filter campaign codes for DISPLAY: exclude non-sales when salesOnly, exclude campaigns outside sent date range, filter by channel
  const filterCodes = useCallback((ccStr, csdStr) => {
    const codes = (ccStr || "").split("|");
    if (!salesOnly && !sentStart && !sentEnd && channelFilter.size === 0 && typeFilter.size === 0 && purposeFilter.size === 0) return codes;
    // Build a map of code → sent date from csd
    const csdMap = {};
    if (csdStr && (sentStart || sentEnd)) {
      csdStr.split("|").forEach(pair => {
        const sep = pair.indexOf(":");
        if (sep > 0) { const c = pair.slice(0, sep), d = pair.slice(sep + 1); if (!csdMap[c] || d < csdMap[c]) csdMap[c] = d; }
      });
    }
    return codes.filter(c => {
      const cl = codeClass[c];
      if (salesOnly) { if (!cl || !cl.isSales) return false; }
      if (channelFilter.size > 0) { if (!cl || !channelFilter.has(cl.channel)) return false; }
      if (typeFilter.size > 0) { if (!cl || !typeFilter.has(cl.type)) return false; }
      if (purposeFilter.size > 0) { if (!cl || !purposeFilter.has(cl.purpose)) return false; }
      if ((sentStart || sentEnd) && csdMap[c]) {
        if (sentStart && csdMap[c] < sentStart) return false;
        if (sentEnd && csdMap[c] > sentEnd) return false;
      }
      return true;
    });
  }, [salesOnly, sentStart, sentEnd, channelFilter, typeFilter, purposeFilter, codeClass]);

  // Totals — computed from filtered rows that have at least one campaign matching filterCodes
  const totals = useMemo(() => {
    const vs = new Set(), hs = new Set(), gs = new Set();
    let raw = 0;
    filtered.forEach(r => {
      const codes = filterCodes(r[C.cc], r[C.csd]);
      if (codes.length === 0) return; // skip VINs with no campaigns in date range
      vs.add(r[C.vin]); hs.add(r[C.hh]); gs.add(r[C.grp]); raw++;
    });
    return { vins: vs.size, hh: hs.size, grp: gs.size, raw };
  }, [filtered, C, filterCodes]);

  // Window pill counts — apply all filters EXCEPT window, then compute per-window VINs
  const windowPillCounts = useMemo(() => {
    if (!ready) return {};
    // Apply all non-window filters first (starting from brandRows, not rows)
    const base = brandRows.filter(r => {
      if (campaignFilter.size > 0) { if (!(r[C.cc]||"").split("|").some(c => campaignFilter.has(c))) return false; }
      if (messageFilter.size > 0) { if (!(r[C.mn]||"").split("|").some(m => messageFilter.has(m))) return false; }
      if (matchesCampClass && !matchesCampClass(r[C.cc])) return false;
      if (modelFilter.size > 0 && !modelFilter.has(r[C.model])) return false;
      if (yearFilter.size > 0 && !yearFilter.has(r[C.my])) return false;
      if (trimFilter.size > 0 && !trimFilter.has(r[C.trim])) return false;
      if (purchaseStart && r[C.pd] < purchaseStart) return false;
      if (purchaseEnd && r[C.pd] > purchaseEnd) return false;
      return true;
    });
    const out = { ALL: new Set() };
    base.forEach(r => out.ALL.add(r[C.vin]));
    if (windowMode === "cumulative") {
      // Each pill shows VINs with minDays <= threshold
      [30, 60, 90, 180].forEach(t => {
        const s = new Set();
        base.forEach(r => { if ((r[C.md] || 999) <= t) s.add(r[C.vin]); });
        out[`${t}-Day`] = s;
      });
    } else {
      // Exclusive: mutually exclusive buckets based on minDaysToPurchase per VIN
      const buckets = { "30-Day": [0, 30], "60-Day": [31, 60], "90-Day": [61, 90], "180-Day": [91, 180] };
      const vinMd = {}; // track min md per VIN
      base.forEach(r => {
        const vin = r[C.vin];
        const md = r[C.md] || 999;
        if (vinMd[vin] === undefined || md < vinMd[vin]) vinMd[vin] = md;
      });
      Object.entries(buckets).forEach(([w, range]) => {
        const s = new Set();
        Object.entries(vinMd).forEach(([vin, md]) => {
          if (md >= range[0] && md <= range[1]) s.add(vin);
        });
        out[w] = s;
      });
    }
    const counts = {};
    Object.entries(out).forEach(([w, s]) => { counts[w] = s.size; });
    return counts;
  }, [ready, brandRows, C, windowMode, campaignFilter, messageFilter, matchesCampClass, modelFilter, yearFilter, trimFilter, purposeFilter, typeFilter, purchaseStart, purchaseEnd]);

  // Pre-compute the FULL campaign set per VIN — used for fractional denominator
  // Only filtered by salesOnly and channelFilter, NOT by sent date range.
  const vinTotalCamps = useMemo(() => {
    const m = {};
    filtered.forEach(r => {
      const vin = r[C.vin];
      if (!m[vin]) m[vin] = new Set();
      const codes = (r[C.cc] || "").split("|");
      codes.forEach(c => {
        const cl = codeClass[c];
        if (salesOnly) { if (!cl || !cl.isSales) return; }
        if (channelFilter.size > 0) { if (!cl || !channelFilter.has(cl.channel)) return; }
        m[vin].add(c);
      });
    });
    return m;
  }, [filtered, C, salesOnly, channelFilter, codeClass]);

  // Aggregate helpers
  // For campaign-sharing views (campaign, campaignType, campaignGroup, message):
  //   fractional = 1/n_total_campaigns per unique VIN (splits credit across campaigns)
  // For one-to-one views (model, modelYear):
  //   fractional = 1.0 per unique VIN (each VIN belongs to exactly one model/year)
  const buildAgg = (key, agg, r, shareMode) => {
    if (!agg[key]) agg[key] = { key, vs: new Set(), hs: new Set(), gs: new Set(), raw: 0, frac: 0, fracVins: new Set() };
    agg[key].vs.add(r[C.vin]); agg[key].hs.add(r[C.hh]); agg[key].gs.add(r[C.grp]); agg[key].raw++;
    if (!agg[key].fracVins.has(r[C.vin])) {
      agg[key].fracVins.add(r[C.vin]);
      if (shareMode === "split") {
        const totalCamps = (vinTotalCamps[r[C.vin]] || new Set()).size || 1;
        agg[key].frac += 1 / totalCamps;
      } else {
        agg[key].frac += 1; // 1:1 — each VIN contributes 1.0 to its group
      }
    }
  };
  const finishAgg = (agg) => Object.values(agg).map(a => ({ key: a.key, vins: a.vs.size, hh: a.hs.size, grp: a.gs.size, raw: a.raw, frac: Math.round(a.frac * 100) / 100, sentDate: a.sentMin || "", channel: a.channel || "" }));

  // Build campaign → sent date lookup from csd field
  const campSentDates = useMemo(() => {
    if (!ready) return {};
    const m = {};
    rows.forEach(r => {
      const csd = r[C.csd];
      if (!csd) return;
      csd.split("|").forEach(pair => {
        const sep = pair.indexOf(":");
        if (sep > 0) {
          const code = pair.slice(0, sep), dt = pair.slice(sep + 1);
          if (!m[code]) m[code] = { min: dt, max: dt, dates: new Set() };
          if (dt < m[code].min) m[code].min = dt;
          if (dt > m[code].max) m[code].max = dt;
          m[code].dates.add(dt);
        }
      });
    });
    // Convert Sets to sorted arrays
    Object.values(m).forEach(v => { v.dates = [...v.dates].sort(); });
    return m;
  }, [ready, rows, C]);

  const aggregated = useMemo(() => {
    if (!ready) return [];
    const agg = {};
    filtered.forEach(r => {
      const codes = filterCodes(r[C.cc], r[C.csd]);
      if (codes.length === 0) return;
      if (viewBy === "campaign") {
        codes.forEach(c => {
          buildAgg(c, agg, r, "split");
          // Attach sent date and channel from lookup
          if (agg[c] && !agg[c].sentMin) {
            const sd = campSentDates[c];
            agg[c].sentMin = sd ? sd.min : "";
            agg[c].sentMax = sd ? sd.max : "";
            agg[c].channel = (codeClass[c] || {}).channel || "Email";
          }
        });
      }
      else if (viewBy === "model") { buildAgg(r[C.model] || "Unknown", agg, r, "whole"); }
      else if (viewBy === "modelYear") { buildAgg(r[C.my] || "Unknown", agg, r, "whole"); }
      else if (viewBy === "message") {
        // Iterate campaign codes and look up message name for each code.
        // This ensures fractional sums to total VINs: if 2 codes share a message,
        // that message gets 2/totalCamps credit (not 1/totalCamps from fracVins dedup).
        codes.forEach(c => {
          const mn = (data.campaignLookup || {})[c] || c;
          if (!agg[mn]) agg[mn] = { key: mn, vs: new Set(), hs: new Set(), gs: new Set(), raw: 0, frac: 0, fracCodes: new Set() };
          agg[mn].vs.add(r[C.vin]); agg[mn].hs.add(r[C.hh]); agg[mn].gs.add(r[C.grp]); agg[mn].raw++;
          const codeVinKey = c + "|" + r[C.vin];
          if (!agg[mn].fracCodes.has(codeVinKey)) {
            agg[mn].fracCodes.add(codeVinKey);
            const totalCamps = (vinTotalCamps[r[C.vin]] || new Set()).size || 1;
            agg[mn].frac += 1 / totalCamps;
          }
        });
      }
      else if (viewBy === "campaignType") { const types = new Set(codes.map(c => (codeClass[c] || {}).type || "Unknown")); types.forEach(t => buildAgg(t, agg, r, "split")); }
      else if (viewBy === "campaignGroup") { const groups = new Set(codes.map(c => (codeClass[c] || {}).group || "Unknown")); groups.forEach(g => buildAgg(g, agg, r, "split")); }
    });
    return finishAgg(agg);
  }, [ready, filtered, C, viewBy, codeClass, filterCodes, campSentDates]);


  // Child rows for expandable views
  const childRows = useMemo(() => {
    const expandableViews = ["model", "campaignType", "campaignGroup"];
    if (!ready || !expandableViews.includes(viewBy)) return {};
    const p2c = {};
    filtered.forEach(r => {
      if (viewBy === "model") {
        const parent = r[C.model] || "Unknown", child = r[C.trim] || "Unknown";
        if (!p2c[parent]) p2c[parent] = {};
        if (!p2c[parent][child]) p2c[parent][child] = { vs: new Set(), hs: new Set(), gs: new Set(), raw: 0, frac: 0, fracVins: new Set() };
        const d = p2c[parent][child]; d.vs.add(r[C.vin]); d.hs.add(r[C.hh]); d.gs.add(r[C.grp]); d.raw++;
        if (!d.fracVins.has(r[C.vin])) { d.fracVins.add(r[C.vin]); d.frac += 1; }
      } else {
        filterCodes(r[C.cc], r[C.csd]).forEach(code => {
          const cl = codeClass[code] || {};
          const parent = viewBy === "campaignType" ? (cl.type || "Unknown") : (cl.group || "Unknown");
          if (!p2c[parent]) p2c[parent] = {};
          if (!p2c[parent][code]) p2c[parent][code] = { vs: new Set(), hs: new Set(), gs: new Set(), raw: 0, frac: 0, fracVins: new Set() };
          const d = p2c[parent][code]; d.vs.add(r[C.vin]); d.hs.add(r[C.hh]); d.gs.add(r[C.grp]); d.raw++;
          if (!d.fracVins.has(r[C.vin])) { d.fracVins.add(r[C.vin]); d.frac += 1 / Math.max((vinTotalCamps[r[C.vin]] || new Set()).size, 1); }
        });
      }
    });
    const out = {};
    Object.entries(p2c).forEach(([parent, children]) => {
      out[parent] = Object.entries(children).map(([k, d]) => ({ key: k, vins: d.vs.size, hh: d.hs.size, grp: d.gs.size, raw: d.raw, frac: Math.round(d.frac * 100) / 100 })).sort((a, b) => b.vins - a.vins);
    });
    return out;
  }, [ready, filtered, C, viewBy, codeClass, filterCodes, vinTotalCamps]);

  // For expandable views, fix parent fractional = sum of child fractionals
  const correctedAgg = useMemo(() => {
    const expandableViews = ["model", "campaignType", "campaignGroup"];
    if (!expandableViews.includes(viewBy)) return aggregated;
    return aggregated.map(a => {
      const kids = childRows[a.key];
      if (kids && kids.length > 0) {
        const childFracSum = Math.round(kids.reduce((s, k) => s + (k.frac || 0), 0) * 100) / 100;
        return { ...a, frac: childFracSum };
      }
      return a;
    });
  }, [aggregated, childRows, viewBy]);

  const totalFrac = useMemo(() => {
    return Math.round(correctedAgg.reduce((s, r) => s + (r.frac || 0), 0) * 100) / 100;
  }, [correctedAgg]);

  const sorted = useMemo(() => {
    const arr = [...correctedAgg];
    arr.sort((a, b) => { const av = a[sortCol] ?? a.key ?? "", bv = b[sortCol] ?? b.key ?? ""; return (typeof av === "number" && typeof bv === "number") ? (av - bv) * sortDir : String(av).localeCompare(String(bv)) * sortDir; });
    return arr;
  }, [correctedAgg, sortCol, sortDir]);

  // ── Treated vs Control buy rate computation ──
  const tvcData = useMemo(() => {
    if (Object.keys(ctrlData).length === 0) return [];
    // Group by campaign type
    const byType = {};
    const byCode = {};
    Object.entries(ctrlData).forEach(([code, arr]) => {
      const [t_delivered, t_purch_hh, c_pop, c_purch_hh] = arr;
      const cl = codeClass[code] || {};
      if (salesOnly && !cl.isSales) return;
      if (channelFilter.size > 0 && !channelFilter.has(cl.channel)) return;
      if (purposeFilter.size > 0 && !purposeFilter.has(cl.purpose)) return;
      if (typeFilter.size > 0 && !typeFilter.has(cl.type)) return;
      const type = cl.type || "Unknown";
      const channel = cl.channel || "Email";
      if (!byType[type]) byType[type] = { type, channel, t_sent: 0, t_purch: 0, c_pop: 0, c_purch: 0, codes: 0 };
      byType[type].t_sent += (t_delivered || 0);
      byType[type].t_purch += (t_purch_hh || 0);
      byType[type].c_pop += (c_pop || 0);
      byType[type].c_purch += (c_purch_hh || 0);
      byType[type].codes++;
      byCode[code] = { code, type, channel, t_sent: t_delivered || 0, t_purch: t_purch_hh || 0, c_pop: c_pop || 0, c_purch: c_purch_hh || 0 };
    });
    // Compute rates
    const compute = (r) => {
      r.t_rate = r.t_sent > 0 ? r.t_purch / r.t_sent * 100 : null;
      r.c_rate = r.c_pop > 0 ? r.c_purch / r.c_pop * 100 : null;
      r.lift_pp = (r.t_rate !== null && r.c_rate !== null) ? r.t_rate - r.c_rate : null;
      r.lift_pct = (r.t_rate !== null && r.c_rate !== null && r.c_rate > 0) ? (r.t_rate / r.c_rate - 1) * 100 : null;
      r.incr_sales = (r.lift_pp !== null && r.t_sent > 0) ? Math.round(r.lift_pp / 100 * r.t_sent) : null;
      return r;
    };
    Object.values(byType).forEach(compute);
    Object.values(byCode).forEach(compute);
    // Overall
    const overall = { type: "OVERALL", t_sent: 0, t_purch: 0, c_pop: 0, c_purch: 0, codes: 0 };
    Object.values(byType).forEach(t => { overall.t_sent += t.t_sent; overall.t_purch += t.t_purch; overall.c_pop += t.c_pop; overall.c_purch += t.c_purch; overall.codes += t.codes; });
    compute(overall);
    return { byType: Object.values(byType).sort((a, b) => (b.incr_sales || 0) - (a.incr_sales || 0)), byCode: Object.values(byCode).sort((a, b) => (b.incr_sales || 0) - (a.incr_sales || 0)), overall };
  }, [ctrlData, codeClass, salesOnly, channelFilter, purposeFilter, typeFilter]);

  if (!ready) {
    return (<div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted }}><div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div><div>Salesmatch QA data not loaded.</div><div style={{ fontSize: 12, marginTop: 8, color: COLORS.textDim }}>Run Process_Salesmatch_QA_Dashboard v3+ to generate salesmatch_qa.json</div></div>);
  }

  const handleSort = (col) => setSortCol(prev => { if (prev === col) { setSortDir(d => d * -1); return col; } setSortDir(-1); return col; });
  const clearAll = () => { setCampaignFilter(new Set()); setModelFilter(new Set()); setYearFilter(new Set()); setTrimFilter(new Set()); setMessageFilter(new Set()); setPurposeFilter(new Set()); setTypeFilter(new Set()); setChannelFilter(new Set()); setWindowFilter("ALL"); setSentStart(""); setSentEnd(""); setPurchaseStart(""); setPurchaseEnd(""); setSalesOnly(is2025 ? false : true); };
  const isFiltered = campaignFilter.size > 0 || modelFilter.size > 0 || yearFilter.size > 0 || trimFilter.size > 0 || messageFilter.size > 0 || purposeFilter.size > 0 || typeFilter.size > 0 || channelFilter.size > 0 || windowFilter !== "ALL" || sentStart || sentEnd || purchaseStart || purchaseEnd || (is2025 ? salesOnly : !salesOnly);
  const toggleExpand = (k) => { const n = new Set(expanded); n.has(k) ? n.delete(k) : n.add(k); setExpanded(n); };
  const getPillVins = (w) => windowPillCounts[w] || 0;
  const SortTh = ({ col, children, align = "right" }) => (<th onClick={() => handleSort(col)} style={{ padding: "10px 12px", textAlign: align, cursor: "pointer", color: sortCol === col ? COLORS.accent : COLORS.textMuted, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", userSelect: "none", borderBottom: `1px solid ${COLORS.cardBorder}` }}>{children} {sortCol === col ? (sortDir > 0 ? "▲" : "▼") : ""}</th>);
  const dateInputStyle = { padding: "5px 8px", background: "#0f172a", border: "1px solid #334155", borderRadius: 6, color: "#f1f5f9", fontSize: 12, width: 130 };
  const fmtFrac = (v) => typeof v === "number" ? v.toLocaleString(undefined, { maximumFractionDigits: 1 }) : "—";

  const hasExpandable = ["model", "campaignType", "campaignGroup"].includes(viewBy);
  const campTypeBadge = (code) => {
    const cl = codeClass[code]; if (!cl) return null;
    const isSales = cl.purpose === "Sales";
    return (<span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 10, fontSize: 9, fontWeight: 600, background: isSales ? "rgba(34,197,94,0.15)" : "rgba(251,146,60,0.15)", color: isSales ? "#22c55e" : "#fb923c", border: `1px solid ${isSales ? "rgba(34,197,94,0.3)" : "rgba(251,146,60,0.3)"}` }}>{cl.type}</span>);
  };
  const viewLabel = { campaign: "Campaign", campaignType: "Campaign Type", campaignGroup: "Campaign Group", model: "Model", modelYear: "Model Year", message: "Message", treatedVsControl: "Treated vs Control", segmentComposition: "Segment × Decile", campaignSegment: "Campaign × Decile" };
  const childLabelFn = (k) => viewBy === "model" ? k : (campLabel[k] ? `${k} — ${campLabel[k]}` : k);

  // Pill labels differ by mode
  const pillLabel = (w) => {
    if (w === "ALL") return "All Windows";
    if (windowMode === "cumulative") return `≤${WIN_THRESHOLDS[w] || "?"} Days`;
    return w;
  };

  const downloadExcel = () => {

    const now = new Date();
    const timestamp = now.toLocaleString("en-US", { year:"numeric", month:"2-digit", day:"2-digit", hour:"2-digit", minute:"2-digit", second:"2-digit" });
    const vl = viewLabel[viewBy] || viewBy;

    // ── Data tab ──
    const dataRows = [];
    // Header metadata
    dataRows.push(["Salesmatch QA Export"]);
    dataRows.push(["Source: FirstParty INNsight Dashboard — Salesmatch QA Tab"]);
    dataRows.push(["URL: https://d1a2gc6wf9ualm.cloudfront.net/?tab=salesmatchQA"]);
    dataRows.push(["Extracted: " + timestamp]);
    dataRows.push(["Data As Of: " + ((data.summary || {}).dataAsOf || "—")]);
    dataRows.push(["Source Table: " + ((data.summary || {}).sourceTable || "salesmatch_combined")]);
    dataRows.push([]);

    // Active filters
    dataRows.push(["Active Filters"]);
    dataRows.push(["Classification Source", classSource === "heroku" ? "Heroku" : "Chalsea"]);
    dataRows.push(["Sales Filter", salesOnly ? "Sales Only" : "All Campaigns"]);
    if (channelFilter.size > 0) dataRows.push(["Channel Filter", [...channelFilter].join(", ")]);
    if (purposeFilter.size > 0) dataRows.push(["Campaign Purpose", [...purposeFilter].join(", ")]);
    if (typeFilter.size > 0) dataRows.push(["Campaign Type", [...typeFilter].join(", ")]);
    if (campaignFilter.size > 0) dataRows.push(["Campaign Codes", [...campaignFilter].join(", ")]);
    if (modelFilter.size > 0) dataRows.push(["Models", [...modelFilter].join(", ")]);
    if (yearFilter.size > 0) dataRows.push(["Model Years", [...yearFilter].join(", ")]);
    if (trimFilter.size > 0) dataRows.push(["Trims", [...trimFilter].join(", ")]);
    if (messageFilter.size > 0) dataRows.push(["Messages", [...messageFilter].join(", ")]);
    dataRows.push(["Attribution Window", windowFilter === "ALL" ? "All Windows" : windowFilter]);
    dataRows.push(["Window Mode", windowMode === "cumulative" ? "Cumulative" : "Exclusive"]);
    if (sentStart || sentEnd) dataRows.push(["Sent Date Range", (sentStart || "—") + " to " + (sentEnd || "—")]);
    if (purchaseStart || purchaseEnd) dataRows.push(["Purchase Date Range", (purchaseStart || "—") + " to " + (purchaseEnd || "—")]);
    dataRows.push(["View By", vl]);
    dataRows.push([]);

    if (viewBy === "treatedVsControl" && tvcData.byType) {
      // ── Treated vs Control export ──
      dataRows.push(["Overall Summary"]);
      dataRows.push(["Treated Buy Rate", tvcData.overall.t_rate !== null ? tvcData.overall.t_rate.toFixed(3) + "%" : "—"]);
      dataRows.push(["Control Buy Rate", tvcData.overall.c_rate !== null ? tvcData.overall.c_rate.toFixed(3) + "%" : "—"]);
      dataRows.push(["Incremental Lift (pp)", tvcData.overall.lift_pp !== null ? (tvcData.overall.lift_pp >= 0 ? "+" : "") + tvcData.overall.lift_pp.toFixed(3) + "pp" : "—"]);
      dataRows.push(["Lift %", tvcData.overall.lift_pct !== null ? tvcData.overall.lift_pct.toFixed(1) + "%" : "—"]);
      dataRows.push(["Incremental Sales", tvcData.overall.incr_sales]);
      dataRows.push([]);

      // By Campaign Type
      const headerRow = dataRows.length;
      dataRows.push(["Campaign Type", "Treated Sent", "Treated Purch", "Treated Rate", "Control Pop", "Control Purch", "Control Rate", "Lift (pp)", "Lift %", "Incr. Sales"]);
      const fmtRate = (v) => v !== null ? v.toFixed(3) + "%" : "—";
      const fmtLift = (v) => v !== null ? (v >= 0 ? "+" : "") + v.toFixed(3) + "pp" : "—";
      const fmtLiftPct = (v) => v !== null ? (v >= 0 ? "+" : "") + v.toFixed(1) + "%" : "—";
      const rowOutlines = [];
      tvcData.byType.forEach(r => {
        dataRows.push([r.type, r.t_sent, r.t_purch, fmtRate(r.t_rate), r.c_pop, r.c_purch, fmtRate(r.c_rate), fmtLift(r.lift_pp), fmtLiftPct(r.lift_pct), r.incr_sales]);
        rowOutlines.push({ level: 0 });
      });
      // Overall row
      const o = tvcData.overall;
      dataRows.push(["OVERALL", o.t_sent, o.t_purch, fmtRate(o.t_rate), o.c_pop, o.c_purch, fmtRate(o.c_rate), fmtLift(o.lift_pp), fmtLiftPct(o.lift_pct), o.incr_sales]);
      rowOutlines.push({ level: 0 });
      dataRows.push([]);

      // By Campaign Code
      dataRows.push(["By Campaign"]);
      dataRows.push(["Campaign", "Type", "Channel", "Treated Sent", "Treated Purch", "Treated Rate", "Control Pop", "Control Purch", "Control Rate", "Lift (pp)", "Incr. Sales"]);
      tvcData.byCode.forEach(r => {
        const label = campLabel[r.code] ? `${r.code} — ${campLabel[r.code]}` : r.code;
        dataRows.push([label, r.type, r.channel, r.t_sent, r.t_purch, fmtRate(r.t_rate), r.c_pop, r.c_purch, fmtRate(r.c_rate), fmtLift(r.lift_pp), r.incr_sales]);
      });

      const wsData = XLSX.utils.aoa_to_sheet(dataRows);
      wsData["!cols"] = [{ wch: 55 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
      if (!wsData["!views"]) wsData["!views"] = [];
      wsData["!views"].push({ state: "frozen", xSplit: 0, ySplit: headerRow + 1 });

      // Format rate columns as percentage
      const dateStr = now.toISOString().slice(0, 10);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsData, "Treated vs Control");
      XLSX.writeFile(wb, `Salesmatch_QA_Treated_vs_Control_${dateStr}.xlsx`);

    } else if (viewBy === "segmentComposition" && segCompData.length > 0) {
      // ── Segment × Decile Composition export ──
      const SEG_LABELS = { CO: "Current Owner", PO: "Previous Owner", HR: "Hand Raiser", OTH: "Other" };
      const totalT = segCompData.reduce((s, r) => s + r[2], 0);
      const totalC = segCompData.reduce((s, r) => s + r[3], 0);
      const totalCPurch = segCompData.reduce((s, r) => s + r[4], 0);

      dataRows.push(["Composition Summary"]);
      dataRows.push(["Total Treated Population", totalT]);
      dataRows.push(["Total Control Population", totalC]);
      dataRows.push(["Total Control Purchases", totalCPurch]);
      dataRows.push(["Overall Control Buy Rate", totalC > 0 ? (totalCPurch / totalC * 100).toFixed(3) + "%" : "—"]);
      dataRows.push(["T/C Population Ratio", totalC > 0 ? (totalT / totalC).toFixed(1) + "×" : "—"]);
      dataRows.push([]);

      const headerRow = dataRows.length;
      dataRows.push(["Segment", "Decile", "Treated Pop", "Treated %", "Control Pop", "Control %", "Diff (pp)", "Ctrl Purchases", "Ctrl Buy Rate"]);

      segCompData.forEach(([seg, decile, t_pop, c_pop, c_purch]) => {
        const tPct = totalT > 0 ? t_pop / totalT * 100 : 0;
        const cPct = totalC > 0 ? c_pop / totalC * 100 : 0;
        const diff = tPct - cPct;
        const cRate = c_pop > 0 ? c_purch / c_pop * 100 : 0;
        dataRows.push([SEG_LABELS[seg] || seg, decile, t_pop, tPct.toFixed(1) + "%", c_pop, cPct.toFixed(1) + "%", (diff >= 0 ? "+" : "") + diff.toFixed(1) + "pp", c_purch, cRate.toFixed(3) + "%"]);
      });

      // Totals row
      const overallCRate = totalC > 0 ? totalCPurch / totalC * 100 : 0;
      dataRows.push(["TOTAL", "", totalT, "100%", totalC, "100%", "", totalCPurch, overallCRate.toFixed(3) + "%"]);

      const wsData = XLSX.utils.aoa_to_sheet(dataRows);
      wsData["!cols"] = [{ wch: 20 }, { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 14 }];
      if (!wsData["!views"]) wsData["!views"] = [];
      wsData["!views"].push({ state: "frozen", xSplit: 0, ySplit: headerRow + 1 });

      const dateStr = now.toISOString().slice(0, 10);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsData, "Segment Composition");
      XLSX.writeFile(wb, `Salesmatch_QA_Segment_Composition_${dateStr}.xlsx`);

    } else if (viewBy === "campaignSegment" && Object.keys(campSegData).length > 0) {
      // ── Campaign × Decile export ──
      const SEG_LABELS = { CO: "Current Owner", PO: "Previous Owner", HR: "Hand Raiser", OTH: "Other" };
      const SEG_ORDER = ["CO", "PO", "HR", "OTH"];
      const selCode = selectedCampCode;
      if (selCode && campSegData[selCode]) {
        const selSegs = campSegData[selCode] || {};
        const cl = codeClass[selCode] || {};
        const label = campLabel[selCode] ? `${selCode} — ${campLabel[selCode]}` : selCode;
        dataRows.push(["Campaign", label]);
        dataRows.push(["Type", cl.type || "—"]);
        dataRows.push(["Channel", cl.channel || "—"]);
        dataRows.push([]);
        dataRows.push(["Segment", "Decile", "Treated Pop", "Treated %"]);
        const totalT = Object.values(selSegs).reduce((s, v) => s + v, 0);
        const displayRows = [];
        Object.entries(selSegs).forEach(([key, val]) => {
          const [seg, decile] = key.split("|");
          displayRows.push({ seg, decile, t_pop: val });
        });
        displayRows.sort((a, b) => SEG_ORDER.indexOf(a.seg) - SEG_ORDER.indexOf(b.seg) || b.t_pop - a.t_pop);
        displayRows.forEach(r => {
          const tPct = totalT > 0 ? (r.t_pop / totalT * 100).toFixed(1) + "%" : "—";
          dataRows.push([SEG_LABELS[r.seg] || r.seg, r.decile, r.t_pop, tPct]);
        });
        dataRows.push(["TOTAL", "", totalT, "100%"]);
      }
      const wsData = XLSX.utils.aoa_to_sheet(dataRows);
      wsData["!cols"] = [{ wch: 20 }, { wch: 18 }, { wch: 16 }, { wch: 14 }];
      const dateStr = now.toISOString().slice(0, 10);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsData, "Campaign Decile");
      XLSX.writeFile(wb, `Salesmatch_QA_Campaign_Decile_${selCode || "all"}_${dateStr}.xlsx`);

    } else {
    // ── Regular view export ──

    // Totals
    dataRows.push(["Totals"]);
    dataRows.push(["Unique VINs", totals.vins]);
    dataRows.push(["Unique Households", totals.hh]);
    dataRows.push(["Unique Groups", totals.grp]);
    dataRows.push(["Fractional", totalFrac]);
    dataRows.push(["Matches", totals.raw]);
    dataRows.push([]);

    // Column headers
    const headerRow = dataRows.length;
    dataRows.push([vl, "Unique VINs", "Unique HH", "Unique Groups", "Fractional", "Matches"]);

    // Data
    // Track row outline levels for Excel grouping
    const rowOutlines = [];

    sorted.forEach(r => {
      const label = viewBy === "campaign" ? (campLabel[r.key] ? `${r.key} — ${campLabel[r.key]}` : r.key) : r.key;
      dataRows.push([label, r.vins, r.hh, r.grp, r.frac, r.raw]);
      rowOutlines.push({ level: 0 });

      // Always include child rows for expandable views (grouped in Excel)
      if (hasExpandable) {
        (childRows[r.key] || []).forEach(t => {
          const cLabel = viewBy === "model" ? t.key : (campLabel[t.key] ? `${t.key} — ${campLabel[t.key]}` : t.key);
          dataRows.push(["    " + cLabel, t.vins, t.hh, t.grp, t.frac, t.raw]);
          rowOutlines.push({ level: 1, hidden: true });
        });
      }
    });

    const wsData = XLSX.utils.aoa_to_sheet(dataRows);

    // Column widths
    wsData["!cols"] = [{ wch: 60 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];

    // Apply row outline levels for grouping (child rows are level 1, collapsed by default)
    if (hasExpandable && rowOutlines.length > 0) {
      wsData["!rows"] = [];
      // Fill rows up to the header row with empty entries
      for (let i = 0; i < headerRow + 1; i++) wsData["!rows"].push({});
      // Add outline levels for data rows
      rowOutlines.forEach(o => {
        wsData["!rows"].push(o.level > 0 ? { level: o.level, hidden: true } : {});
      });
      // Set outline properties: summary rows are above detail (default Excel behavior)
      if (!wsData["!outline"]) wsData["!outline"] = {};
      wsData["!outline"].above = true;
    }

    // Freeze panes at the column header row
    if (!wsData["!views"]) wsData["!views"] = [];
    wsData["!views"].push({ state: "frozen", xSplit: 0, ySplit: headerRow + 1 });

    // ── About tab ──
    const aboutRows = is2025 ? [
      ["Salesmatch 2025 — About This Data (Corrected)"],
      [],
      ["DATA SOURCE"],
      ["Source: ius_unity_prod.sfmc.salesmatch_corrected_2025 (persisted Delta table)"],
      ["This is a corrected dataset fixing critical DM matchback issues in the original pipeline."],
      [],
      ["DM CORRECTION"],
      ["Problem: DM SentDate in salesmatch_combined contained 2026 data-load timestamps, not actual 2025 mail dates."],
      ["The pipeline matched 2026 timestamps against 2026 purchases — 99% of DM VINs were wrong."],
      ["Fix: DM treated households from treated_campaign joined to Email purchase universe."],
      ["Send dates derived from campaign names (mid-month proxy). Only 60-day matches kept."],
      ["Result: 10,584 correctly matched Email+DM multi-channel VINs (was 0 before)."],
      ["Only 121 VINs (1.2%) overlap between old broken DM and corrected DM."],
      ["Total: 100,612 unique VINs (was 109,690 with broken DM)."],
      [],
      ["CAMPAIGN CLASSIFICATION"],
      ["Carlos Cascante's 2025 campaign mapping (HMA_2025_Campaign_codes_x_Type.xlsx, 208 codes)."],
      ["MSP REG — Regular monthly MSP (Apr-Jun, Sep-Oct). 29 codes."],
      ["MSP SE — Spring Sales Event, Summer Getaway, Winter Sales Event. 87 codes."],
      ["Launch — IONIQ 9, Palisade ICE, Palisade HEV. 32 codes."],
      ["Alt Fuel — HEV Brand, Tax Credit, EV Incentive. 13 codes."],
      ["Other — Anniversary, Welcome Kit, Miscellaneous (non-sales)."],
      [],
      ["ATTRIBUTION"],
      ["60-day window only. DaysBetween 0-30 tagged 30-Day, 31-60 tagged 60-Day."],
      ["Both treated purchases AND control purchases are filtered to the same 60-day attribution window."],
      ["Fractional = 1 / total campaign touches per VIN across all channels."],
      ["Email/SMS dates from original pipeline. DM dates from campaign name derivation."],
      [],
      ["LIMITATIONS"],
      ["5 DM codes (033130, 033561, 033680, 033777, 033799) lack treated_campaign records."],
      ["DM-only buyers not captured (~2K estimated). Requires RDR table access."],
      ["One SMS code (034154) SentDate corrected from 2026-01-03 to 2025-12-20."],
    ] : [
      ["Salesmatch QA — About This Data"],
      [],
      ["DATA SOURCE"],
      ["The data comes from salesmatch_combined in Databricks Unity Catalog (ius_unity_prod.sfmc.salesmatch_combined)."],
      ["This table is populated by an SFMC Automation Studio query that joins four tables:"],
      ["  EmailSendLog — who got sent what email and when (delivered only)"],
      ["  ContactTargetMaster_Main — customer identity (HouseholdId, GroupId)"],
      ["  AssetTargetMaster_Main — vehicle purchased (VIN, Model, Year, Trim)"],
      ["  AssetTargetMaster_Sub1 — sale details (RDR type, purchase date)"],
      ["Only new vehicle sales (RDR type) within 180 days of email send and within the last 18 months are included."],
      [],
      ["DATA TRANSFORMATION"],
      ["Raw matchback data is extracted from SFMC to SFTP, then ingested into Databricks via an automated pipeline."],
      ["A processing notebook transforms it into a VIN-level JSON dataset and pushes to S3 for the dashboard."],
      ["Each row represents one VIN x HouseholdId x GroupId combination with all campaigns as pipe-delimited strings."],
      ["The browser computes true distinct counts using JavaScript Sets for accurate numbers under any filter combination."],
      [],
      ["CAMPAIGN CLASSIFICATION"],
      ["Two sources available via the Heroku / Chalsea toggle:"],
      ["Heroku — Derived from the group_campaign field using keyword matching (MSP, LAUNCH, ALT FUEL, EMAGAZINE, etc.)"],
      ["Chalsea — Manually curated from the Household ID Campaign Codes spreadsheet (212 codes)."],
      ["Some codes differ between sources (e.g., Palisade HEV = Launch in Heroku, Alt-Fuel Vehicle in Chalsea)."],
      [],
      ["─────────────────────────────────────────────────────────────────────────────"],
      [],
      ["SFMC SQL QUERY (Salesmatch Combined)"],
      [],
      ["The following query runs as a single SFMC Query Activity in Automation Studio:"],
      [],
      ["SELECT DISTINCT"],
      ["    ctm.FirstName, ctm.LastName, ctm.ContactKey, ctm.GroupId, ctm.HouseholdId,"],
      ["    atm.VIN, atm.ModelGroupDesc, ats.SeriesDescription, atm.ModelYear, atm.Trim,"],
      ["    ats.RDRCPOType AS SaleType, esl.CampaignCode, esl.SentDate,"],
      ["    LEFT(esl.MessageName, 254) AS MessageName,"],
      ["    CASE WHEN ISDATE(ats.RDRCPODate) = 1 THEN CONVERT(DATE, ats.RDRCPODate)"],
      ["         ELSE CONVERT(DATE, ats.PurchaseDate) END AS PurchaseDate,"],
      ["    DATEDIFF(DAY, esl.SentDate, <PurchaseDate logic>) AS DaysToPurchase,"],
      ["    CASE WHEN DaysToPurchase <= 30 THEN '30-Day'"],
      ["         WHEN DaysToPurchase <= 60 THEN '60-Day'"],
      ["         WHEN DaysToPurchase <= 90 THEN '90-Day'"],
      ["         WHEN DaysToPurchase <= 180 THEN '180-Day' END AS AttributionWindow"],
      ["FROM EmailSendLog esl"],
      ["INNER JOIN ContactTargetMaster_Main ctm ON esl.ContactKey = ctm.ContactKey"],
      ["INNER JOIN AssetTargetMaster_Main atm ON ctm.ContactKey = atm.ContactKey"],
      ["INNER JOIN AssetTargetMaster_Sub1 ats ON atm.VIN = ats.VIN"],
      ["WHERE esl.Delivered = 'TRUE'"],
      ["    AND esl.CampaignCode IN (<196 campaign codes>)"],
      ["    AND ats.RDRCPOType = 'RDR'"],
      ["    AND PurchaseDate >= DATEADD(MONTH, -18, GETDATE())"],
      ["    AND PurchaseDate >= esl.SentDate"],
      ["    AND DATEDIFF(DAY, esl.SentDate, PurchaseDate) <= 180"],
      [],
      ["─────────────────────────────────────────────────────────────────────────────"],
      [],
      ["QUERY COLUMN DEFINITIONS"],
      [],
      ["Column", "Source Table", "Description"],
      ["FirstName", "ContactTargetMaster_Main", "Customer first name"],
      ["LastName", "ContactTargetMaster_Main", "Customer last name"],
      ["ContactKey", "EmailSendLog / ContactTargetMaster", "Unique customer identifier — join key between email sends and customer master"],
      ["GroupId", "ContactTargetMaster_Main", "Customer group identifier — used for distinct group counting"],
      ["HouseholdId", "ContactTargetMaster_Main", "Household identifier — multiple contacts can share a household"],
      ["VIN", "AssetTargetMaster_Main", "Vehicle Identification Number — unique 17-character identifier for each car sold"],
      ["ModelGroupDesc", "AssetTargetMaster_Main", "Vehicle model group (e.g., TUCSON, PALISADE, IONIQ 5)"],
      ["SeriesDescription", "AssetTargetMaster_Sub1", "Detailed vehicle series description"],
      ["ModelYear", "AssetTargetMaster_Main", "Model year of the vehicle (e.g., 2025, 2026)"],
      ["Trim", "AssetTargetMaster_Main", "Vehicle trim level (e.g., SEL, LIMITED, SE)"],
      ["SaleType", "AssetTargetMaster_Sub1", "Type of sale — filtered to RDR (new vehicle retail delivery report) only"],
      ["CampaignCode", "EmailSendLog", "6-digit SFMC campaign identifier (e.g., 033978)"],
      ["SentDate", "EmailSendLog", "Date/time the email was delivered to the customer"],
      ["MessageName", "EmailSendLog", "SFMC message/journey name (truncated to 254 chars)"],
      ["PurchaseDate", "AssetTargetMaster_Sub1", "Date of vehicle purchase — uses RDRCPODate if valid, otherwise PurchaseDate"],
      ["DaysToPurchase", "Calculated", "Number of days between SentDate and PurchaseDate (0 = same day)"],
      ["AttributionWindow", "Calculated", "Bucket based on DaysToPurchase: 30-Day (0-30), 60-Day (31-60), 90-Day (61-90), 180-Day (91-180)"],
      [],
      ["─────────────────────────────────────────────────────────────────────────────"],
      [],
      ["QUERY FILTERS EXPLAINED"],
      [],
      ["Filter", "Value", "Purpose"],
      ["esl.Delivered = 'TRUE'", "TRUE", "Only count emails that were successfully delivered (excludes bounces)"],
      ["esl.CampaignCode IN (...)", "196 codes", "Limits to known HMA campaign codes — sourced from Heroku dm.dih_hma_campaign"],
      ["ats.RDRCPOType = 'RDR'", "RDR", "New vehicle sales only — excludes CPO (certified pre-owned) and other transaction types"],
      ["PurchaseDate >= DATEADD(MONTH, -18, GETDATE())", "18-month lookback", "Only includes purchases within the last 18 months from query run date"],
      ["PurchaseDate >= esl.SentDate", "Forward-looking", "Purchase must happen AFTER the email was sent (not before)"],
      ["DATEDIFF(...) <= 180", "180-day max", "Maximum 180 days between email send and purchase for attribution"],
      [],
      ["─────────────────────────────────────────────────────────────────────────────"],
      [],
      ["DASHBOARD METRIC GLOSSARY"],
      [],
      ["Term", "Definition"],
      ["Unique VINs", "Count of distinct Vehicle Identification Numbers — each VIN represents one car sale. This is the primary metric."],
      ["Unique HH", "Count of distinct HouseholdId values — the number of unique households that purchased."],
      ["Unique Groups", "Count of distinct GroupId values — the number of unique customer groups that purchased."],
      ["Fractional", "Each VIN contributes 1.0 total, split equally across all campaigns that touched it. A VIN touched by 3 campaigns gives each campaign 1/3 credit. The sum across all campaigns equals the total Unique VINs. For Model/Model Year views, fractional equals Unique VINs since each VIN has one model/year."],
      ["Matches", "Total rows in the filtered dataset. A VIN can appear on multiple rows if associated with different HouseholdId/GroupId combinations."],
      ["Attribution Window (Exclusive)", "Mutually exclusive time buckets: 30-Day = 0-30 days after send, 60-Day = 31-60 days, 90-Day = 61-90 days, 180-Day = 91-180 days."],
      ["Attribution Window (Cumulative)", "Running totals: ≤30 Days = all purchases 0-30 days, ≤60 Days = all purchases 0-60 days, etc. No VIN duplication — each VIN counted once."],
      ["DaysToPurchase", "Number of days between the email send date and the vehicle purchase date. In cumulative mode, the minimum DaysToPurchase across all campaign sends for a VIN is used."],
      ["Campaign Purpose", "Top-level classification: Sales (campaigns designed to drive purchases) or Non-sales (owner communications like eMag, Anniversary, Welcome)."],
      ["Campaign Type", "Sub-classification within Purpose: MSP, Launch, Alt-Fuel Vehicle, Sales Event, Auto Show, eMag, Anniversary, Welcome, Nurture, Off Lease, Conquest, etc."],
      ["Campaign Group", "The Heroku group_campaign field — the journey or campaign group that the individual campaign code belongs to (e.g., [G] 2025 HMA MSP)."],
      [],
      ["─────────────────────────────────────────────────────────────────────────────"],
      [],
      ["DASHBOARD FILTER DESCRIPTIONS"],
      [],
      ["Filter", "Type", "Description"],
      ["Campaign Purpose", "Multi-select pills", "Filter by Sales or Non-sales classification. Affects which campaign codes are included."],
      ["Campaign Type", "Multi-select pills", "Filter by campaign sub-type (MSP, Launch, Alt-Fuel Vehicle, etc.). Options change based on Classification source."],
      ["Campaigns", "Multi-select pills", "Filter by specific 6-digit campaign codes. Dropdown shows code + message name for identification."],
      ["Models", "Multi-select pills", "Filter by vehicle model group (TUCSON, PALISADE, IONIQ 5, etc.)."],
      ["Years", "Multi-select pills", "Filter by vehicle model year (2022-2026)."],
      ["Trims", "Multi-select pills", "Filter by vehicle trim level (SE, SEL, LIMITED, etc.). Trim is a subset of Model."],
      ["Messages", "Multi-select pills", "Filter by SFMC message/journey name."],
      ["Sent Date", "Date range", "Filter VINs by the date range their campaign emails were sent."],
      ["Purchase Date", "Date range", "Filter VINs by the date range they were purchased."],
      ["Attribution Window", "Single-select pills", "Filter by attribution time window. Cumulative mode includes all shorter windows; Exclusive mode shows individual buckets only."],
      ["Classification Source", "Toggle", "Switch between Heroku-derived and Chalsea-curated campaign classifications. Heroku uses group_campaign keywords; Chalsea uses a manually maintained spreadsheet."],
    ];

    const wsAbout = XLSX.utils.aoa_to_sheet(aboutRows);
    wsAbout["!cols"] = [{ wch: 40 }, { wch: 30 }, { wch: 100 }];

    // Build workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsData, "Salesmatch QA Data");
    XLSX.utils.book_append_sheet(wb, wsAbout, "About This Data");

    // Generate filename with date
    const dateStr = now.toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Salesmatch_QA_${vl.replace(/\s+/g, "_")}_${dateStr}.xlsx`);
    } // end else (regular view)
  };

  return (
    <div>
      {/* ── INFO BUTTON ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: COLORS.textDim }}>
          Source: <span style={{ color: is2025 ? "#22c55e" : COLORS.textMuted }}>{is2025 ? "salesmatch_corrected_2025" : ((data.summary || {}).sourceTable || "salesmatch_combined")}</span>
          {(data.summary || {}).dataAsOf && <span> · Data as of {data.summary.dataAsOf}</span>}
          {parentBrandFilter && parentBrandFilter !== "ALL" && <span style={{ marginLeft: 8, padding: "1px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: parentBrandFilter === "HMA" ? "#002C5F" : "#8B7355", color: "#fff" }}>{parentBrandFilter === "HMA" ? "HYUNDAI" : "GENESIS"}</span>}
          <span style={{ marginLeft: 12, color: "#64748b", fontStyle: "italic" }}>{is2025 ? "Corrected dataset · Carlos mapping · DM dates fixed · ≤60 day attribution" : "Only the filters below affect the Salesmatch QA data."}</span>
        </div>
        <button onClick={() => setShowInfo(true)}
          style={{ padding: "5px 14px", background: "rgba(255,255,255,0.04)", border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6, color: COLORS.textMuted, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 14 }}>ℹ️</span> About This Data
        </button>
      </div>

      {/* ── INFO MODAL ── */}
      {showInfo && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowInfo(false); }}>
          <div style={{ background: "#1e293b", border: `1px solid ${COLORS.cardBorder}`, borderRadius: 12, maxWidth: 700, width: "100%", maxHeight: "80vh", overflowY: "auto", padding: "28px 32px", color: COLORS.text, fontSize: 13, lineHeight: 1.7 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{is2025 ? "Salesmatch 2025 — About This Data (Corrected)" : "Salesmatch QA — About This Data"}</div>
              <button onClick={() => setShowInfo(false)} style={{ background: "none", border: "none", color: COLORS.textDim, fontSize: 20, cursor: "pointer", padding: "4px 8px" }}>✕</button>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>Data Source</div>
              {is2025 ? (<>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                The data comes from <span style={{ color: COLORS.text }}>salesmatch_corrected_2025</span> in Databricks Unity Catalog
                (<span style={{ fontFamily: "monospace", fontSize: 11 }}>ius_unity_prod.sfmc.salesmatch_corrected_2025</span>).
                This is a <span style={{ color: "#22c55e", fontWeight: 600 }}>corrected dataset</span> that fixes critical issues found in the original pipeline.
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text, fontWeight: 600 }}>What was wrong:</span> The DM (Direct Mail) channel SentDate in salesmatch_combined
                contained <span style={{ color: "#f87171" }}>2026 data-load timestamps</span>, not actual 2025 mail drop dates.
                The pipeline matched these 2026 timestamps against 2026 purchases — producing entirely wrong sales matches.
                All 15,052 DM rows (9,360 VINs) in the original data attributed 2026 purchases to 2025 DM campaigns.
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text, fontWeight: 600 }}>How it was fixed:</span> DM treated households from
                <span style={{ fontFamily: "monospace", fontSize: 11 }}> treated_campaign</span> were joined to the Email purchase universe
                (which has correct HouseholdId + PurchaseDate). DM send dates were derived from campaign names
                (e.g., "033174_2025_JUN_HMA_MSP_REG_DM1" → June 15, 2025). Matches were filtered to ≤60 days.
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text, fontWeight: 600 }}>Result:</span> 10,584 correctly matched Email+DM multi-channel VINs
                (was 0 before). Only 121 VINs (1.2%) overlap between the old broken DM data and the corrected data — 99% were wrong.
                Total unique VINs: <span style={{ color: COLORS.text, fontWeight: 600 }}>100,612</span> (was 109,690 with broken DM).
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text, fontWeight: 600 }}>Scope:</span> 2025 Program Year campaigns only (per Carlos's
                HMA_2025_Campaign_codes_x_Type.xlsx). 2026 program year campaigns excluded. Email and SMS dates are original (correct).
                One SMS code (034154) had SentDate corrected from 2026-01-03 to 2025-12-20.
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text, fontWeight: 600 }}>Limitations:</span> 5 DM codes (033130, 033561, 033680, 033777, 033799) lack
                treated_campaign records and could not be corrected. DM-only buyers (households that received DM but no Email and still purchased)
                are not captured — estimated ~2,000 additional VINs. This would require access to the raw RDR purchase table.
              </p>
              </>) : (<>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                The data comes from <span style={{ color: COLORS.text }}>salesmatch_combined</span> in Databricks Unity Catalog
                (<span style={{ fontFamily: "monospace", fontSize: 11 }}>ius_unity_prod.sfmc.salesmatch_combined</span>).
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                This table is populated by three SFMC Automation Studio queries — one per channel — that join to the same contact and asset tables:
              </p>
              <p style={{ margin: "0 0 4px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>Email</span> — <span style={{ fontFamily: "monospace", fontSize: 11 }}>EmailSendLog</span> (who got sent what, filtered by Delivered = TRUE)
              </p>
              <p style={{ margin: "0 0 4px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>SMS</span> — <span style={{ fontFamily: "monospace", fontSize: 11 }}>SMSSendLog</span> (custom DE with CampaignCode, ContactKey, SentDate, filtered by Delivered = TRUE)
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>DM (Direct Mail)</span> — <span style={{ fontFamily: "monospace", fontSize: 11 }}>DMSendLog</span> (custom DE with CampaignCode, ContactKey, JourneyName, filtered by Sent = TRUE)
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                All three channels join to <span style={{ color: COLORS.text }}>ContactTargetMaster</span> (customer identity — HouseholdId, GroupId),
                <span style={{ color: COLORS.text }}> AssetTargetMaster_Main</span> (vehicle purchased — VIN, Model, Trim), and
                <span style={{ color: COLORS.text }}> AssetTargetMaster_Sub1</span> (sale details — RDR type, purchase date).
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                Only new vehicle sales (RDR type) are included. The purchase must occur within 180 days after the campaign send and within the last 18 months.
              </p>
              </>)}
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>Data Transformation</div>
              {is2025 ? (<>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                The corrected dataset was built in a multi-step process using a Databricks notebook
                (<span style={{ fontFamily: "monospace", fontSize: 11 }}>HMA_2025_Salesmatch_Extract.py</span>):
              </p>
              <p style={{ margin: "0 0 4px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>Step 1 — Email/SMS base:</span> All Email and SMS rows from salesmatch_combined with correct SentDates,
                filtered to 2025 campaigns only (per Carlos's campaign code list). These dates are correct in the original pipeline.
              </p>
              <p style={{ margin: "0 0 4px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>Step 2 — DM correction:</span> DM treated households from
                <span style={{ fontFamily: "monospace", fontSize: 11 }}> treated_campaign</span> were joined to the Email purchase universe
                (HouseholdId match). Send dates were derived from campaign naming convention (mid-month proxy).
                7 of 12 DM codes had treated_campaign records; 5 could not be corrected.
              </p>
              <p style={{ margin: "0 0 4px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>Step 3 — Attribution window:</span> Only matches within 60 days of the (corrected) send date are included.
                DaysBetween ≤30 is tagged as "30-Day", 31-60 as "60-Day". This 60-day window is applied consistently
                to <span style={{ color: COLORS.text, fontWeight: 600 }}>both treated purchases and control purchases</span>,
                ensuring the Treated vs Control buy rate comparison is apples-to-apples.
                (The control_purchases table was unaffected by the DM date bug — it already had correct 2025 send dates.)
              </p>
              <p style={{ margin: "0 0 4px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>Step 4 — Fractional attribution:</span> Each VIN's contribution = 1 / total campaign touches across
                all channels. A VIN touched by Email + DM + SMS gets 1/3 credit per channel. Sum of all fractionals = total unique VINs.
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>Step 5 — Enrichment:</span> Vehicle info (ModelGroupDesc, ModelYear, Trim) and household identifiers
                (HouseholdId, GroupId) are joined from salesmatch_combined. The corrected table is persisted as a Delta table in Databricks.
              </p>
              </>) : (<>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                The SFMC automation runs three Query Activities in sequence: Email (overwrite), SMS (append), DM (append) — producing a single combined dataset in the <span style={{ fontFamily: "monospace", fontSize: 11 }}>Salesmatch_Combined</span> Data Extension.
                This is exported to CSV via SFTP, then ingested into Databricks via an automated pipeline.
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                During ingestion, the pipeline enriches each row with classification columns: <span style={{ color: COLORS.text }}>IsSales</span> (boolean), <span style={{ color: COLORS.text }}>Channel</span> (Email/SMS/DM),
                <span style={{ color: COLORS.text }}> CampaignType</span> (MSP, Launch, Alt-Fuel Vehicle, etc.), and <span style={{ color: COLORS.text }}>CampaignGroup</span> — derived from the Heroku campaign tables.
                Campaign codes not found in Heroku are classified via a MessageName fallback that parses the campaign naming convention.
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                A processing notebook transforms the enriched table into a VIN-level JSON dataset with per-campaign sent dates (<span style={{ fontFamily: "monospace", fontSize: 11 }}>csd</span> field) and pushes it to S3/CloudFront.
                Each row represents one unique VIN × HouseholdId × GroupId combination, carrying all campaigns
                and attribution windows as pipe-delimited strings. The browser computes distinct counts using JavaScript Sets.
              </p>
              </>)}
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>Campaign Classification</div>
              {is2025 ? (<>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                Classification uses <span style={{ color: COLORS.text, fontWeight: 600 }}>Carlos Cascante's 2025 campaign mapping</span> from
                <span style={{ fontFamily: "monospace", fontSize: 11 }}> HMA_2025_Campaign_codes_x_Type.xlsx</span> (208 codes).
                Both the Heroku and Chalsea toggle slots display the same Carlos classification.
              </p>
              <p style={{ margin: "0 0 4px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>MSP REG</span> — Regular monthly MSP campaigns (Apr-Jun, Sep-Oct). 29 codes.
              </p>
              <p style={{ margin: "0 0 4px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>MSP SE (Sales Event)</span> — Spring Sales Event (Feb-Mar), Summer Getaway (Jul-Aug),
                Winter Sales Event (Nov-Dec). 87 codes. Databricks had almost all of these labeled as just "MSP".
              </p>
              <p style={{ margin: "0 0 4px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>Launch</span> — IONIQ 9 Pre-Launch/Launch, Palisade ICE Launch, Palisade HEV Launch. 32 codes.
              </p>
              <p style={{ margin: "0 0 4px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>Alt Fuel</span> — HEV Brand awareness, Tax Credit campaigns, EV Incentive SMS. 13 codes.
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>Other</span> — Anniversary, Welcome Kit, Miscellaneous (NYC Auto Show, EV Incentive SMS). Non-sales flagged.
              </p>
              </>) : (<>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                Two classification sources are available via the Heroku / Chalsea toggle:
              </p>
              <p style={{ margin: "0 0 4px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>Heroku</span> — Derived from the <span style={{ fontFamily: "monospace", fontSize: 11 }}>group_campaign</span> field
                in the Heroku PostgreSQL campaign tables using keyword matching (e.g., "MSP", "LAUNCH", "ALT FUEL", "EMAGAZINE").
                Campaign codes not in the Heroku table (e.g., new SMS/DM-only campaigns) are classified from the MessageName field using the same keyword logic.
              </p>
              <p style={{ margin: "0 0 4px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>Chalsea</span> — Manually curated classification from the Household ID Campaign Codes spreadsheet.
                Some codes are classified differently between the two sources (e.g., Palisade HEV campaigns are "Launch" in Heroku but "Alt-Fuel Vehicle" in Chalsea's view).
              </p>
              <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                <span style={{ color: COLORS.text }}>Channel</span> is derived from the campaign name: campaigns containing "_DM" are classified as Direct Mail, "_SMS" as SMS, and all others as Email.
                <span style={{ color: COLORS.text }}> IsSales</span> is a boolean flag — Auto Show, eMag, Anniversary, Welcome, and Partnerships campaigns are flagged as non-sales.
              </p>
              </>)}
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>Glossary</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <tbody>
                  {[
                    ["Unique VINs", "Count of distinct Vehicle Identification Numbers — each VIN represents one car sale. This is the primary metric."],
                    ["Unique HH", "Count of distinct HouseholdId values — the number of unique households that purchased."],
                    ["Unique Groups", "Count of distinct GroupId values — the number of unique customer groups that purchased."],
                    ["Fractional", "Each VIN contributes 1.0 total, split equally across all campaigns that touched it. A VIN touched by 3 campaigns gives each campaign 1/3 credit. The denominator is fixed across all date filters — filtering to Q1 doesn't change each campaign's share, it only changes which campaigns are visible."],
                    ["Matches", "The total number of rows in the filtered dataset. A VIN can appear on multiple rows if it's associated with different HouseholdId/GroupId combinations."],
                    ["Attribution Window", is2025
                      ? "≤60 days only in this dataset. Both treated and control purchases are filtered to the same 60-day window. DaysBetween ≤30 is tagged 30-Day, 31-60 is tagged 60-Day. Cumulative mode shows running totals; Exclusive mode shows individual buckets."
                      : "The time between campaign send and vehicle purchase. Exclusive mode shows mutually exclusive buckets (e.g., 60-Day = purchases between 31-60 days). Cumulative mode shows running totals (e.g., ≤60 Days = all purchases within 0-60 days)."],
                    ["DaysToPurchase", "Number of days between the campaign send date and the purchase date. Used for cumulative window filtering (≤30, ≤60, ≤90, ≤180)."],
                    ["Channel", is2025
                      ? "Email, SMS, or DM (Direct Mail). DM dates in this dataset were corrected from campaign names (mid-month proxy) because the original DMSendLog had broken 2026 timestamps. The control_purchases table was unaffected — it already had correct 2025 DM dates."
                      : "The communication channel: Email, SMS (text message), or DM (Direct Mail). Derived from the campaign name. Email uses EmailSendLog, SMS uses SMSSendLog, DM uses DMSendLog."],
                    ["Sales Only / All", is2025
                      ? "All (default for 2025 tab) shows all campaigns. Sales Only excludes non-sales campaigns (Anniversary, Welcome Kit, Miscellaneous) and recalculates fractional."
                      : "Sales Only (default) excludes non-sales campaigns from the view and recalculates fractional using only sales campaigns. All includes everything."],
                    ["Campaign Purpose", "Sales or Non-sales classification. Sales campaigns are designed to drive purchases (MSP, Launch, Alt-Fuel Vehicle, Sales Event). Non-sales include eMag, Anniversary, Welcome, Auto Show, and other owner communications."],
                    ["Campaign Type", is2025
                      ? "Carlos's Program classification: MSP REG, MSP SE (Sales Event), Launch, Alt Fuel, Anniversary, Welcome Kit, Miscellaneous."
                      : "Sub-classification: MSP, Launch, Alt-Fuel Vehicle, Sales Event, Auto Show, eMag, Anniversary, Welcome, etc."],
                    ["Campaign Group", is2025
                      ? "Carlos's Group column from HMA_2025_Campaign_codes_x_Type.xlsx — the campaign group (e.g., [G] 2025 HMA MSP REG APR, [G] 2025 HMA Summer Getaway)."
                      : "The Heroku group_campaign field — the journey/group that the campaign belongs to (e.g., [G] 2025 HMA MSP)."],
                    ["Sent Date", "Per-campaign sent date. When filtering by sent date range, only campaigns actually sent within the range appear — other campaigns on the same VIN are excluded from the view but still count toward the fractional denominator."],
                  ].map(([term, desc]) => (
                    <tr key={term} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <td style={{ padding: "8px 12px 8px 0", color: COLORS.text, fontWeight: 600, whiteSpace: "nowrap", verticalAlign: "top" }}>{term}</td>
                      <td style={{ padding: "8px 0", color: COLORS.textMuted }}>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>Dashboard Filters</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                  <th style={{ padding: "6px 8px 6px 0", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Filter</th>
                  <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Type</th>
                  <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Description</th>
                </tr></thead>
                <tbody>
                  {[
                    ["Sales Only / All", "Toggle", "Sales Only (default) excludes non-sales campaigns. All includes eMag, Auto Show, Anniversary, Welcome campaigns"],
                    ["Channel", "Multi-select", "Filter by communication channel: Email, SMS, or DM (Direct Mail)"],
                    ["Campaign Purpose", "Multi-select", "Filter by Sales or Non-sales classification"],
                    ["Campaign Type", "Multi-select", "Filter by sub-type (MSP, Launch, Alt-Fuel Vehicle, etc.)"],
                    ["Campaigns", "Multi-select", "Filter by specific 6-digit campaign codes"],
                    ["Models", "Multi-select", "Filter by vehicle model group (TUCSON, PALISADE, etc.)"],
                    ["Years", "Multi-select", "Filter by vehicle model year (2022-2026)"],
                    ["Trims", "Multi-select", "Filter by vehicle trim level (SE, SEL, LIMITED, etc.)"],
                    ["Messages", "Multi-select", "Filter by SFMC message/journey name"],
                    ["Sent Date", "Date range + presets", "Filter by campaign send date range. Quick-set buttons for years and quarters. Click multiple quarters to extend the range. Filters per-campaign sent dates, not per-VIN"],
                    ["Purchase Date", "Date range + presets", "Filter by vehicle purchase date range. Same quick-set controls as Sent Date"],
                    ["Attribution Window", "Single-select", "Filter by time window. Cumulative includes shorter windows; Exclusive shows mutually exclusive buckets that sum to total"],
                    ["Classification Source", "Toggle", "Switch between Heroku-derived and Chalsea-curated classifications"],
                  ].map(([filter, type, desc]) => (
                    <tr key={filter} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <td style={{ padding: "6px 8px 6px 0", color: COLORS.text, fontWeight: 600, whiteSpace: "nowrap", verticalAlign: "top" }}>{filter}</td>
                      <td style={{ padding: "6px 8px", color: COLORS.textDim, whiteSpace: "nowrap", verticalAlign: "top" }}>{type}</td>
                      <td style={{ padding: "6px 8px", color: COLORS.textMuted }}>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16, marginTop: 8 }}>
              <button onClick={() => setShowTech(t => !t)}
                style={{ background: "none", border: "none", color: COLORS.accent, fontSize: 13, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 10 }}>{showTech ? "▼" : "▶"}</span> Want to know more technical details?
              </button>

              {showTech && (
                <div style={{ marginTop: 16 }}>

                  {is2025 ? (<>
                  {/* ═══ SALESMATCH 2025 TECHNICAL DETAILS ═══ */}

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>1. SFMC Automation Studio — Source Queries</div>
                    <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                      Three SFMC Query Activities run in sequence (Email overwrite, SMS append, DM append) to produce the
                      <span style={{ fontFamily: "monospace", fontSize: 11 }}> Salesmatch_Combined</span> Data Extension. Each query follows the same pattern:
                    </p>
                    <pre style={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, padding: 14, fontSize: 11, color: "#94a3b8", overflowX: "auto", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{`-- Email channel (overwrite mode)
SELECT DISTINCT
    ctm.FirstName, ctm.LastName, ctm.ContactKey,
    ctm.GroupId, ctm.HouseholdId,
    atm.VIN, atm.ModelGroupDesc, ats.SeriesDescription,
    atm.ModelYear, atm.Trim,
    ats.RDRCPOType AS SaleType, esl.CampaignCode,
    esl.SentDate, LEFT(esl.MessageName, 254) AS MessageName,
    CASE WHEN ISDATE(ats.RDRCPODate) = 1
         THEN CONVERT(DATE, ats.RDRCPODate)
         ELSE CONVERT(DATE, ats.PurchaseDate)
    END AS PurchaseDate,
    DATEDIFF(DAY, esl.SentDate, <PurchaseDate>) AS DaysToPurchase,
    CASE WHEN DaysToPurchase <= 30 THEN '30-Day'
         WHEN DaysToPurchase <= 60 THEN '60-Day'
         WHEN DaysToPurchase <= 90 THEN '90-Day'
         WHEN DaysToPurchase <= 180 THEN '180-Day' END AS AttributionWindow
FROM EmailSendLog esl
INNER JOIN ContactTargetMaster_Main ctm ON esl.ContactKey = ctm.ContactKey
INNER JOIN AssetTargetMaster_Main atm ON ctm.ContactKey = atm.ContactKey
INNER JOIN AssetTargetMaster_Sub1 ats ON atm.VIN = ats.VIN
WHERE esl.Delivered = 'TRUE'
    AND esl.CampaignCode IN (<campaign codes>)
    AND ats.RDRCPOType = 'RDR'
    AND PurchaseDate >= DATEADD(MONTH, -18, GETDATE())
    AND PurchaseDate >= esl.SentDate
    AND DATEDIFF(DAY, esl.SentDate, PurchaseDate) <= 180

-- SMS channel (append mode): same joins, but FROM SMSSendLog
-- DM channel (append mode): same joins, but FROM DMSendLog
--   ⚠ DM SentDate = data-load timestamp (2026), NOT actual mail date`}</pre>
                    <p style={{ margin: "8px 0", color: COLORS.textMuted, fontSize: 11 }}>
                      The combined DE is exported via SFTP and ingested into Databricks as
                      <span style={{ fontFamily: "monospace" }}> ius_unity_prod.sfmc.salesmatch_combined</span> (562K total rows, ~196K at ≤60-day).
                    </p>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>2. Databricks Tables Used</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                      <thead><tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                        <th style={{ padding: "6px 8px 6px 0", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Table</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Schema</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Role in Corrected Dataset</th>
                      </tr></thead>
                      <tbody>
                        {[
                          ["salesmatch_combined", "sfmc", "Email/SMS rows with correct SentDates. DM rows excluded (broken dates). Also source for vehicle info (ModelGroupDesc, ModelYear, Trim) and HouseholdId/GroupId enrichment."],
                          ["treated_campaign", "sfmc", "47M rows. DM treated households (HouseholdId, CampaignCode, Segment). Joined to Email purchase universe to build corrected DM matchback."],
                          ["control_campaign", "sfmc", "Control group population by CampaignCode × Segment × HouseholdId. Used for Treated vs Control buy rates."],
                          ["control_purchases", "sfmc", "Control group purchases. Has correct DM dates (unaffected by treated-side bug). Filtered to ≤60-day attribution to match treated."],
                          ["treated_summary", "sfmc", "Aggregate treated HH counts per campaign. Used for buy rate denominators."],
                          ["salesmatch_corrected_2025", "sfmc", "OUTPUT: Persisted Delta table (191,989 rows, 100,612 VINs). Created by HMA_2025_Salesmatch_Extract notebook."],
                        ].map(([tbl, schema, desc]) => (
                          <tr key={tbl} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                            <td style={{ padding: "5px 8px 5px 0", color: COLORS.text, fontFamily: "monospace", fontSize: 10 }}>{tbl}</td>
                            <td style={{ padding: "5px 8px", color: COLORS.textDim, fontSize: 10 }}>{schema}</td>
                            <td style={{ padding: "5px 8px", color: COLORS.textMuted }}>{desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>3. DM Correction — The Fix</div>
                    <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>
                      Since the raw RDR purchase table (<span style={{ fontFamily: "monospace", fontSize: 11 }}>rdr_datawarehouse</span>) is not accessible,
                      the correction uses the <span style={{ color: COLORS.text }}>Email purchase universe</span> as the baseline:
                    </p>
                    <pre style={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, padding: 14, fontSize: 11, color: "#94a3b8", overflowX: "auto", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{`-- Step 1: Get Email purchases (correct dates, correct VINs)
email_purchases = salesmatch_combined
    WHERE Channel = 'Email'
    AND AttributionWindow IN ('30-Day','60-Day')
    AND CampaignCode IN (<Carlos 2025 codes>)
    → yields HouseholdId, VIN, PurchaseDate per purchase

-- Step 2: Get DM treated households
dm_treated = treated_campaign
    WHERE CampaignCode IN ('033174','033643','033681','033798',
          '033817','034012','034041')  -- 7 codes with treated records
    → yields HouseholdId, CampaignCode

-- Step 3: Join on HouseholdId
dm_corrected = dm_treated
    JOIN email_purchases ON HouseholdId
    WHERE corrected_send_date <= PurchaseDate
    AND DATEDIFF(PurchaseDate, corrected_send_date) <= 60

-- Send dates derived from campaign names:
-- 033174 → 2025-06-15 (JUN MSP REG DM)
-- 033643 → 2025-06-15 (JUN WELCOME DM)
-- 033681 → 2025-06-15 (JUN ANNIVERSARY DM)
-- 033798 → 2025-10-15 (OCT MSP REG DM)
-- 033817 → 2025-12-15 (DEC PALISADE HEV DM)
-- 034012 → 2025-11-15 (NOV MSP SE DM)
-- 034041 → 2025-12-15 (DEC MSP SE DM)

-- 5 codes NOT correctable (no treated_campaign records):
-- 033130, 033561, 033680, 033777, 033799`}</pre>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>4. External Inputs</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                      <thead><tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                        <th style={{ padding: "6px 8px 6px 0", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>File</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Source</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Used For</th>
                      </tr></thead>
                      <tbody>
                        {[
                          ["HMA_2025_Campaign_codes_x_Type.xlsx", "Carlos Cascante (INNOCEAN)", "208 campaign codes → Program classification (MSP REG / MSP SE / Launch / Alt Fuel / etc). Overrides Databricks CampaignType which incorrectly labeled nearly all campaigns as 'MSP'."],
                          ["2024_Year-End_Results_Summary_r11_60_Day.xlsx", "Chalsea-Blaze Chen (INNOCEAN)", "2024 comparison numbers: 91,954 DB sales, 33,138 Prospect, HH counts per audience, 2024 buy rates, ROI. Tab: ALL 2024-YE_60."],
                        ].map(([file, src, use]) => (
                          <tr key={file} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                            <td style={{ padding: "5px 8px 5px 0", color: COLORS.text, fontFamily: "monospace", fontSize: 10 }}>{file}</td>
                            <td style={{ padding: "5px 8px", color: COLORS.textDim }}>{src}</td>
                            <td style={{ padding: "5px 8px", color: COLORS.textMuted }}>{use}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>5. Notebooks & Deployment</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                      <thead><tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                        <th style={{ padding: "6px 8px 6px 0", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Notebook</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Purpose</th>
                      </tr></thead>
                      <tbody>
                        {[
                          ["HMA_2025_Salesmatch_Extract.py", "Creates salesmatch_corrected_2025 Delta table. Pulls Email/SMS from salesmatch_combined, re-matches DM via treated_campaign, applies Carlos mapping, computes fractional attribution."],
                          ["Process_Salesmatch_2025_Dashboard.py", "Reads corrected table, enriches with vehicle/HH data, builds QA-compatible JSON (v:3 format with campaignClass, headlines, ctrl, campSeg, etc.), pushes to S3 + CloudFront."],
                        ].map(([nb, purpose]) => (
                          <tr key={nb} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                            <td style={{ padding: "5px 8px 5px 0", color: COLORS.text, fontFamily: "monospace", fontSize: 10 }}>{nb}</td>
                            <td style={{ padding: "5px 8px", color: COLORS.textMuted }}>{purpose}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p style={{ margin: "8px 0 0", color: COLORS.textDim, fontSize: 11 }}>
                      Deployment: salesmatch_2025.json → s3://ius-firstparty-dashboard/ → CloudFront (E3BKSDXQTC06Z3) → Dashboard fetches at load.
                    </p>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>6. Column Definitions (Corrected Table)</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                      <thead><tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                        <th style={{ padding: "6px 8px 6px 0", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Column</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Source</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Description</th>
                      </tr></thead>
                      <tbody>
                        {[
                          ["VIN", "AssetTargetMaster_Main", "Vehicle Identification Number — 17-char unique identifier. Primary metric."],
                          ["PurchaseDate", "AssetTargetMaster_Sub1", "Uses RDRCPODate if valid, else PurchaseDate. From Email purchase universe."],
                          ["CampaignCode", "SendLog / treated_campaign", "6-digit SFMC campaign identifier (e.g., 033978)"],
                          ["MessageName", "SendLog", "SFMC message/journey name (truncated to 254 chars). Used for campaign lookup."],
                          ["DerivedSentDate", "Calculated", "Email/SMS: original SentDate. DM: mid-month proxy from campaign name (e.g., 2025-06-15 for JUN campaigns)."],
                          ["DaysBetween", "Calculated", "DATEDIFF(PurchaseDate, DerivedSentDate). Filtered to 0–60."],
                          ["Channel", "SendLog table used", "Email, SMS, or DM. DM channel re-matched via treated_campaign join."],
                          ["CampaignTouches", "Calculated", "Total qualifying rows for this VIN across all channels/campaigns."],
                          ["FractionalSale", "Calculated", "1 / CampaignTouches. Sum across all rows = 100,612 (total unique VINs)."],
                          ["Source", "Calculated", "'Email', 'SMS', or 'DM_Corrected'. Tags which correction path was used."],
                        ].map(([col, src, desc]) => (
                          <tr key={col} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                            <td style={{ padding: "5px 8px 5px 0", color: COLORS.text, fontFamily: "monospace", fontSize: 10 }}>{col}</td>
                            <td style={{ padding: "5px 8px", color: COLORS.textDim, fontSize: 10 }}>{src}</td>
                            <td style={{ padding: "5px 8px", color: COLORS.textMuted }}>{desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  </>) : (<>
                  {/* ═══ SALESMATCH QA TECHNICAL DETAILS (original) ═══ */}

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>SFMC SQL Query</div>
                    <p style={{ margin: "0 0 8px", color: COLORS.textMuted }}>The following query runs as a single SFMC Query Activity in Automation Studio:</p>
                    <pre style={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, padding: 14, fontSize: 11, color: "#94a3b8", overflowX: "auto", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{`SELECT DISTINCT
    ctm.FirstName, ctm.LastName, ctm.ContactKey,
    ctm.GroupId, ctm.HouseholdId,
    atm.VIN, atm.ModelGroupDesc, ats.SeriesDescription,
    atm.ModelYear, atm.Trim,
    ats.RDRCPOType AS SaleType, esl.CampaignCode,
    esl.SentDate, LEFT(esl.MessageName, 254) AS MessageName,
    CASE WHEN ISDATE(ats.RDRCPODate) = 1
         THEN CONVERT(DATE, ats.RDRCPODate)
         ELSE CONVERT(DATE, ats.PurchaseDate)
    END AS PurchaseDate,
    DATEDIFF(DAY, esl.SentDate, <PurchaseDate>) AS DaysToPurchase,
    CASE WHEN DaysToPurchase <= 30 THEN '30-Day'
         WHEN DaysToPurchase <= 60 THEN '60-Day'
         WHEN DaysToPurchase <= 90 THEN '90-Day'
         WHEN DaysToPurchase <= 180 THEN '180-Day'
    END AS AttributionWindow
FROM EmailSendLog esl
INNER JOIN ContactTargetMaster_Main ctm
    ON esl.ContactKey = ctm.ContactKey
INNER JOIN AssetTargetMaster_Main atm
    ON ctm.ContactKey = atm.ContactKey
INNER JOIN AssetTargetMaster_Sub1 ats
    ON atm.VIN = ats.VIN
WHERE esl.Delivered = 'TRUE'
    AND esl.CampaignCode IN (<196 campaign codes>)
    AND ats.RDRCPOType = 'RDR'
    AND PurchaseDate >= DATEADD(MONTH, -18, GETDATE())
    AND PurchaseDate >= esl.SentDate
    AND DATEDIFF(DAY, esl.SentDate, PurchaseDate) <= 180`}</pre>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>Query Column Definitions</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                      <thead><tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                        <th style={{ padding: "6px 8px 6px 0", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Column</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Source Table</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Description</th>
                      </tr></thead>
                      <tbody>
                        {[
                          ["FirstName", "ContactTargetMaster_Main", "Customer first name"],
                          ["LastName", "ContactTargetMaster_Main", "Customer last name"],
                          ["ContactKey", "EmailSendLog / CTM", "Unique customer identifier — join key between sends and master"],
                          ["GroupId", "ContactTargetMaster_Main", "Customer group identifier"],
                          ["HouseholdId", "ContactTargetMaster_Main", "Household identifier — multiple contacts can share a household"],
                          ["VIN", "AssetTargetMaster_Main", "Vehicle Identification Number — 17-character unique identifier"],
                          ["ModelGroupDesc", "AssetTargetMaster_Main", "Vehicle model group (e.g., TUCSON, PALISADE)"],
                          ["SeriesDescription", "AssetTargetMaster_Sub1", "Detailed vehicle series description"],
                          ["ModelYear", "AssetTargetMaster_Main", "Model year (e.g., 2025, 2026)"],
                          ["Trim", "AssetTargetMaster_Main", "Trim level (e.g., SEL, LIMITED)"],
                          ["SaleType", "AssetTargetMaster_Sub1", "Sale type — filtered to RDR (new vehicle retail delivery) only"],
                          ["CampaignCode", "EmailSendLog", "6-digit SFMC campaign identifier (e.g., 033978)"],
                          ["SentDate", "EmailSendLog", "Date/time the email was delivered"],
                          ["MessageName", "EmailSendLog", "SFMC message/journey name (truncated to 254 chars)"],
                          ["PurchaseDate", "AssetTargetMaster_Sub1", "Purchase date — uses RDRCPODate if valid, otherwise PurchaseDate"],
                          ["DaysToPurchase", "Calculated", "Days between SentDate and PurchaseDate (0 = same day)"],
                          ["AttributionWindow", "Calculated", "Bucket: 30-Day (0-30), 60-Day (31-60), 90-Day (61-90), 180-Day (91-180)"],
                        ].map(([col, src, desc]) => (
                          <tr key={col} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                            <td style={{ padding: "5px 8px 5px 0", color: COLORS.text, fontFamily: "monospace", fontSize: 10 }}>{col}</td>
                            <td style={{ padding: "5px 8px", color: COLORS.textDim, fontSize: 10 }}>{src}</td>
                            <td style={{ padding: "5px 8px", color: COLORS.textMuted }}>{desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.accent, marginBottom: 8 }}>Query Filters Explained</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                      <thead><tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                        <th style={{ padding: "6px 8px 6px 0", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Filter</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Value</th>
                        <th style={{ padding: "6px 8px", textAlign: "left", color: COLORS.text, fontWeight: 600 }}>Purpose</th>
                      </tr></thead>
                      <tbody>
                        {[
                          ["esl.Delivered = 'TRUE'", "TRUE", "Only delivered emails (excludes bounces)"],
                          ["CampaignCode IN (...)", "196 codes", "Known HMA campaign codes from Heroku dm.dih_hma_campaign"],
                          ["RDRCPOType = 'RDR'", "RDR", "New vehicle sales only (excludes CPO)"],
                          ["PurchaseDate >= DATEADD(-18 months)", "18-month lookback", "Purchases within the last 18 months"],
                          ["PurchaseDate >= SentDate", "Forward-looking", "Purchase must happen after the email was sent"],
                          ["DATEDIFF(...) <= 180", "180-day max", "Max 180 days between send and purchase"],
                        ].map(([filter, val, purpose]) => (
                          <tr key={filter} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                            <td style={{ padding: "5px 8px 5px 0", color: COLORS.text, fontFamily: "monospace", fontSize: 10 }}>{filter}</td>
                            <td style={{ padding: "5px 8px", color: COLORS.textDim }}>{val}</td>
                            <td style={{ padding: "5px 8px", color: COLORS.textMuted }}>{purpose}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  </>)}

                </div>
              )}
            </div>

            <div style={{ textAlign: "right", marginTop: 16 }}>
              <button onClick={() => setShowInfo(false)}
                style={{ padding: "8px 24px", background: COLORS.accent, border: "none", borderRadius: 6, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── FILTERS ── */}
      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 10, padding: "14px 18px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: COLORS.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Filters</span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {["all", "sales"].map(s => (
                <button key={s} onClick={() => setSalesOnly(s === "sales")}
                  style={{ padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: (s === "sales" ? salesOnly : !salesOnly) ? 700 : 400, background: (s === "sales" ? salesOnly : !salesOnly) ? (s === "sales" ? "rgba(34,197,94,0.15)" : "rgba(14,165,233,0.15)") : "transparent", border: `1px solid ${(s === "sales" ? salesOnly : !salesOnly) ? (s === "sales" ? "rgba(34,197,94,0.5)" : COLORS.accent) : "#334155"}`, color: (s === "sales" ? salesOnly : !salesOnly) ? (s === "sales" ? "#22c55e" : COLORS.accent) : COLORS.textDim, cursor: "pointer", textTransform: "capitalize" }}>
                  {s === "all" ? "All" : "Sales Only"}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 10, color: COLORS.textDim }}>Classification:</span>
            {["heroku", "chalsea"].map(s => (
              <button key={s} onClick={() => { setClassSource(s); setPurposeFilter(new Set()); setTypeFilter(new Set()); }}
                style={{ padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: classSource === s ? 700 : 400, background: classSource === s ? "rgba(14,165,233,0.15)" : "transparent", border: `1px solid ${classSource === s ? COLORS.accent : "#334155"}`, color: classSource === s ? COLORS.accent : COLORS.textDim, cursor: "pointer" }}>
                {s === "heroku" ? "Heroku" : "Chalsea"}
              </button>
            ))}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <SmMultiSelect selected={purposeFilter} onChange={setPurposeFilter} options={purposeOptions} noun="Purpose" mobile={mobile} />
          <SmMultiSelect selected={typeFilter} onChange={setTypeFilter} options={typeOptions} noun="Type" mobile={mobile} />
          <SmMultiSelect selected={channelFilter} onChange={setChannelFilter} options={channelOptions} noun="Channel" mobile={mobile} />
          <SmMultiSelect selected={campaignFilter} onChange={setCampaignFilter} options={brandFilterOptions.campaigns || []} noun="Campaigns" labelFn={c => campLabel[c] ? `${c} — ${campLabel[c]}` : c} mobile={mobile} />
          <SmMultiSelect selected={modelFilter} onChange={setModelFilter} options={brandFilterOptions.models || []} noun="Models" mobile={mobile} />
          <SmMultiSelect selected={yearFilter} onChange={setYearFilter} options={brandFilterOptions.modelYears || []} noun="Years" mobile={mobile} />
          <SmMultiSelect selected={trimFilter} onChange={setTrimFilter} options={brandFilterOptions.trims || []} noun="Trims" mobile={mobile} />
          <SmMultiSelect selected={messageFilter} onChange={setMessageFilter} options={fv.messageNames || []} noun="Messages" mobile={mobile} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          {[
            { label: "Sent", start: sentStart, end: sentEnd, setStart: setSentStart, setEnd: setSentEnd, prefix: "s" },
            { label: "Purchase", start: purchaseStart, end: purchaseEnd, setStart: setPurchaseStart, setEnd: setPurchaseEnd, prefix: "p" },
          ].map(dr => {
            const presets = [
              { l: "2025", s: "2025-01-01", e: "2025-12-31", yr: true },
              { l: "2026", s: "2026-01-01", e: "2026-12-31", yr: true },
              { l: "Q1 '25", s: "2025-01-01", e: "2025-03-31" },
              { l: "Q2 '25", s: "2025-04-01", e: "2025-06-30" },
              { l: "Q3 '25", s: "2025-07-01", e: "2025-09-30" },
              { l: "Q4 '25", s: "2025-10-01", e: "2025-12-31" },
              { l: "Q1 '26", s: "2026-01-01", e: "2026-03-31" },
              { l: "Q2 '26", s: "2026-04-01", e: "2026-06-30" },
            ];
            // Determine which presets are "active" (within the current date range)
            const isActive = (p) => dr.start && dr.end && p.s >= dr.start && p.e <= dr.end;
            const handlePreset = (p) => {
              if (p.yr) {
                // Year presets always replace
                if (dr.start === p.s && dr.end === p.e) { dr.setStart(""); dr.setEnd(""); }
                else { dr.setStart(p.s); dr.setEnd(p.e); }
                return;
              }
              if (!dr.start || !dr.end) {
                // No range set — start fresh
                dr.setStart(p.s); dr.setEnd(p.e);
              } else if (isActive(p)) {
                // Clicking an active quarter — shrink the range
                // Find which active quarters remain after removing this one
                const activeQs = presets.filter(q => !q.yr && q !== p && isActive(q));
                if (activeQs.length === 0) { dr.setStart(""); dr.setEnd(""); }
                else { dr.setStart(activeQs[0].s); dr.setEnd(activeQs[activeQs.length - 1].e); }
              } else {
                // Extend the range to include this quarter
                dr.setStart(p.s < dr.start ? p.s : dr.start);
                dr.setEnd(p.e > dr.end ? p.e : dr.end);
              }
            };
            return (
              <div key={dr.prefix} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 11, color: COLORS.textDim, width: 60 }}>{dr.label}:</span>
                <input type="date" value={dr.start} onChange={e => dr.setStart(e.target.value)} style={dateInputStyle} />
                <span style={{ color: COLORS.textDim }}>→</span>
                <input type="date" value={dr.end} onChange={e => dr.setEnd(e.target.value)} style={dateInputStyle} />
                {presets.map(p => (
                  <button key={`${dr.prefix}-${p.l}`} onClick={() => handlePreset(p)}
                    style={{ padding: "2px 7px", borderRadius: 3, fontSize: 10, background: isActive(p) || (dr.start === p.s && dr.end === p.e) ? "rgba(14,165,233,0.15)" : "transparent", border: `1px solid ${isActive(p) || (dr.start === p.s && dr.end === p.e) ? COLORS.accent : "#334155"}`, color: isActive(p) || (dr.start === p.s && dr.end === p.e) ? COLORS.accent : COLORS.textDim, cursor: "pointer" }}>{p.l}</button>
                ))}
                {(dr.start || dr.end) && <button onClick={() => { dr.setStart(""); dr.setEnd(""); }} style={{ padding: "2px 7px", borderRadius: 3, fontSize: 10, background: "transparent", border: "1px solid #334155", color: COLORS.textDim, cursor: "pointer" }}>✕</button>}
              </div>
            );
          })}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            {isFiltered && <button onClick={clearAll} style={{ padding: "5px 10px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 5, color: COLORS.error, fontSize: 11, cursor: "pointer" }}>✕ Clear All</button>}
          </div>
        </div>
      </div>

      {/* ── ATTRIBUTION WINDOW PILLS ── */}
      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 10, padding: "14px 18px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: COLORS.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Attribution Window</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 10, color: COLORS.textDim }}>Mode:</span>
            {["cumulative", "exclusive"].map(m => (
              <button key={m} onClick={() => { setWindowMode(m); setWindowFilter("ALL"); }}
                style={{ padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: windowMode === m ? 700 : 400, background: windowMode === m ? "rgba(14,165,233,0.15)" : "transparent", border: `1px solid ${windowMode === m ? COLORS.accent : "#334155"}`, color: windowMode === m ? COLORS.accent : COLORS.textDim, cursor: "pointer", textTransform: "capitalize" }}>
                {m}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["ALL", ...(brandFilterOptions.windows || []).sort((a, b) => parseInt(a) - parseInt(b))].map(w => {
            const active = windowFilter === w;
            return (<button key={w} onClick={() => setWindowFilter(w)} style={{ padding: "10px 18px", borderRadius: 8, background: active ? COLORS.accent : "rgba(255,255,255,0.03)", border: `1px solid ${active ? COLORS.accent : COLORS.cardBorder}`, color: active ? "#fff" : COLORS.textMuted, cursor: "pointer", textAlign: "center", minWidth: 100 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{pillLabel(w)}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: active ? "#fff" : COLORS.text }}>{getPillVins(w).toLocaleString()}</div>
              <div style={{ fontSize: 10, opacity: 0.7 }}>VINs</div>
            </button>);
          })}
        </div>
      </div>

      {/* ── VIEW BY + TOTALS + TABLE ── */}
      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.cardBorder}`, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 600 }}>VIEW BY:</span>
          {[{ id: "campaignType", label: "Campaign Type" }, { id: "campaignGroup", label: "Campaign Group" }, { id: "campaign", label: "Campaign" }, { id: "model", label: "Model" }, { id: "modelYear", label: "Model Year" }, { id: "message", label: "Message" }, ...(Object.keys(ctrlData).length > 0 ? [{ id: "treatedVsControl", label: "Treated vs Control" }] : []), ...(segCompData.length > 0 || Object.keys(campSegData).length > 0 ? [{ id: "segmentComposition", label: "Segment × Decile" }] : []), ...(Object.keys(campSegData).length > 0 ? [{ id: "campaignSegment", label: "Campaign × Decile" }] : [])].map(v => (
            <button key={v.id} onClick={() => { setViewBy(v.id); setExpanded(new Set()); }} style={{ padding: "5px 12px", background: viewBy === v.id ? COLORS.accent : "transparent", border: `1px solid ${viewBy === v.id ? COLORS.accent : COLORS.cardBorder}`, borderRadius: 5, color: viewBy === v.id ? "#fff" : COLORS.textMuted, fontSize: 12, cursor: "pointer" }}>{v.label}</button>
          ))}
          <span style={{ marginLeft: "auto", fontSize: 11, color: COLORS.textDim }}>{viewBy === "treatedVsControl" ? (tvcData.byCode ? tvcData.byCode.length : 0) : viewBy === "segmentComposition" ? (segCompData.length || Object.keys(campSegData).length) : viewBy === "campaignSegment" ? Object.keys(campSegData).length : sorted.length} rows</span>
          <button onClick={() => downloadExcel()} style={{ padding: "5px 12px", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 5, color: "#22c55e", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 13 }}>📥</span> Export Excel
          </button>
        </div>
        {viewBy === "treatedVsControl" && tvcData.byType ? (
          /* ── TREATED VS CONTROL TABLE ── */
          <div>
            {/* Summary stats bar */}
            <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${COLORS.cardBorder}`, background: "rgba(14,165,233,0.04)" }}>
              {[
                { l: "Treated Buy Rate", v: tvcData.overall.t_rate !== null ? tvcData.overall.t_rate.toFixed(3) + "%" : "—", a: true },
                { l: "Control Buy Rate", v: tvcData.overall.c_rate !== null ? tvcData.overall.c_rate.toFixed(3) + "%" : "—" },
                { l: "Incremental Lift", v: tvcData.overall.lift_pp !== null ? (tvcData.overall.lift_pp >= 0 ? "+" : "") + tvcData.overall.lift_pp.toFixed(3) + "pp" : "—" },
                { l: "Lift %", v: tvcData.overall.lift_pct !== null ? (tvcData.overall.lift_pct >= 0 ? "+" : "") + tvcData.overall.lift_pct.toFixed(1) + "%" : "—" },
                { l: "Incremental Sales", v: tvcData.overall.incr_sales !== null ? tvcData.overall.incr_sales.toLocaleString() : "—" },
              ].map((m, i) => (
                <div key={m.l} style={{ flex: 1, padding: "12px 16px", borderRight: i < 4 ? `1px solid ${COLORS.cardBorder}` : "none", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: m.a ? COLORS.accent : COLORS.text }}>{m.v}</div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>{m.l}</div>
                </div>
              ))}
            </div>

            {/* By Campaign Type */}
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.cardBorder}` }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted }}>BY CAMPAIGN TYPE</span>
            </div>
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: COLORS.card, zIndex: 2 }}>
                  <tr>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Campaign Type</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Treated Sent</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Treated Purch</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Treated Rate</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Control Pop</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Control Purch</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Control Rate</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Lift (pp)</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Lift %</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Incr. Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {[...tvcData.byType, { ...tvcData.overall, type: "OVERALL" }].map((r, i) => {
                    const isOverall = r.type === "OVERALL";
                    const liftColor = (r.lift_pp || 0) > 0 ? "#22c55e" : (r.lift_pp || 0) < 0 ? "#ef4444" : COLORS.textMuted;
                    return (
                      <tr key={r.type} style={{ borderBottom: `1px solid ${COLORS.cardBorder}`, background: isOverall ? "rgba(14,165,233,0.06)" : i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                        <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: isOverall ? 700 : 500, color: isOverall ? COLORS.accent : COLORS.text }}>{r.type}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, color: COLORS.textMuted }}>{(r.t_sent || 0).toLocaleString()}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, color: COLORS.text }}>{(r.t_purch || 0).toLocaleString()}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 600, color: COLORS.accent }}>{r.t_rate !== null ? r.t_rate.toFixed(3) + "%" : "—"}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, color: COLORS.textMuted }}>{(r.c_pop || 0).toLocaleString()}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, color: COLORS.text }}>{(r.c_purch || 0).toLocaleString()}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 600, color: "#f59e0b" }}>{r.c_rate !== null ? r.c_rate.toFixed(3) + "%" : "—"}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: liftColor }}>{r.lift_pp !== null ? (r.lift_pp >= 0 ? "+" : "") + r.lift_pp.toFixed(3) + "pp" : "—"}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 600, color: liftColor }}>{r.lift_pct !== null ? (r.lift_pct >= 0 ? "+" : "") + r.lift_pct.toFixed(1) + "%" : "—"}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 13, fontWeight: 700, color: liftColor }}>{r.incr_sales !== null ? r.incr_sales.toLocaleString() : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* By Campaign Code */}
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.cardBorder}`, borderTop: `1px solid ${COLORS.cardBorder}` }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted }}>BY CAMPAIGN ({tvcData.byCode.length} campaigns with control data)</span>
            </div>
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0, background: COLORS.card, zIndex: 2 }}>
                  <tr>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Campaign</th>
                    <th style={{ padding: "10px 12px", textAlign: "center", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Type</th>
                    <th style={{ padding: "10px 12px", textAlign: "center", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Ch</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>T.Rate</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>C.Rate</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Lift (pp)</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Incr. Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {tvcData.byCode.map((r, i) => {
                    const liftColor = (r.lift_pp || 0) > 0 ? "#22c55e" : (r.lift_pp || 0) < 0 ? "#ef4444" : COLORS.textMuted;
                    const chColor = r.channel === "SMS" ? "#22c55e" : r.channel === "DM" ? "#f59e0b" : COLORS.textDim;
                    return (
                      <tr key={r.code} style={{ borderBottom: `1px solid ${COLORS.cardBorder}`, background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                        <td style={{ padding: "8px 12px", fontSize: 12, color: COLORS.text }}>{r.code}{campLabel[r.code] ? <span style={{ color: COLORS.textDim, marginLeft: 6, fontSize: 11 }}>— {campLabel[r.code]}</span> : ""}</td>
                        <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 10, color: COLORS.textMuted }}>{r.type}</td>
                        <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 10, fontWeight: 600, color: chColor }}>{r.channel}</td>
                        <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, fontWeight: 600, color: COLORS.accent }}>{r.t_rate !== null ? r.t_rate.toFixed(3) + "%" : "—"}</td>
                        <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, fontWeight: 600, color: "#f59e0b" }}>{r.c_rate !== null ? r.c_rate.toFixed(3) + "%" : "—"}</td>
                        <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: liftColor }}>{r.lift_pp !== null ? (r.lift_pp >= 0 ? "+" : "") + r.lift_pp.toFixed(3) + "pp" : "—"}</td>
                        <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: liftColor }}>{r.incr_sales !== null ? r.incr_sales.toLocaleString() : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "12px 16px", fontSize: 11, color: COLORS.textDim, borderTop: `1px solid ${COLORS.cardBorder}` }}>
              Treated Rate = purchased HH / delivered contacts · Control Rate = purchased HH / control population · Lift = Treated Rate − Control Rate · Incremental Sales = Lift × Treated Sent
            </div>
          </div>
        ) : viewBy === "segmentComposition" && (segCompData.length > 0 || Object.keys(campSegData).length > 0) ? (
          /* ── SEGMENT × DECILE COMPOSITION ── */
          (() => {
            const SEG_LABELS = { CO: "Current Owner", PO: "Previous Owner", HR: "Hand Raiser", OTH: "Other" };
            const isFiltered = campaignFilter.size > 0 || typeFilter.size > 0 || channelFilter.size > 0 || purposeFilter.size > 0;

            // If we have per-campaign data, always compute from campSeg (filter-aware)
            // Falls back to pre-computed segComp only if no campSeg data available
            let displayData = [];
            if (Object.keys(campSegData).length > 0) {
              const segTotals = {};
              const filteredCodes = Object.keys(campSegData).filter(code => {
                const cl = codeClass[code] || {};
                if (salesOnly && !cl.isSales) return false;
                if (campaignFilter.size > 0 && !campaignFilter.has(code)) return false;
                if (typeFilter.size > 0 && !typeFilter.has(cl.type)) return false;
                if (channelFilter.size > 0 && !channelFilter.has(cl.channel)) return false;
                if (purposeFilter.size > 0 && !purposeFilter.has(cl.purpose)) return false;
                return true;
              });
              filteredCodes.forEach(code => {
                const tSeg = campSegData[code] || {};
                const cSeg = campCtrlSegData[code] || {};
                const tPurch = campTreatedSegPurchData[code] || {};
                const cPurch = campCtrlSegPurchData[code] || {};
                const allKeys = new Set([...Object.keys(tSeg), ...Object.keys(cSeg)]);
                allKeys.forEach(key => {
                  if (!segTotals[key]) segTotals[key] = { t_pop: 0, t_purch: 0, c_pop: 0, c_purch: 0 };
                  segTotals[key].t_pop += (tSeg[key] || 0);
                  segTotals[key].t_purch += (tPurch[key] || 0);
                  segTotals[key].c_pop += (cSeg[key] || 0);
                  segTotals[key].c_purch += (cPurch[key] || 0);
                });
              });
              displayData = Object.entries(segTotals).map(([key, v]) => {
                const [seg, decile] = key.split("|");
                return [seg, decile, v.t_pop, v.t_purch, v.c_pop, v.c_purch];
              }).sort((a, b) => a[0].localeCompare(b[0]) || b[2] - a[2]);
            } else {
              displayData = segCompData.length > 0 && segCompData[0].length >= 6 ? segCompData : segCompData.map(r => [r[0], r[1], r[2], 0, r[3], r[4]]);
            }

            const totalT = displayData.reduce((s, r) => s + r[2], 0);
            const totalTPurch = displayData.reduce((s, r) => s + r[3], 0);
            const totalC = displayData.reduce((s, r) => s + r[4], 0);
            const totalCPurch = displayData.reduce((s, r) => s + r[5], 0);
            // Group by segment
            const bySegment = {};
            displayData.forEach(([seg, decile, t_pop, t_purch, c_pop, c_purch]) => {
              if (!bySegment[seg]) bySegment[seg] = { seg, rows: [], t_total: 0, t_purch_total: 0, c_total: 0, c_purch_total: 0 };
              bySegment[seg].rows.push({ decile, t_pop, t_purch, c_pop, c_purch });
              bySegment[seg].t_total += t_pop;
              bySegment[seg].t_purch_total += t_purch;
              bySegment[seg].c_total += c_pop;
              bySegment[seg].c_purch_total += c_purch;
            });
            return (
              <div>
                {/* Summary bar */}
                <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${COLORS.cardBorder}`, background: "rgba(14,165,233,0.04)" }}>
                  {[
                    { l: "Treated Population", v: totalT.toLocaleString(), a: true },
                    { l: "Treated Sales", v: totalTPurch.toLocaleString() },
                    { l: "Control Population", v: totalC.toLocaleString() },
                    { l: "Control Sales", v: totalCPurch.toLocaleString() },
                    { l: "TBR", v: totalT > 0 ? (totalTPurch / totalT * 100).toFixed(3) + "%" : "—" },
                    { l: "CBR", v: totalC > 0 ? (totalCPurch / totalC * 100).toFixed(3) + "%" : "—" },
                  ].map((m, i) => (
                    <div key={m.l} style={{ flex: 1, padding: "12px 16px", borderRight: i < 4 ? `1px solid ${COLORS.cardBorder}` : "none", textAlign: "center" }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: m.a ? COLORS.accent : COLORS.text }}>{m.v}</div>
                      <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>{m.l}</div>
                    </div>
                  ))}
                </div>

                {/* Composition comparison header */}
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted }}>POPULATION COMPOSITION — TREATED vs CONTROL</span>
                  {isFiltered && <span style={{ fontSize: 11, color: "#f59e0b", marginLeft: 8 }}>(filtered)</span>}
                  <span style={{ fontSize: 11, color: COLORS.textDim, marginLeft: 8 }}>
                    Positive diff = treated over-indexed · Negative diff = control over-indexed
                  </span>
                </div>

                <div style={{ maxHeight: 500, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ position: "sticky", top: 0, background: COLORS.card, zIndex: 2 }}>
                      <tr>
                        <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Segment</th>
                        <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Decile</th>
                        <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Treated Pop</th>
                        <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Treated %</th>
                        <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Control Pop</th>
                        <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Control %</th>
                        <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Diff (pp)</th>
                        <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>T.Sales</th>
                        <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>TBR</th>
                        <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>C.Sales</th>
                        <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>CBR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(bySegment).map(sg => (
                        sg.rows.map((r, i) => {
                          const tPct = totalT > 0 ? r.t_pop / totalT * 100 : 0;
                          const cPct = totalC > 0 ? r.c_pop / totalC * 100 : 0;
                          const diff = tPct - cPct;
                          const diffColor = diff > 2 ? "#22c55e" : diff < -2 ? "#ef4444" : COLORS.textMuted;
                          const tRate = r.t_pop > 0 && r.t_purch > 0 ? r.t_purch / r.t_pop * 100 : 0;
                          const cRate = r.c_pop > 0 && r.c_purch > 0 ? r.c_purch / r.c_pop * 100 : 0;
                          const isFirst = i === 0;
                          return (
                            <tr key={sg.seg + r.decile} style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                              <td style={{ padding: "8px 12px", fontSize: 12, fontWeight: isFirst ? 600 : 400, color: isFirst ? COLORS.text : COLORS.textDim }}>{isFirst ? (SEG_LABELS[sg.seg] || sg.seg) : ""}</td>
                              <td style={{ padding: "8px 12px", fontSize: 12, color: COLORS.text }}>{r.decile}</td>
                              <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, color: COLORS.textMuted }}>{r.t_pop.toLocaleString()}</td>
                              <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, fontWeight: 600, color: COLORS.accent }}>{tPct.toFixed(1)}%</td>
                              <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, color: COLORS.textMuted }}>{r.c_pop.toLocaleString()}</td>
                              <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, fontWeight: 600, color: "#f59e0b" }}>{cPct.toFixed(1)}%</td>
                              <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: diffColor }}>{(diff >= 0 ? "+" : "") + diff.toFixed(1) + "pp"}</td>
                              <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, color: r.t_purch > 0 ? COLORS.text : COLORS.textDim }}>{r.t_purch > 0 ? r.t_purch.toLocaleString() : "—"}</td>
                              <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, fontWeight: 600, color: tRate > 0 ? COLORS.accent : COLORS.textDim }}>{tRate > 0 ? tRate.toFixed(3) + "%" : "—"}</td>
                              <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, color: r.c_purch > 0 ? COLORS.text : COLORS.textDim }}>{r.c_purch > 0 ? r.c_purch.toLocaleString() : "—"}</td>
                              <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, fontWeight: 600, color: cRate > 0 ? "#f59e0b" : COLORS.textDim }}>{cRate > 0 ? cRate.toFixed(3) + "%" : "—"}</td>
                            </tr>
                          );
                        })
                      ))}
                      {/* Totals row */}
                      <tr style={{ borderTop: `2px solid ${COLORS.cardBorder}`, background: "rgba(14,165,233,0.06)" }}>
                        <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: COLORS.accent }} colSpan={2}>TOTAL</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{totalT.toLocaleString()}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.accent }}>100%</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{totalC.toLocaleString()}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: "#f59e0b" }}>100%</td>
                        <td style={{ padding: "10px 12px" }}></td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{totalTPurch.toLocaleString()}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.accent }}>{totalT > 0 ? (totalTPurch / totalT * 100).toFixed(3) + "%" : "—"}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{totalCPurch.toLocaleString()}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: "#f59e0b" }}>{totalC > 0 ? (totalCPurch / totalC * 100).toFixed(3) + "%" : "—"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: "12px 16px", fontSize: 11, color: COLORS.textDim, borderTop: `1px solid ${COLORS.cardBorder}` }}>
                  Treated % and Control % show each group's share of total population · Red diff = control over-indexed (compositional bias) · Ctrl Buy Rate = natural purchase rate without marketing · CO = Current Owner, PO = Previous Owner, HR = Hand Raiser · D1-2 = highest propensity, D7-10 = lowest
                </div>
              </div>
            );
          })()
        ) : viewBy === "campaignSegment" && Object.keys(campSegData).length > 0 ? (
          /* ── CAMPAIGN × DECILE ── */
          (() => {
            const SEG_LABELS = { CO: "Current Owner", PO: "Previous Owner", HR: "Hand Raiser", OTH: "Other" };
            const SEG_ORDER = ["CO", "PO", "HR", "OTH"];
            const DECILE_ORDER = ["D1-2", "D3-4", "D1-6", "D7-10", "D-Other", "No-Decile"];

            const campGroups = [];
            Object.entries(campSegData).forEach(([code, segs]) => {
              const cl = codeClass[code] || {};
              if (salesOnly && !cl.isSales) return;
              if (campaignFilter.size > 0 && !campaignFilter.has(code)) return;
              if (typeFilter.size > 0 && !typeFilter.has(cl.type)) return;
              if (channelFilter.size > 0 && !channelFilter.has(cl.channel)) return;
              if (purposeFilter.size > 0 && !purposeFilter.has(cl.purpose)) return;
              const label = campLabel[code] ? `${code} — ${campLabel[code]}` : code;
              const ctrlSegs = campCtrlSegData[code] || {};
              const ctrlSegPurch = campCtrlSegPurchData[code] || {};
              const treatedSegPurch = campTreatedSegPurchData[code] || {};
              const campTotal_t = Object.values(segs).reduce((s, v) => s + v, 0);
              const campTotal_c = Object.values(ctrlSegs).reduce((s, v) => s + v, 0);
              const ctrl = ctrlData[code] || [0, 0, 0, 0];
              const t_sales = ctrl[1] || 0;
              const c_sales = ctrl[3] || 0;
              const rows = [];
              const allKeys = new Set([...Object.keys(segs), ...Object.keys(ctrlSegs)]);
              allKeys.forEach(key => {
                const [seg, decile] = key.split("|");
                rows.push({ seg, decile: decile || "—", t_pop: segs[key] || 0, c_pop: ctrlSegs[key] || 0, c_purch: ctrlSegPurch[key] || 0, t_purch: treatedSegPurch[key] || 0 });
              });
              rows.sort((a, b) => SEG_ORDER.indexOf(a.seg) - SEG_ORDER.indexOf(b.seg) || DECILE_ORDER.indexOf(a.decile) - DECILE_ORDER.indexOf(b.decile));
              campGroups.push({ code, label, type: cl.type || "—", campTotal_t, campTotal_c, t_sales, c_sales, rows });
            });
            campGroups.sort((a, b) => b.campTotal_t - a.campTotal_t);

            const totalT = campGroups.reduce((s, g) => s + g.campTotal_t, 0);
            const totalC = campGroups.reduce((s, g) => s + g.campTotal_c, 0);
            const totalTSales = campGroups.reduce((s, g) => s + g.t_sales, 0);
            const totalCSales = campGroups.reduce((s, g) => s + g.c_sales, 0);
            const hasControl = totalC > 0;

            return (
              <div>
                <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${COLORS.cardBorder}`, background: "rgba(14,165,233,0.04)" }}>
                  {[
                    { l: "Campaigns", v: campGroups.length.toLocaleString(), a: true },
                    { l: "Treated Pop", v: totalT.toLocaleString() },
                    { l: "Treated Sales", v: totalTSales.toLocaleString() },
                    ...(hasControl ? [{ l: "Control Pop", v: totalC.toLocaleString() }, { l: "Ctrl Sales", v: totalCSales.toLocaleString() }] : []),
                  ].map((m, i) => (
                    <div key={m.l} style={{ flex: 1, padding: "12px 16px", borderRight: `1px solid ${COLORS.cardBorder}`, textAlign: "center" }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: m.a ? COLORS.accent : COLORS.text }}>{m.v}</div>
                      <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>{m.l}</div>
                    </div>
                  ))}
                </div>
                <div style={{ maxHeight: 600, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ position: "sticky", top: 0, background: COLORS.card, zIndex: 2 }}>
                      <tr>
                        <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}`, width: 200, maxWidth: 200 }}>Campaign</th>
                        <th style={{ padding: "10px 8px", textAlign: "left", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Type</th>
                        <th style={{ padding: "10px 8px", textAlign: "left", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Segment</th>
                        <th style={{ padding: "10px 8px", textAlign: "left", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Decile</th>
                        <th style={{ padding: "10px 8px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Treated Pop</th>
                        <th style={{ padding: "10px 8px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Treated %</th>
                        <th style={{ padding: "10px 8px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Control Pop</th>
                        <th style={{ padding: "10px 8px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Control %</th>
                        <th style={{ padding: "10px 8px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>Diff (pp)</th>
                        <th style={{ padding: "10px 8px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>T.Sales</th>
                        <th style={{ padding: "10px 8px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>TBR</th>
                        <th style={{ padding: "10px 8px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>C.Sales</th>
                        <th style={{ padding: "10px 8px", textAlign: "right", fontSize: 11, fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.cardBorder}` }}>CBR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campGroups.map((g, gIdx) => {
                        const tbr = g.campTotal_t > 0 ? (g.t_sales / g.campTotal_t * 100) : 0;
                        const cbr = g.campTotal_c > 0 ? (g.c_sales / g.campTotal_c * 100) : 0;
                        return (
                          <Fragment key={g.code}>
                            {gIdx > 0 && (<tr><td colSpan={13} style={{ padding: "4px 0", border: "none" }}></td></tr>)}
                            {g.rows.map((r, rIdx) => {
                              const tPct = g.campTotal_t > 0 ? r.t_pop / g.campTotal_t * 100 : 0;
                              const cPct = g.campTotal_c > 0 ? r.c_pop / g.campTotal_c * 100 : 0;
                              const diff = tPct - cPct;
                              const diffColor = diff > 2 ? "#22c55e" : diff < -2 ? "#ef4444" : COLORS.textMuted;
                              const segColor = r.seg === "CO" ? "#f59e0b" : r.seg === "PO" ? "#8b5cf6" : r.seg === "HR" ? "#22c55e" : COLORS.textDim;
                              const isFirst = rIdx === 0;
                              const cRate = r.c_pop > 0 ? "—" : "—";
                              return (
                                <tr key={rIdx} style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                                  <td style={{ padding: "6px 12px", fontSize: 12, fontWeight: isFirst ? 700 : 400, color: isFirst ? COLORS.text : "transparent", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 200 }} title={g.label}>{isFirst ? (<>{g.label.length > 28 ? g.label.slice(0, 28) + "…" : g.label}{g.label.length > 28 && <span style={{ marginLeft: 4, cursor: "pointer", fontSize: 10, color: COLORS.textDim, border: `1px solid ${COLORS.textDim}`, borderRadius: "50%", padding: "0 4px", display: "inline-block", lineHeight: "14px" }} title={g.label}>i</span>}</>) : ""}</td>
                                  <td style={{ padding: "6px 8px", fontSize: 11, color: isFirst ? COLORS.textMuted : "transparent" }}>{isFirst ? g.type : g.type}</td>
                                  <td style={{ padding: "6px 8px", fontSize: 11, fontWeight: 600, color: segColor }}>{SEG_LABELS[r.seg] || r.seg}</td>
                                  <td style={{ padding: "6px 8px", fontSize: 11, color: COLORS.text }}>{r.decile}</td>
                                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 12, color: COLORS.textMuted }}>{r.t_pop.toLocaleString()}</td>
                                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 12, fontWeight: 600, color: COLORS.accent }}>{tPct.toFixed(1)}%</td>
                                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 12, color: r.c_pop > 0 ? COLORS.textMuted : COLORS.textDim }}>{r.c_pop > 0 ? r.c_pop.toLocaleString() : "—"}</td>
                                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 12, fontWeight: 600, color: "#f59e0b" }}>{r.c_pop > 0 ? cPct.toFixed(1) + "%" : "—"}</td>
                                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: r.c_pop > 0 ? diffColor : COLORS.textDim }}>{r.c_pop > 0 ? (diff >= 0 ? "+" : "") + diff.toFixed(1) + "pp" : "—"}</td>
                                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 12, color: r.t_purch > 0 ? COLORS.text : COLORS.textDim }}>{r.t_purch > 0 ? r.t_purch.toLocaleString() : "—"}</td>
                                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 12, fontWeight: 600, color: r.t_pop > 0 && r.t_purch > 0 ? COLORS.accent : COLORS.textDim }}>{r.t_pop > 0 && r.t_purch > 0 ? (r.t_purch / r.t_pop * 100).toFixed(3) + "%" : "—"}</td>
                                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 12, color: r.c_purch > 0 ? COLORS.text : COLORS.textDim }}>{r.c_purch > 0 ? r.c_purch.toLocaleString() : "—"}</td>
                                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 12, fontWeight: 600, color: r.c_pop > 0 && r.c_purch > 0 ? "#f59e0b" : COLORS.textDim }}>{r.c_pop > 0 && r.c_purch > 0 ? (r.c_purch / r.c_pop * 100).toFixed(3) + "%" : "—"}</td>
                                </tr>
                              );
                            })}
                            {/* Campaign subtotal */}
                            <tr style={{ borderBottom: `2px solid ${COLORS.cardBorder}`, background: "rgba(14,165,233,0.04)" }}>
                              <td colSpan={2} style={{ padding: "7px 12px" }}></td>
                              <td colSpan={2} style={{ padding: "7px 8px", fontSize: 11, fontWeight: 700, color: COLORS.accent }}>Subtotal</td>
                              <td style={{ padding: "7px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{g.campTotal_t.toLocaleString()}</td>
                              <td style={{ padding: "7px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.accent }}>100%</td>
                              <td style={{ padding: "7px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{g.campTotal_c > 0 ? g.campTotal_c.toLocaleString() : "—"}</td>
                              <td style={{ padding: "7px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: "#f59e0b" }}>{g.campTotal_c > 0 ? "100%" : "—"}</td>
                              <td style={{ padding: "7px 8px" }}></td>
                              <td style={{ padding: "7px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{g.t_sales > 0 ? g.t_sales.toLocaleString() : "—"}</td>
                              <td style={{ padding: "7px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: tbr > 0 ? COLORS.accent : COLORS.textDim }}>{tbr > 0 ? tbr.toFixed(3) + "%" : "—"}</td>
                              <td style={{ padding: "7px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{g.c_sales > 0 ? g.c_sales.toLocaleString() : "—"}</td>
                              <td style={{ padding: "7px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: cbr > 0 ? "#f59e0b" : COLORS.textDim }}>{cbr > 0 ? cbr.toFixed(3) + "%" : "—"}</td>
                            </tr>
                          </Fragment>
                        );
                      })}
                      {/* Grand total */}
                      <tr style={{ borderTop: `2px solid ${COLORS.cardBorder}`, background: "rgba(14,165,233,0.06)" }}>
                        <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: COLORS.accent }} colSpan={4}>TOTAL ({campGroups.length} campaigns)</td>
                        <td style={{ padding: "10px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{totalT.toLocaleString()}</td>
                        <td style={{ padding: "10px 8px" }}></td>
                        <td style={{ padding: "10px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{totalC.toLocaleString()}</td>
                        <td style={{ padding: "10px 8px" }}></td>
                        <td style={{ padding: "10px 8px" }}></td>
                        <td style={{ padding: "10px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{totalTSales.toLocaleString()}</td>
                        <td style={{ padding: "10px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.accent }}>{totalT > 0 ? (totalTSales / totalT * 100).toFixed(3) + "%" : "—"}</td>
                        <td style={{ padding: "10px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: COLORS.text }}>{totalCSales.toLocaleString()}</td>
                        <td style={{ padding: "10px 8px", textAlign: "right", fontSize: 12, fontWeight: 700, color: "#f59e0b" }}>{totalC > 0 ? (totalCSales / totalC * 100).toFixed(3) + "%" : "—"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: "12px 16px", fontSize: 11, color: COLORS.textDim, borderTop: `1px solid ${COLORS.cardBorder}` }}>
                  Campaign name + type on first row · Indented segment × decile rows below · Subtotal with ctrl purchases and buy rate · Same columns as Segment × Decile view · CO = Current Owner, PO = Previous Owner, HR = Hand Raiser
                </div>
              </div>
            );
          })()
        ) : (
        /* ── REGULAR TABLE ── */
        <>
        <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${COLORS.cardBorder}`, background: "rgba(14,165,233,0.04)" }}>
          {[{ l: "Unique VINs", v: totals.vins, a: true }, { l: "Unique HH", v: totals.hh }, { l: "Unique Groups", v: totals.grp }, { l: "Fractional", v: fmtFrac(totalFrac) }, { l: "Matches", v: totals.raw }].map((m, i) => (
            <div key={m.l} style={{ flex: 1, padding: "12px 16px", borderRight: i < 4 ? `1px solid ${COLORS.cardBorder}` : "none", textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: m.a ? COLORS.accent : COLORS.text }}>{typeof m.v === "number" ? m.v.toLocaleString() : m.v}</div>
              <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>{m.l}</div>
            </div>
          ))}
        </div>
        <div style={{ maxHeight: 500, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, background: COLORS.card, zIndex: 2 }}>
              <tr>
                <SortTh col="key" align="left">{viewLabel[viewBy] || viewBy}</SortTh>
                {viewBy === "campaign" && <SortTh col="sentDate">Sent</SortTh>}
                {viewBy === "campaign" && <SortTh col="channel">Channel</SortTh>}
                <SortTh col="vins">Unique VINs</SortTh><SortTh col="hh">Unique HH</SortTh><SortTh col="grp">Unique Groups</SortTh><SortTh col="frac">Fractional</SortTh><SortTh col="raw">Matches</SortTh>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => {
                const kids = hasExpandable ? (childRows[r.key] || []) : [];
                const hasKids = kids.length > 0;
                const isOpen = expanded.has(r.key);
                return (
                  <Fragment key={r.key || i}>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }} onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "10px 12px", fontSize: 12, color: COLORS.text }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {hasExpandable && hasKids && <span onClick={() => toggleExpand(r.key)} style={{ cursor: "pointer", fontSize: 10, color: COLORS.textDim, width: 16, textAlign: "center", flexShrink: 0 }}>{isOpen ? "▼" : "▶"}</span>}
                          {hasExpandable && !hasKids && <span style={{ width: 16, flexShrink: 0 }} />}
                          <div style={{ fontWeight: 600, maxWidth: 450, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: hasKids ? "pointer" : "default" }} onClick={hasKids ? () => toggleExpand(r.key) : undefined}>
                            {viewBy === "campaign" ? (campLabel[r.key] ? `${r.key} — ${campLabel[r.key]}` : r.key) : r.key}
                          </div>
                          {viewBy === "campaign" && campTypeBadge(r.key)}
                        </div>
                      </td>
                      {viewBy === "campaign" && (() => {
                        const sd = campSentDates[r.key];
                        const dateCount = sd ? sd.dates.length : 0;
                        const isExp = expandedDates === r.key;
                        return (
                          <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: COLORS.textDim, whiteSpace: "nowrap", position: "relative" }}>
                            {r.sentDate || ""}
                            {dateCount > 1 && (
                              <span onClick={() => setExpandedDates(isExp ? null : r.key)}
                                style={{ cursor: "pointer", marginLeft: 4, fontSize: 10, color: isExp ? COLORS.accent : COLORS.textDim, opacity: isExp ? 1 : 0.6 }}
                                title={`${dateCount} sent dates`}>📅</span>
                            )}
                            {isExp && sd && (
                              <div style={{ position: "absolute", right: 12, top: "100%", background: "#1e293b", border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6, padding: "6px 10px", zIndex: 10, fontSize: 11, color: COLORS.text, whiteSpace: "nowrap", boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}>
                                {sd.dates.map(d => <div key={d} style={{ padding: "2px 0" }}>{d}</div>)}
                              </div>
                            )}
                          </td>
                        );
                      })()}
                      {viewBy === "campaign" && <td style={{ padding: "10px 12px", textAlign: "center", fontSize: 10, color: r.channel === "SMS" ? "#22c55e" : r.channel === "DM" ? "#f59e0b" : COLORS.textDim, fontWeight: 600 }}>{r.channel || "Email"}</td>}
                      <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 13, color: COLORS.accent, fontWeight: 700 }}>{r.vins.toLocaleString()}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, color: COLORS.text }}>{r.hh.toLocaleString()}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, color: COLORS.textMuted }}>{r.grp.toLocaleString()}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, color: COLORS.warning }}>{fmtFrac(r.frac)}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, color: COLORS.textDim }}>{r.raw.toLocaleString()}</td>
                    </tr>
                    {isOpen && kids.map(t => (
                      <tr key={`${r.key}-${t.key}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.02)", background: "rgba(14,165,233,0.02)" }}>
                        <td style={{ padding: "6px 12px 6px 44px", fontSize: 11, color: COLORS.textDim }}>
                          {childLabelFn(t.key)}
                          {(viewBy === "campaignType" || viewBy === "campaignGroup") && campTypeBadge(t.key)}
                        </td>
                        <td style={{ padding: "6px 12px", textAlign: "right", fontSize: 11, color: COLORS.accent }}>{t.vins.toLocaleString()}</td>
                        <td style={{ padding: "6px 12px", textAlign: "right", fontSize: 11, color: COLORS.textMuted }}>{t.hh.toLocaleString()}</td>
                        <td style={{ padding: "6px 12px", textAlign: "right", fontSize: 11, color: COLORS.textDim }}>{t.grp.toLocaleString()}</td>
                        <td style={{ padding: "6px 12px", textAlign: "right", fontSize: 11, color: COLORS.warning }}>{fmtFrac(t.frac)}</td>
                        <td style={{ padding: "6px 12px", textAlign: "right", fontSize: 11, color: COLORS.textDim }}>{t.raw.toLocaleString()}</td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>

      {!isFiltered && data.multiTouch && data.multiTouch.length > 0 && (
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 10, padding: 20, marginTop: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 14 }}>Multi-Touch Distribution</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {data.multiTouch.slice(0, 10).map(t => (<div key={t[0]} style={{ padding: "10px 16px", background: "rgba(255,255,255,0.03)", borderRadius: 8, border: `1px solid ${COLORS.cardBorder}`, textAlign: "center" }}><div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{t[1].toLocaleString()}</div><div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>{t[0]} {t[0] === 1 ? "campaign" : "campaigns"}</div></div>))}
          </div>
        </div>
      )}

      {!isFiltered && data.byMonth && data.byMonth.length > 0 && (
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 10, padding: 20, marginTop: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 14 }}>Monthly Trend — Unique VINs</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.byMonth.map(m => ({ month: m[0], vins: m[1] }))}>
              <XAxis dataKey="month" tick={{ fill: COLORS.textDim, fontSize: 10 }} />
              <YAxis tick={{ fill: COLORS.textDim, fontSize: 10 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Tooltip contentStyle={{ background: "#1e293b", border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6, fontSize: 12 }} labelStyle={{ color: COLORS.text }} formatter={(v) => [v.toLocaleString(), "Unique VINs"]} />
              <Bar dataKey="vins" fill={COLORS.accent} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}


function FirstPartyINNsightInner({ user, logout }) {
  // ═══ STATE ═══
  const [activeTab, setActiveTab] = useState("campaigns");
  const [screenshotStatus, setScreenshotStatus] = useState("");
  const [showAbout, setShowAbout] = useState(false);
  const [data, setData] = useState(null);
  const [audienceData, setAudienceData] = useState(null);
  const [smData, setSmData] = useState(null);
  const [sm25Data, setSm25Data] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Brand filter (ALL, HMA, GMA)
  const [brandFilter, setBrandFilter] = useState("ALL");
  
  // Year filter (ALL or a year like "2024", "2025", "2026")
  const [yearFilter, setYearFilter] = useState("ALL");
  
  // Campaign tab state
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [attrWindow, setAttrWindow] = useState('90d');
  const [sortConfig, setSortConfig] = useState({ col: 'lastSendDate', dir: -1 });
  
  // Journey tab state
  const [selectedJourney, setSelectedJourney] = useState(null);
  
  // Responsive
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ═══ DATA LOADING ═══
  useEffect(() => {
    // Derive brand from groupCampaign/campaignName — GMA campaigns have "GMA" or "Genesis" in the name
    const deriveBrand = (campaigns) => {
      if (!campaigns) return campaigns;
      return campaigns.map(c => {
        if (c.brand) return c; // already set
        const gc = (c.groupCampaign || "").toUpperCase();
        const cn = (c.campaignName || "").toUpperCase();
        const isGMA = gc.includes("GMA") || gc.includes("GENESIS") || gc.includes("GENHOUSE")
                   || cn.includes("GMA") || cn.includes("GENESIS") || cn.includes("GENHOUSE");
        return { ...c, brand: isGMA ? "GMA" : "HMA" };
      });
    };

    const loadData = async () => {
      try {
        // Load campaign data
        const campaignPaths = ["./first_party_dashboard.json", "/first_party_dashboard.json", "./sfmc_first_party.json"];
        let campaignLoaded = false;
        const cacheBust = `?v=${Date.now()}`;  // Force bypass browser disk cache after deploys
        
        for (const path of campaignPaths) {
          try {
            const res = await fetch(path + cacheBust);
            if (res.ok) {
              const json = await res.json();
              if (json.campaigns) json.campaigns = deriveBrand(json.campaigns);
              // Derive brand for links from campaign lookup
              if (json.links && json.campaigns) {
                const brandByCampaign = {};
                json.campaigns.forEach(c => { if (c.campaignCode) brandByCampaign[c.campaignCode] = c.brand; });
                json.links = json.links.map(l => l.brand ? l : { ...l, brand: brandByCampaign[l.campaignCode] || "HMA" });
              }
              setData(json);
              campaignLoaded = true;
              break;
            }
          } catch (e) {
            continue;
          }
        }
        
        if (!campaignLoaded) {
          const sample = getSampleData();
          if (sample.campaigns) sample.campaigns = deriveBrand(sample.campaigns);
          if (sample.links && sample.campaigns) {
            const brandByCampaign = {};
            sample.campaigns.forEach(c => { if (c.campaignCode) brandByCampaign[c.campaignCode] = c.brand; });
            sample.links = sample.links.map(l => l.brand ? l : { ...l, brand: brandByCampaign[l.campaignCode] || "HMA" });
          }
          setData(sample);
        }
        
        // Load audience profile data
        const audiencePaths = ["./audience_profile.json", "/audience_profile.json"];
        for (const path of audiencePaths) {
          try {
            const res = await fetch(path + cacheBust);
            if (res.ok) {
              const json = await res.json();
              setAudienceData(json);
              break;
            }
          } catch (e) {
            continue;
          }
        }
        
        // Use sample audience data if not loaded
        if (!audienceData) {
          setAudienceData(getSampleAudienceData());
        }

        // Load salesmatch QA data (separate JSON)
        const smPaths = ["./salesmatch_qa.json", "/salesmatch_qa.json"];
        for (const path of smPaths) {
          try {
            const res = await fetch(path + cacheBust);
            if (res.ok) {
              const json = await res.json();
              setSmData(json);
              break;
            }
          } catch (e) {
            continue;
          }
        }

        // Load salesmatch 2025 corrected data (separate JSON)
        const sm25Paths = ["./salesmatch_2025.json", "/salesmatch_2025.json"];
        for (const path of sm25Paths) {
          try {
            const res = await fetch(path + cacheBust);
            if (res.ok) {
              const json = await res.json();
              setSm25Data(json);
              break;
            }
          } catch (e) {
            continue;
          }
        }
        
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // ═══ URL PARAMS ═══
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab && ["campaigns", "journeys", "audience", "deliverability", "links", "salesmatchQA", "salesmatch2025"].includes(tab)) {
      setActiveTab(tab);
    }
    const year = params.get("year");
    if (year && /^\d{4}$/.test(year)) {
      setYearFilter(year);
    } else if (year === "ALL") {
      setYearFilter("ALL");
    }
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", activeTab);
    if (yearFilter && yearFilter !== "ALL") {
      url.searchParams.set("year", yearFilter);
    } else {
      url.searchParams.delete("year");
    }
    window.history.replaceState({}, "", url.toString());
  }, [activeTab, yearFilter]);

  // Screenshot — capture dashboard content
  const captureCanvas = useCallback(async () => {
    if (!window.html2canvas) {
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }
    const el = document.getElementById("dashboard-content");
    if (!el) return null;
    return await window.html2canvas(el, { backgroundColor: "#0f172a", scale: 2, useCORS: true });
  }, []);

  const copyScreenshot = useCallback(async () => {
    setScreenshotStatus("📸 Copying...");
    try {
      const canvas = await captureCanvas();
      if (!canvas) { setScreenshotStatus("⚠ Error"); return; }
      const blob = await new Promise(r => canvas.toBlob(r, "image/png"));
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setScreenshotStatus("✓ Copied!");
    } catch (e) {
      console.error("Copy failed:", e);
      setScreenshotStatus("⚠ Failed");
    }
    setTimeout(() => setScreenshotStatus(""), 2500);
  }, [captureCanvas]);

  const saveScreenshot = useCallback(async () => {
    setScreenshotStatus("📸 Saving...");
    try {
      const canvas = await captureCanvas();
      if (!canvas) { setScreenshotStatus("⚠ Error"); return; }
      const link = document.createElement("a");
      link.download = `firstparty_${activeTab}_${new Date().toISOString().slice(0,10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      setScreenshotStatus("✓ Saved!");
    } catch (e) {
      console.error("Save failed:", e);
      setScreenshotStatus("⚠ Failed");
    }
    setTimeout(() => setScreenshotStatus(""), 2500);
  }, [captureCanvas, activeTab]);

  // Helper: extract the year a campaign belongs to. Prefers lastSendDate,
  // falls back to firstSendDate, then to an explicit `year` field.
  const getCampaignYear = (c) => {
    const iso = c?.lastSendDate || c?.firstSendDate;
    if (typeof iso === 'string' && iso.length >= 4) {
      const y = iso.slice(0, 4);
      if (/^\d{4}$/.test(y)) return y;
    }
    if (c?.year) return String(c.year);
    return null;
  };

  // ═══ COMPUTED: Available Years ═══
  const availableYears = useMemo(() => {
    if (!data?.campaigns) return [];
    const years = new Set();
    data.campaigns.forEach(c => {
      const y = getCampaignYear(c);
      if (y) years.add(y);
    });
    return Array.from(years).sort((a, b) => b.localeCompare(a)); // most recent first
  }, [data]);

  // ═══ COMPUTED: Filtered & Sorted Campaigns ═══
  const sortedCampaigns = useMemo(() => {
    if (!data?.campaigns) return [];
    // Apply brand filter
    let list = [...data.campaigns];
    if (brandFilter !== "ALL") {
      list = list.filter(c => c.brand === brandFilter);
    }
    // Apply year filter
    if (yearFilter !== "ALL") {
      list = list.filter(c => getCampaignYear(c) === yearFilter);
    }
    // Sort
    const { col, dir } = sortConfig;
    return list.sort((a, b) => {
      let va = a[col], vb = b[col];
      if (col === 'sales') {
        va = attrWindow === '30d' ? a.sales30d : attrWindow === '60d' ? a.sales60d : a.sales90d;
        vb = attrWindow === '30d' ? b.sales30d : attrWindow === '60d' ? b.sales60d : b.sales90d;
      }
      if (typeof va === 'string') return dir * va.localeCompare(vb);
      return dir * ((va || 0) - (vb || 0));
    });
  }, [data, sortConfig, attrWindow, brandFilter, yearFilter]);

  // ═══ COMPUTED: Journey Groups ═══
  const journeys = useMemo(() => {
    if (!data?.campaigns) return [];
    const groups = {};
    
    // Filter by brand first
    let campaigns = brandFilter === "ALL" 
      ? data.campaigns 
      : data.campaigns.filter(c => c.brand === brandFilter);
    // Apply year filter
    if (yearFilter !== "ALL") {
      campaigns = campaigns.filter(c => getCampaignYear(c) === yearFilter);
    }
    
    campaigns.forEach(c => {
      const groupName = c.groupCampaign || c.journeyName || 'Ungrouped';
      if (!groups[groupName]) {
        groups[groupName] = { name: groupName, campaigns: [], audiences: {} };
      }
      groups[groupName].campaigns.push(c);
      
      // Parse audience (CO/HR/PO)
      const audMatch = c.campaignName?.match(/_(CO|HR|PO)_/);
      const audience = audMatch ? audMatch[1] : 'Other';
      
      // Parse wave
      const waveMatch = c.campaignName?.match(/_EM(\d)/);
      const wave = waveMatch ? `EM${waveMatch[1]}` : 'EM1';
      
      if (!groups[groupName].audiences[audience]) {
        groups[groupName].audiences[audience] = { EM1: null, EM2: null, EM3: null };
      }
      
      const existing = groups[groupName].audiences[audience][wave];
      if (!existing || c.sent > existing.sent) {
        groups[groupName].audiences[audience][wave] = c;
      }
    });
    
    return Object.values(groups).map(g => {
      const totalSent = g.campaigns.reduce((s, c) => s + (c.sent || 0), 0);
      const totalSales = g.campaigns.reduce((s, c) => {
        const sales = attrWindow === '30d' ? c.sales30d : attrWindow === '60d' ? c.sales60d : c.sales90d;
        return s + (sales || 0);
      }, 0);
      const avgOpenRate = g.campaigns.length > 0 
        ? g.campaigns.reduce((s, c) => s + (c.openRate || 0), 0) / g.campaigns.length 
        : 0;
      
      // Data quality checks
      let hasDataIssue = false;
      let dataIssueDetails = [];
      Object.entries(g.audiences).forEach(([aud, waves]) => {
        if (waves.EM1 && waves.EM2 && waves.EM2.sent > waves.EM1.sent) {
          hasDataIssue = true;
          dataIssueDetails.push(`${aud}: EM2 (${waves.EM2.sent.toLocaleString()}) > EM1 (${waves.EM1.sent.toLocaleString()})`);
        }
        if (waves.EM2 && waves.EM3 && waves.EM3.sent > waves.EM2.sent) {
          hasDataIssue = true;
          dataIssueDetails.push(`${aud}: EM3 (${waves.EM3.sent.toLocaleString()}) > EM2 (${waves.EM2.sent.toLocaleString()})`);
        }
      });
      
      return {
        ...g,
        totalSent,
        totalSales,
        avgOpenRate,
        campaignCount: g.campaigns.length,
        hasDataIssue,
        dataIssueDetails,
        firstSend: g.campaigns.reduce((min, c) => !min || c.firstSendDate < min ? c.firstSendDate : min, null),
        lastSend: g.campaigns.reduce((max, c) => !max || c.lastSendDate > max ? c.lastSendDate : max, null),
      };
    }).sort((a, b) => (b.lastSend || '').localeCompare(a.lastSend || ''));
  }, [data, attrWindow, brandFilter, yearFilter]);

  // ═══ COMPUTED: Filtered Summary (recomputed when year or brand filter active) ═══
  const filteredSummary = useMemo(() => {
    if (!data?.campaigns) return null;
    // When both filters are ALL, return the pre-computed summary as-is for accuracy
    if (brandFilter === "ALL" && yearFilter === "ALL" && data.summary) {
      return data.summary;
    }
    // Recompute from sortedCampaigns (already filtered by brand + year)
    const list = sortedCampaigns;
    const totalCampaigns = list.length;
    const totalSent = list.reduce((s, c) => s + (c.sent || 0), 0);
    const totalOpened = list.reduce((s, c) => s + (c.opened || 0), 0);
    const totalClicked = list.reduce((s, c) => s + (c.clicked || 0), 0);
    const totalDelivered = list.reduce((s, c) => s + (c.delivered || 0), 0);
    const avgOpenRate = totalDelivered > 0 ? (totalOpened / totalDelivered * 100) : 0;
    const avgClickRate = totalDelivered > 0 ? (totalClicked / totalDelivered * 100) : 0;
    const totalSales30d = list.reduce((s, c) => s + (c.sales30d || 0), 0);
    const totalSales60d = list.reduce((s, c) => s + (c.sales60d || 0), 0);
    const totalSales90d = list.reduce((s, c) => s + (c.sales90d || 0), 0);
    return {
      totalCampaigns, totalSent, avgOpenRate, avgClickRate,
      totalSales30d, totalSales60d, totalSales90d,
    };
  }, [data, sortedCampaigns, brandFilter, yearFilter]);

  // ═══ COMPUTED: Filtered Links (for Link Analysis tab) ═══
  // When a year filter is active, limit links to those whose campaignCode
  // belongs to a campaign in the filtered campaign list.
  const filteredLinks = useMemo(() => {
    const rawLinks = data?.links || [];
    if (yearFilter === "ALL") return rawLinks;
    const allowedCodes = new Set(sortedCampaigns.map(c => c.campaignCode).filter(Boolean));
    return rawLinks.filter(l => l.campaignCode && allowedCodes.has(l.campaignCode));
  }, [data, sortedCampaigns, yearFilter]);

  // ═══ TAB CONFIG ═══
  const tabs = [
    { id: "campaigns", label: "Campaigns", icon: "📧" },
    { id: "journeys", label: "Journeys", icon: "🔀" },
    { id: "wavePerformance", label: "Wave Performance", icon: "📊" },
    { id: "audience", label: "Audience", icon: "👥" },
    { id: "deliverability", label: "Deliverability", icon: "📬" },
    { id: "links", label: "Link Analysis", icon: "🔗" },
    { id: "salesmatchQA", label: "Salesmatch QA", icon: "🔍" },
    { id: "salesmatch2025", label: "Salesmatch 2025", icon: "📊" },
  ];

  // ═══ LOADING STATE ═══
  if (loading) {
    return (
      <div style={{ minHeight:"100vh", background:COLORS.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={{ textAlign:"center", color:COLORS.textMuted }}>
          <div style={{ fontSize:48, marginBottom:16 }}>📊</div>
          <div style={{ fontSize:14 }}>Loading FirstParty data...</div>
        </div>
      </div>
    );
  }

  // ═══ RENDER ═══
  return (
    <div style={{ 
      minHeight: "100vh", 
      background: `linear-gradient(135deg, ${COLORS.bg} 0%, #0f172a 50%, #0c1929 100%)`,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      color: COLORS.text
    }}>
      {/* ═══ HEADER ═══ */}
      <header style={{ 
        padding: mobile ? "16px" : "20px 32px", 
        borderBottom: `1px solid ${COLORS.cardBorder}`,
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        position: "sticky",
        top: 0,
        zIndex: 100
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          {/* Logo + Title */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src={DATA_CRAFT_LOGO} alt="Data+Craft" style={{ width: mobile ? 32 : 40, height: mobile ? 32 : 40 }} />
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: mobile ? 18 : 22, fontWeight: 700, color: "#f1f5f9", letterSpacing: "-0.02em" }}>
                  FirstParty INNsight
                </div>
              </div>
              <div style={{ fontSize: mobile ? 11 : 13, color: "#94a3b8", marginTop: 2 }}>
                HMA + GMA Direct Email Analytics{mobile ? "" : " · Innocean Data+Craft"}
              </div>
            </div>
          </div>
          
          {/* Brand Filter + Attribution Window */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            {/* Brand Filter with Logo Buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginRight: 2 }}>Brand</span>
              {/* All Pill */}
              <button onClick={() => setBrandFilter("ALL")}
                style={{ 
                  padding: mobile ? "6px 12px" : "6px 14px", 
                  border: brandFilter === "ALL" ? `2px solid ${bp(brandFilter).primary}` : "2px solid #475569", 
                  borderRadius: 20,
                  fontSize: mobile ? 12 : 13, 
                  fontWeight: brandFilter === "ALL" ? 700 : 400, 
                  cursor: "pointer", 
                  transition: "all 0.15s",
                  background: brandFilter === "ALL" ? bp("ALL").primary : "transparent", 
                  color: brandFilter === "ALL" ? "#f1f5f9" : "#cbd5e1",
                  minHeight: mobile ? 36 : 32
                }}>
                All
              </button>
              {/* HMA Logo Button */}
              <button onClick={() => setBrandFilter("HMA")}
                style={{ 
                  padding: "0", 
                  width: mobile ? 70 : 80, 
                  border: brandFilter === "HMA" ? `2px solid ${BRAND_PALETTE.HMA.primary}` : "2px solid #475569", 
                  borderRadius: 20,
                  cursor: "pointer", 
                  transition: "all 0.15s",
                  background: brandFilter === "HMA" ? BRAND_PALETTE.HMA.primary : "transparent",
                  minHeight: mobile ? 36 : 32, 
                  display: "inline-flex", 
                  alignItems: "center", 
                  justifyContent: "center" 
                }}>
                <HyundaiLogo height={18} fill={brandFilter === "HMA" ? "#f1f5f9" : "#94a3b8"} />
              </button>
              {/* GMA Logo Button */}
              <button onClick={() => setBrandFilter("GMA")}
                style={{ 
                  padding: "0 8px", 
                  width: mobile ? 70 : 80, 
                  border: brandFilter === "GMA" ? `2px solid ${BRAND_PALETTE.GMA.primary}` : "2px solid #475569", 
                  borderRadius: 20,
                  cursor: "pointer", 
                  transition: "all 0.15s",
                  background: brandFilter === "GMA" ? BRAND_PALETTE.GMA.primary : "transparent",
                  minHeight: mobile ? 36 : 32, 
                  display: "inline-flex", 
                  alignItems: "center", 
                  justifyContent: "center" 
                }}>
                <GenesisLogo height={16} fill={brandFilter === "GMA" ? "#1e293b" : "#AAA8A6"} maxWidth={60} />
              </button>
            </div>
            
            {/* Year Filter */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Year</span>
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                style={{
                  padding: mobile ? "6px 10px" : "6px 12px",
                  fontSize: mobile ? 12 : 13,
                  fontWeight: yearFilter === "ALL" ? 400 : 700,
                  border: yearFilter === "ALL" ? "2px solid #475569" : `2px solid ${bp(brandFilter).primary}`,
                  borderRadius: 6,
                  background: yearFilter === "ALL" ? "transparent" : bp(brandFilter).primary,
                  color: yearFilter === "ALL" ? "#cbd5e1" : "#f1f5f9",
                  cursor: "pointer",
                  minHeight: mobile ? 36 : 32,
                  outline: "none",
                  transition: "all 0.15s",
                }}
              >
                <option value="ALL" style={{ background: COLORS.card, color: COLORS.text }}>All Years</option>
                {availableYears.map(y => (
                  <option key={y} value={y} style={{ background: COLORS.card, color: COLORS.text }}>{y}</option>
                ))}
              </select>
            </div>
            
            {/* Attribution Window Toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Attribution</span>
              <div style={{ display: "flex", gap: 2, background: COLORS.card, padding: 2, borderRadius: 6 }}>
                {['30d', '60d', '90d'].map(w => (
                  <button key={w} onClick={() => setAttrWindow(w)}
                    style={{ 
                      padding: "6px 12px", 
                      fontSize: 11, 
                      fontWeight: 600, 
                      border: "none", 
                      borderRadius: 4, 
                      cursor: "pointer",
                      background: attrWindow === w ? bp(brandFilter).primary : "transparent",
                      color: attrWindow === w ? "#fff" : COLORS.textMuted,
                      transition: "all 0.2s"
                    }}>
                    {w.replace('d', 'D')}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Screenshot (Copy + Save) + About + User Badge */}
            <button onClick={copyScreenshot} title="Copy screenshot to clipboard for Teams"
              style={{ padding: "6px 10px", background: "#334155", border: "none", borderRadius: 6, fontSize: 12, color: "#94a3b8", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#475569"; e.currentTarget.style.color = "#f1f5f9"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#334155"; e.currentTarget.style.color = "#94a3b8"; }}>
              &#128203; {screenshotStatus === "✓ Copied!" ? screenshotStatus : "Copy"}
            </button>
            <button onClick={saveScreenshot} title="Save screenshot as PNG file"
              style={{ padding: "6px 10px", background: "#334155", border: "none", borderRadius: 6, fontSize: 12, color: "#94a3b8", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#475569"; e.currentTarget.style.color = "#f1f5f9"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#334155"; e.currentTarget.style.color = "#94a3b8"; }}>
              &#128190; {screenshotStatus === "✓ Saved!" ? screenshotStatus : "Save"}
            </button>
            <button onClick={() => setShowAbout(v => !v)} title="About this dashboard"
              style={{ padding: "6px 10px", background: showAbout ? "#475569" : "#334155", border: "none", borderRadius: 6, fontSize: 12, color: showAbout ? "#f1f5f9" : "#94a3b8", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#475569"; e.currentTarget.style.color = "#f1f5f9"; }}
              onMouseLeave={e => { if (!showAbout) { e.currentTarget.style.background = "#334155"; e.currentTarget.style.color = "#94a3b8"; } }}>
              &#9432; About
            </button>
            {user && <UserBadge user={user} logout={logout} />}
          </div>
        </div>

        {/* Tab Navigation */}
        <nav style={{ 
          display: "flex", 
          gap: 4, 
          marginTop: 16,
          overflowX: "auto",
          paddingBottom: 4
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              disabled={tab.disabled}
              style={{
                padding: "10px 16px",
                fontSize: 12,
                fontWeight: 600,
                border: "none",
                borderRadius: 6,
                cursor: tab.disabled ? "not-allowed" : "pointer",
                background: activeTab === tab.id ? COLORS.accent : "transparent",
                color: activeTab === tab.id ? "#fff" : tab.disabled ? COLORS.textDim : COLORS.textMuted,
                opacity: tab.disabled ? 0.5 : 1,
                transition: "all 0.2s",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 6
              }}>
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              {tab.disabled && <span style={{ fontSize: 9, opacity: 0.7 }}>soon</span>}
            </button>
          ))}
        </nav>
      </header>

      {/* ═══ MAIN CONTENT ═══ */}
      <div id="dashboard-content">
      {/* About panel — collapsible */}
      {showAbout && (
        <div style={{ margin: mobile ? 16 : "16px 32px", background: "#1e293b", border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: mobile ? 16 : 24, position: "relative" }}>
          <button onClick={() => setShowAbout(false)}
            style={{ position: "absolute", top: 12, right: 12, background: "none", border: "none", fontSize: 18, color: "#94a3b8", cursor: "pointer", padding: 4 }}>✕</button>

          <div style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9", marginBottom: 12 }}>About FirstParty INNsight</div>

          <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: mobile ? 16 : 24, fontSize: 13, color: "#94a3b8", lineHeight: 1.6 }}>
            <div>
              <div style={{ fontWeight: 700, color: "#f1f5f9", marginBottom: 4, fontSize: 13 }}>What is this?</div>
              <p style={{ margin: "0 0 12px" }}>
                A first-party CRM analytics dashboard for HMA and GMA direct email, SMS, and DM campaign performance. Combines SFMC send/engagement data with household-level sales matchback attribution to show which campaigns drive vehicle purchases.
              </p>

              <div style={{ fontWeight: 700, color: "#f1f5f9", marginBottom: 4, fontSize: 13 }}>Where does the data come from?</div>
              <p style={{ margin: "0 0 6px" }}>
                <span style={{ fontWeight: 600, color: "#cbd5e1" }}>Campaign data</span> — Salesforce Marketing Cloud (SFMC) EmailSendLog, SMSSendLog, and DMSendLog, joined to ContactTargetMaster and AssetTargetMaster for sales attribution.
              </p>
              <p style={{ margin: "0 0 6px" }}>
                <span style={{ fontWeight: 600, color: "#cbd5e1" }}>Campaign classification</span> — Heroku sfmc_first_party_campaigns table provides campaign grouping, purpose (Sales/Non-sales), and type (MSP, Launch, Alt-Fuel Vehicle, etc.).
              </p>
              <p style={{ margin: "0 0 6px" }}>
                <span style={{ fontWeight: 600, color: "#cbd5e1" }}>Sales matchback</span> — VIN-level RDR purchase data matched to campaign recipients within a 180-day attribution window. Fractional attribution splits credit equally across campaigns per VIN.
              </p>
              <p style={{ margin: 0 }}>
                <span style={{ fontWeight: 600, color: "#cbd5e1" }}>Processing pipeline</span> — SFMC Automation Studio exports CSV to SFTP, Databricks ingests and enriches the data, generates JSON, and pushes to S3/CloudFront.
              </p>
            </div>

            <div>
              <div style={{ fontWeight: 700, color: "#f1f5f9", marginBottom: 4, fontSize: 13 }}>Current Status</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  { status: "done", text: "Email campaign matchback (196+ campaigns, 165K+ VINs)" },
                  { status: "done", text: "SMS campaign matchback via SMSSendLog" },
                  { status: "done", text: "DM campaign matchback via DMSendLog" },
                  { status: "done", text: "IsSales flag and Channel classification (Email/SMS/DM)" },
                  { status: "done", text: "Fractional attribution with stable denominators across date filters" },
                  { status: "done", text: "Per-campaign sent date filtering" },
                  { status: "done", text: "Excel export with row grouping and styling" },
                  { status: "done", text: "Treated vs Control buy rate analysis with incremental lift" },
                  { status: "wip", text: "Incremental buy rate by powertrain (alt-fuel)" },
                  { status: "planned", text: "GMA replication" },
                  { status: "planned", text: "Automated recurring pipeline schedule" },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12 }}>
                    <span style={{ flexShrink: 0, marginTop: 1, width: 18, height: 18, borderRadius: 9, fontSize: 10, fontWeight: 700,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: item.status === "done" ? "rgba(34,197,94,0.15)" : item.status === "wip" ? "rgba(234,179,8,0.15)" : "rgba(148,163,184,0.1)",
                      color: item.status === "done" ? "#22c55e" : item.status === "wip" ? "#eab308" : "#64748b" }}>
                      {item.status === "done" ? "✓" : item.status === "wip" ? "◉" : "○"}
                    </span>
                    <span style={{ color: item.status === "planned" ? "#64748b" : "#cbd5e1" }}>{item.text}</span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 16, padding: 10, background: "rgba(30,41,59,0.5)", borderRadius: 6, border: `1px solid ${COLORS.cardBorder}`, fontSize: 12, color: "#94a3b8" }}>
                <span style={{ fontWeight: 600, color: "#cbd5e1" }}>Questions or issues?</span> Use the Screenshot button to capture what you're seeing and share in Teams.
              </div>
            </div>
          </div>
        </div>
      )}

      <main style={{ padding: mobile ? 16 : 32, maxWidth: 1600, margin: "0 auto" }}>
        
        {/* ─── SUMMARY STATS ─── */}
        {data && activeTab !== "salesmatchQA" && activeTab !== "salesmatch2025" && (
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: mobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)", 
            gap: 12, 
            marginBottom: 24 
          }}>
            {[
              { label: "Campaigns", value: filteredSummary?.totalCampaigns || sortedCampaigns.length || 0, color: COLORS.text },
              { label: "Total Sent", value: filteredSummary?.totalSent || 0, color: COLORS.text },
              { label: "Avg Open Rate", value: `${(filteredSummary?.avgOpenRate || 0).toFixed(1)}%`, color: COLORS.success },
              { label: "Avg Click Rate", value: `${(filteredSummary?.avgClickRate || 0).toFixed(1)}%`, color: COLORS.accent },
              { label: "Opt-Outs", value: sortedCampaigns.reduce((sum, c) => sum + (c.optout || 0), 0) || 0, color: "#f87171" },
              { label: `Sales (${attrWindow})`, value: getSalesValue(filteredSummary, attrWindow), color: COLORS.warning },
            ].map((stat, i) => (
              <div key={i} style={{ 
                padding: 16, 
                background: COLORS.card, 
                border: `1px solid ${COLORS.cardBorder}`, 
                borderRadius: 8,
                textAlign: "center"
              }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", marginBottom: 4 }}>
                  {stat.label}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: stat.color }}>
                  {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ─── CAMPAIGNS TAB ─── */}
        {activeTab === "campaigns" && (
          <CampaignsTab
            campaigns={sortedCampaigns}
            selectedCampaign={selectedCampaign}
            setSelectedCampaign={setSelectedCampaign}
            sortConfig={sortConfig}
            setSortConfig={setSortConfig}
            attrWindow={attrWindow}
            mobile={mobile}
          />
        )}

        {/* ─── JOURNEYS TAB ─── */}
        {activeTab === "journeys" && (
          <JourneysTab
            journeys={journeys}
            selectedJourney={selectedJourney}
            setSelectedJourney={setSelectedJourney}
            attrWindow={attrWindow}
            mobile={mobile}
          />
        )}

        {/* ─── WAVE PERFORMANCE TAB ─── */}
        {activeTab === "wavePerformance" && (
          <WavePerformanceTab campaigns={sortedCampaigns} mobile={mobile} />
        )}

        {/* ─── AUDIENCE TAB ─── */}
        {activeTab === "audience" && (
          <AudienceTab audienceData={audienceData} mobile={mobile} />
        )}

        {/* ─── DELIVERABILITY TAB ─── */}
        {activeTab === "deliverability" && (
          <DeliverabilityTab campaigns={sortedCampaigns} mobile={mobile} />
        )}

        {/* ─── LINKS TAB ─── */}
        {activeTab === "links" && (
          <LinksTab campaigns={sortedCampaigns} links={filteredLinks} mobile={mobile} brandFilter={brandFilter} />
        )}

        {/* ─── SALESMATCH QA TAB ─── */}
        {activeTab === "salesmatchQA" && (
          <SalesmatchQATab data={smData} mobile={mobile} brandFilter={brandFilter} />
        )}

        {/* ─── SALESMATCH 2025 (CORRECTED) TAB ─── */}
        {activeTab === "salesmatch2025" && (
          <SalesmatchQATab data={sm25Data} mobile={mobile} variant="2025" />
        )}

        {/* ─── FOOTER ─── */}
        <footer style={{ 
          marginTop: 32, 
          paddingTop: 16, 
          borderTop: `1px solid ${COLORS.cardBorder}`,
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10,
          color: COLORS.textDim
        }}>
          <span>Data as of: {data?.summary?.dataAsOf || data?.dataAsOf || "—"}</span>
          <span>FirstParty INNsight · Innocean Data+Craft · April 2026</span>
        </footer>
      </main>
      </div>{/* end dashboard-content */}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CAMPAIGNS TAB COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function CampaignsTab({ campaigns, selectedCampaign, setSelectedCampaign, sortConfig, setSortConfig, attrWindow, mobile }) {
  const toggleSort = (col) => {
    setSortConfig(prev => ({ col, dir: prev.col === col ? -prev.dir : -1 }));
  };

  const SortHeader = ({ col, label, align = "left" }) => (
    <th 
      onClick={() => toggleSort(col)}
      style={{ 
        padding: "10px 12px", 
        textAlign: align, 
        fontWeight: 600, 
        color: sortConfig.col === col ? COLORS.text : COLORS.textMuted, 
        cursor: "pointer", 
        userSelect: "none",
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.5px"
      }}>
      {label} {sortConfig.col === col ? (sortConfig.dir === -1 ? "↓" : "↑") : ""}
    </th>
  );

  return (
    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : selectedCampaign ? "1fr 1fr" : "1fr", gap: 16 }}>
      {/* Campaign List */}
      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, overflow: "hidden" }}>
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${COLORS.cardBorder}`, fontSize: 13, fontWeight: 600, color: COLORS.text }}>
          📧 Campaign List ({campaigns.length})
        </div>
        <div style={{ maxHeight: 600, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#1e293b", position: "sticky", top: 0, zIndex: 1 }}>
                <SortHeader col="campaignCode" label="Code" />
                <SortHeader col="campaignName" label="Campaign" />
                <SortHeader col="campaignType" label="Type" />
                <SortHeader col="lastSendDate" label="Date" />
                <SortHeader col="sent" label="Sent" align="right" />
                <SortHeader col="openRate" label="Open %" align="right" />
                <SortHeader col="optout" label="Opt-Out" align="right" />
                <SortHeader col="sales" label="Sales" align="right" />
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c, i) => {
                const isSelected = selectedCampaign?.campaignCode === c.campaignCode;
                const sales = attrWindow === '30d' ? c.sales30d : attrWindow === '60d' ? c.sales60d : c.sales90d;
                return (
                  <tr 
                    key={c.campaignCode || i} 
                    onClick={() => setSelectedCampaign(isSelected ? null : c)}
                    style={{ 
                      cursor: "pointer", 
                      background: isSelected ? "rgba(14,165,233,0.15)" : i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)",
                      borderLeft: isSelected ? `3px solid ${COLORS.accent}` : "3px solid transparent",
                      transition: "all 0.15s"
                    }}>
                    <td style={{ padding: "10px 12px", color: COLORS.accent, fontFamily: "monospace", fontSize: 10, fontWeight: 600 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ 
                          padding: "2px 5px", 
                          borderRadius: 3, 
                          fontSize: 8, 
                          fontWeight: 700,
                          background: c.brand === 'GMA' ? "#8b5cf6" : "#3b82f6",
                          color: "#fff"
                        }}>
                          {c.brand || 'HMA'}
                        </span>
                        {c.campaignCode}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px", color: COLORS.text, maxWidth: 200 }}>
                      <div style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {c.campaignName}
                      </div>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ padding: "3px 8px", background: "#1e293b", borderRadius: 4, fontSize: 9, color: COLORS.textMuted }}>
                        {c.campaignType || "—"}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px", color: COLORS.textDim, fontSize: 10 }}>{c.lastSendDate}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: COLORS.textMuted }}>{c.sent?.toLocaleString()}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: COLORS.success, fontWeight: 600 }}>{c.openRate?.toFixed(1)}%</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: c.optout > 0 ? "#f87171" : COLORS.textDim }}>{c.optout?.toLocaleString() || 0}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: COLORS.warning, fontWeight: 600 }}>{sales?.toLocaleString(undefined, {maximumFractionDigits:0})}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Campaign Detail Panel */}
      {selectedCampaign && (
        <CampaignDetailPanel campaign={selectedCampaign} onClose={() => setSelectedCampaign(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CAMPAIGN DETAIL PANEL
// ─────────────────────────────────────────────────────────────────────────────
function CampaignDetailPanel({ campaign, onClose }) {
  const [activeSection, setActiveSection] = useState('overview'); // 'overview' | 'links'
  const [linkSort, setLinkSort] = useState({ col: 'clicks', dir: -1 });
  const [linkSearch, setLinkSearch] = useState('');
  
  // Calculate Net CTR for each link (clicks / delivered * 100)
  const linksWithCTR = useMemo(() => {
    if (!campaign.linkBreakdown || campaign.linkBreakdown.length === 0) return [];
    const delivered = campaign.delivered || 1;
    return campaign.linkBreakdown.map(link => ({
      ...link,
      netCTR: delivered > 0 ? (link.clicks / delivered * 100) : 0,
    }));
  }, [campaign]);
  
  // Filter and sort links
  const sortedLinks = useMemo(() => {
    let filtered = linksWithCTR;
    if (linkSearch) {
      const term = linkSearch.toLowerCase();
      filtered = filtered.filter(l => l.linkName?.toLowerCase().includes(term));
    }
    const { col, dir } = linkSort;
    return [...filtered].sort((a, b) => {
      if (col === 'linkName') return dir * (a.linkName || '').localeCompare(b.linkName || '');
      return dir * ((a[col] || 0) - (b[col] || 0));
    });
  }, [linksWithCTR, linkSearch, linkSort]);
  
  // Extract short URL for display
  const shortUrl = (url) => {
    if (!url) return '';
    // Remove tracking params for display
    try {
      const u = new URL(url);
      let path = u.pathname.replace(/\/$/, '');
      // Truncate long paths
      if (path.length > 50) path = path.slice(0, 47) + '...';
      return u.hostname.replace('www.', '') + path;
    } catch {
      return url.length > 60 ? url.slice(0, 57) + '...' : url;
    }
  };
  
  const hasLinks = linksWithCTR.length > 0;
  const totalLinkClicks = linksWithCTR.reduce((s, l) => s + (l.clicks || 0), 0);

  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 200px)" }}>
      {/* Header */}
      <div style={{ padding: "14px 16px", borderBottom: `1px solid ${COLORS.cardBorder}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ 
              padding: "3px 6px", 
              borderRadius: 3, 
              fontSize: 9, 
              fontWeight: 700,
              background: campaign.brand === 'GMA' ? "#8b5cf6" : "#3b82f6",
              color: "#fff"
            }}>
              {campaign.brand || 'HMA'}
            </span>
            <span style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, color: COLORS.accent, background: "#1e293b", padding: "4px 10px", borderRadius: 4 }}>
              {campaign.campaignCode}
            </span>
            <span style={{ fontSize: 10, color: COLORS.textDim, padding: "3px 8px", background: "#1e293b", borderRadius: 4 }}>
              {campaign.campaignType}
            </span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{campaign.campaignName}</div>
          {campaign.groupCampaign && (
            <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>Journey: {campaign.groupCampaign}</div>
          )}
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: COLORS.textDim, cursor: "pointer", fontSize: 18, padding: 4 }}>✕</button>
      </div>
      
      {/* Section Tabs */}
      <div style={{ display: "flex", borderBottom: `1px solid ${COLORS.cardBorder}`, flexShrink: 0 }}>
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'links', label: `Click Activity${hasLinks ? ` (${linksWithCTR.length})` : ''}` },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            style={{
              flex: 1,
              padding: "10px 12px",
              background: activeSection === tab.id ? "rgba(14,165,233,0.15)" : "transparent",
              border: "none",
              borderBottom: activeSection === tab.id ? `2px solid ${COLORS.accent}` : "2px solid transparent",
              color: activeSection === tab.id ? COLORS.accent : COLORS.textMuted,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s"
            }}
          >
            {tab.id === 'links' ? '🔗 ' : '📊 '}{tab.label}
          </button>
        ))}
      </div>
      
      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {activeSection === 'overview' ? (
          <>
            {/* Metrics Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 20 }}>
              {[
                { label: "SENT", value: campaign.sent, color: COLORS.text },
                { label: "DELIVERED", value: campaign.delivered, color: COLORS.text },
                { label: "OPENED", value: campaign.opened, color: COLORS.success },
                { label: "CLICKED", value: campaign.clicked, color: COLORS.accent },
                { label: "OPT-OUT", value: campaign.optout || 0, color: "#f87171" },
              ].map((m, i) => (
                <div key={i} style={{ textAlign: "center", padding: 10, background: "#1e293b", borderRadius: 6 }}>
                  <div style={{ fontSize: 9, color: COLORS.textDim, marginBottom: 3 }}>{m.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: m.color }}>{m.value?.toLocaleString() || "—"}</div>
                </div>
              ))}
            </div>

            {/* Wave Breakdown */}
            {campaign.waveBreakdown?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 10 }}>Wave Breakdown</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {['EM1', 'EM2', 'EM3'].map(w => {
                    const wave = campaign.waveBreakdown.find(x => x.wave === w);
                    if (!wave) return null;
                    const pct = campaign.sent > 0 ? (wave.sent / campaign.sent * 100) : 0;
                    return (
                      <div key={w} style={{ flex: 1, padding: 12, background: "#1e293b", borderRadius: 6, textAlign: "center" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>{w}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.accent }}>{wave.sent?.toLocaleString()}</div>
                        <div style={{ fontSize: 10, color: COLORS.textDim }}>{pct.toFixed(0)}% of total</div>
                        <div style={{ marginTop: 8, height: 4, background: "#334155", borderRadius: 2, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: COLORS.accent, borderRadius: 2 }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Audience Breakdown */}
            {campaign.audienceBreakdown?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 10 }}>Audience Breakdown</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {campaign.audienceBreakdown.map((a, i) => (
                    <div key={i} style={{ flex: 1, padding: 12, background: "#1e293b", borderRadius: 6, borderLeft: `3px solid ${AUDIENCE_COLORS[a.audience] || COLORS.textDim}` }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: AUDIENCE_COLORS[a.audience] || COLORS.textMuted, marginBottom: 4 }}>
                        {a.audience === 'HR' ? 'Hand Raisers' : a.audience === 'CO' ? 'Current Owners' : a.audience === 'PO' ? 'Previous Owners' : a.audience}
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{a.sent?.toLocaleString()}</div>
                      <div style={{ fontSize: 10, color: COLORS.textDim }}>sent</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Vehicle Breakdown */}
            {campaign.vehicleBreakdown?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 10 }}>Vehicle Groups</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {campaign.vehicleBreakdown.map((v, i) => (
                    <span key={i} style={{ padding: "5px 10px", background: "#1e293b", borderRadius: 4, fontSize: 10, color: COLORS.text }}>
                      {v.vehicle}: <span style={{ color: COLORS.accent, fontWeight: 600 }}>{v.sent?.toLocaleString()}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Link Summary on Overview */}
            {hasLinks && (
              <div style={{ marginTop: 16, padding: 12, background: "rgba(14,165,233,0.1)", border: `1px solid ${COLORS.accent}`, borderRadius: 6 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.accent }}>🔗 {linksWithCTR.length} Tracked Links</span>
                    <span style={{ fontSize: 10, color: COLORS.textMuted, marginLeft: 12 }}>
                      Total Clicks: <span style={{ color: COLORS.text, fontWeight: 600 }}>{totalLinkClicks.toLocaleString()}</span>
                    </span>
                  </div>
                  <button 
                    onClick={() => setActiveSection('links')}
                    style={{
                      padding: "6px 12px",
                      background: COLORS.accent,
                      border: "none",
                      borderRadius: 4,
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 600,
                      cursor: "pointer"
                    }}
                  >
                    View Click Activity →
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Click Activity Section - mimics SFMC Tracking view */
          <div>
            {!hasLinks ? (
              <div style={{ padding: 40, textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
                <div style={{ fontSize: 13, color: COLORS.textMuted }}>No link click data available for this campaign</div>
                <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 8 }}>
                  Run Process_SFMC_First_Party_Campaigns v1.5 to populate link data
                </div>
              </div>
            ) : (
              <>
                {/* Link Stats Summary */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
                  {[
                    { label: "Total Links", value: linksWithCTR.length, color: COLORS.text },
                    { label: "Total Clicks", value: totalLinkClicks, color: COLORS.accent },
                    { label: "Avg CTR", value: `${(totalLinkClicks / (campaign.delivered || 1) * 100).toFixed(2)}%`, color: COLORS.success },
                    { label: "Top Link Clicks", value: sortedLinks[0]?.clicks || 0, color: COLORS.warning },
                  ].map((m, i) => (
                    <div key={i} style={{ textAlign: "center", padding: 10, background: "#1e293b", borderRadius: 6 }}>
                      <div style={{ fontSize: 9, color: COLORS.textDim, marginBottom: 3 }}>{m.label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: m.color }}>
                        {typeof m.value === 'number' ? m.value.toLocaleString() : m.value}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Search */}
                <div style={{ marginBottom: 12 }}>
                  <input
                    type="text"
                    placeholder="Search links..."
                    value={linkSearch}
                    onChange={(e) => setLinkSearch(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      background: "#1e293b",
                      border: `1px solid ${COLORS.cardBorder}`,
                      borderRadius: 6,
                      color: COLORS.text,
                      fontSize: 11
                    }}
                  />
                </div>
                
                {/* Links Table - matches SFMC Tracking format */}
                <div style={{ background: "#1e293b", borderRadius: 6, overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                    <thead>
                      <tr style={{ background: "#0f172a" }}>
                        <th 
                          onClick={() => setLinkSort(prev => ({ col: 'linkName', dir: prev.col === 'linkName' ? -prev.dir : 1 }))}
                          style={{ padding: "10px 12px", textAlign: "left", color: linkSort.col === 'linkName' ? COLORS.text : COLORS.textDim, fontWeight: 600, cursor: "pointer", userSelect: "none" }}
                        >
                          Link {linkSort.col === 'linkName' && (linkSort.dir === 1 ? '↑' : '↓')}
                        </th>
                        <th 
                          onClick={() => setLinkSort(prev => ({ col: 'totalClicks', dir: prev.col === 'totalClicks' ? -prev.dir : -1 }))}
                          style={{ padding: "10px 12px", textAlign: "right", color: linkSort.col === 'totalClicks' ? COLORS.text : COLORS.textDim, fontWeight: 600, width: 70, cursor: "pointer", userSelect: "none" }}
                        >
                          Total {linkSort.col === 'totalClicks' && (linkSort.dir === -1 ? '↓' : '↑')}
                        </th>
                        <th 
                          onClick={() => setLinkSort(prev => ({ col: 'clicks', dir: prev.col === 'clicks' ? -prev.dir : -1 }))}
                          style={{ padding: "10px 12px", textAlign: "right", color: linkSort.col === 'clicks' ? COLORS.text : COLORS.textDim, fontWeight: 600, width: 70, cursor: "pointer", userSelect: "none" }}
                        >
                          Unique {linkSort.col === 'clicks' && (linkSort.dir === -1 ? '↓' : '↑')}
                        </th>
                        <th 
                          onClick={() => setLinkSort(prev => ({ col: 'netCTR', dir: prev.col === 'netCTR' ? -prev.dir : -1 }))}
                          style={{ padding: "10px 12px", textAlign: "right", color: linkSort.col === 'netCTR' ? COLORS.text : COLORS.textDim, fontWeight: 600, width: 70, cursor: "pointer", userSelect: "none" }}
                        >
                          Net CTR {linkSort.col === 'netCTR' && (linkSort.dir === -1 ? '↓' : '↑')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedLinks.map((link, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${COLORS.cardBorder}` }}>
                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ color: COLORS.accent, fontSize: 10, wordBreak: "break-all", lineHeight: 1.4 }} title={link.linkName}>
                              {shortUrl(link.linkName)}
                            </div>
                          </td>
                          <td style={{ padding: "10px 12px", textAlign: "right", color: COLORS.text, fontWeight: 500 }}>
                            {link.totalClicks?.toLocaleString()}
                          </td>
                          <td style={{ padding: "10px 12px", textAlign: "right", color: COLORS.accent, fontWeight: 600 }}>
                            {link.clicks?.toLocaleString()}
                          </td>
                          <td style={{ padding: "10px 12px", textAlign: "right", color: link.netCTR >= 0.1 ? COLORS.success : COLORS.textMuted, fontWeight: 500 }}>
                            {link.netCTR.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {sortedLinks.length === 0 && linkSearch && (
                  <div style={{ padding: 20, textAlign: "center", color: COLORS.textMuted, fontSize: 11 }}>
                    No links matching "{linkSearch}"
                  </div>
                )}
                
                <div style={{ marginTop: 12, fontSize: 10, color: COLORS.textDim, textAlign: "center" }}>
                  Showing {sortedLinks.length} of {linksWithCTR.length} links • Net CTR = Unique Clicks / Delivered
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// JOURNEYS TAB COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function JourneysTab({ journeys, selectedJourney, setSelectedJourney, attrWindow, mobile }) {
  const anomalyCount = journeys.filter(j => j.hasDataIssue).length;
  
  return (
    <div>
      {/* Anomaly Summary */}
      {anomalyCount > 0 && (
        <div style={{ padding: 12, background: "rgba(245,158,11,0.1)", border: `1px solid ${COLORS.warning}`, borderRadius: 6, marginBottom: 16, fontSize: 12 }}>
          <span style={{ color: COLORS.warning, fontWeight: 600 }}>⚠️ {anomalyCount} journey{anomalyCount > 1 ? 's' : ''} with data anomalies</span>
          <span style={{ color: COLORS.textMuted, marginLeft: 8 }}>— waves larger than expected (possible data gap)</span>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : selectedJourney ? "1fr 1fr" : "1fr", gap: 16 }}>
        {/* Journey List */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", borderBottom: `1px solid ${COLORS.cardBorder}`, fontSize: 13, fontWeight: 600, color: COLORS.text }}>
            🔀 Journey Groups ({journeys.length})
          </div>
          <div style={{ maxHeight: 600, overflowY: "auto" }}>
            {journeys.map((j, i) => {
              const isSelected = selectedJourney?.name === j.name;
              return (
                <div 
                  key={j.name} 
                  onClick={() => setSelectedJourney(isSelected ? null : j)}
                  style={{ 
                    padding: "14px 16px", 
                    cursor: "pointer", 
                    borderBottom: `1px solid ${COLORS.cardBorder}`,
                    background: isSelected ? "rgba(14,165,233,0.15)" : i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)",
                    borderLeft: isSelected ? `3px solid ${COLORS.accent}` : "3px solid transparent",
                    transition: "all 0.15s"
                  }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{j.name}</span>
                        {j.hasDataIssue && (
                          <span title={j.dataIssueDetails.join('; ')} style={{ fontSize: 12, cursor: "help" }}>⚠️</span>
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 3 }}>
                        {j.campaignCount} campaigns · {j.firstSend} → {j.lastSend}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{j.totalSent.toLocaleString()}</div>
                      <div style={{ fontSize: 10, color: COLORS.textDim }}>sent</div>
                    </div>
                  </div>
                  {/* Audience Pills */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {Object.entries(j.audiences).filter(([_, waves]) => waves.EM1 || waves.EM2 || waves.EM3).map(([aud, waves]) => {
                      const waveCount = [waves.EM1, waves.EM2, waves.EM3].filter(Boolean).length;
                      return (
                        <span key={aud} style={{ 
                          fontSize: 9, 
                          padding: "3px 8px", 
                          borderRadius: 4, 
                          background: AUDIENCE_COLORS[aud] || COLORS.textDim, 
                          color: "#fff", 
                          fontWeight: 600 
                        }}>
                          {aud} ({waveCount}w)
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Journey Detail Panel */}
        {selectedJourney && (
          <JourneyDetailPanel journey={selectedJourney} onClose={() => setSelectedJourney(null)} />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// JOURNEY DETAIL PANEL
// ─────────────────────────────────────────────────────────────────────────────
function JourneyDetailPanel({ journey, onClose }) {
  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, overflow: "hidden" }}>
      <div style={{ padding: "14px 16px", borderBottom: `1px solid ${COLORS.cardBorder}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{journey.name}</div>
          <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
            {journey.campaignCount} campaigns · {journey.totalSent.toLocaleString()} total sent
          </div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: COLORS.textDim, cursor: "pointer", fontSize: 18, padding: 4 }}>✕</button>
      </div>
      
      <div style={{ padding: 16, maxHeight: 550, overflowY: "auto" }}>
        {/* Data Quality Alert */}
        {journey.hasDataIssue && (
          <div style={{ padding: 12, background: "rgba(245,158,11,0.1)", border: `1px solid ${COLORS.warning}`, borderRadius: 6, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.warning, marginBottom: 4 }}>⚠️ Data Anomaly Detected</div>
            <div style={{ fontSize: 10, color: "#fcd34d" }}>
              {journey.dataIssueDetails.map((d, i) => <div key={i}>{d}</div>)}
            </div>
            <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 6 }}>Each wave should be ≤ prior wave (non-responders only)</div>
          </div>
        )}
        
        {/* Wave Funnels by Audience */}
        {Object.entries(journey.audiences)
          .filter(([_, waves]) => waves.EM1 || waves.EM2 || waves.EM3)
          .map(([aud, waves]) => {
          const audColor = AUDIENCE_COLORS[aud] || COLORS.textDim;
          const audName = aud === 'HR' ? 'Hand Raisers' : aud === 'CO' ? 'Current Owners' : aud === 'PO' ? 'Previous Owners' : aud;
          const em1Sent = waves.EM1?.sent || 0;
          const em2Sent = waves.EM2?.sent || 0;
          const maxSent = Math.max(em1Sent, em2Sent, waves.EM3?.sent || 0, 1);
          
          return (
            <div key={aud} style={{ marginBottom: 20, padding: 14, background: "#1e293b", borderRadius: 8, borderLeft: `3px solid ${audColor}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: audColor, marginBottom: 14 }}>{audName} ({aud})</div>
              
              {/* Wave Funnel */}
              <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                {['EM1', 'EM2', 'EM3'].map((w, wi) => {
                  const wave = waves[w];
                  if (!wave) return (
                    <div key={w} style={{ flex: 1, textAlign: "center", opacity: 0.3 }}>
                      <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 4 }}>{w}</div>
                      <div style={{ height: 60, background: "#334155", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ fontSize: 10, color: COLORS.textDim }}>—</span>
                      </div>
                    </div>
                  );
                  
                  const pct = em1Sent > 0 && wi > 0 ? (wave.sent / em1Sent * 100) : 100;
                  const barHeight = Math.max(24, (wave.sent / maxSent) * 80);
                  const prevWave = wi === 1 ? waves.EM1 : wi === 2 ? waves.EM2 : null;
                  const isAnomaly = prevWave && wave.sent > prevWave.sent;
                  
                  return (
                    <div key={w} style={{ flex: 1, textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 4 }}>{w}</div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <div style={{ 
                          width: "100%", 
                          height: barHeight, 
                          background: isAnomaly ? COLORS.error : audColor, 
                          borderRadius: 4,
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}>
                          {isAnomaly && <span style={{ position: "absolute", top: -18, fontSize: 12 }}>⚠️</span>}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginTop: 6 }}>{wave.sent.toLocaleString()}</div>
                        {wi > 0 && em1Sent > 0 && (
                          <div style={{ fontSize: 9, color: isAnomaly ? "#f87171" : COLORS.textDim }}>
                            {pct.toFixed(0)}% of EM1
                          </div>
                        )}
                      </div>
                      {/* Campaign Code */}
                      <div style={{ marginTop: 8, fontSize: 9, fontFamily: "monospace", color: COLORS.accent, background: COLORS.bg, padding: "3px 6px", borderRadius: 3 }}>
                        {wave.campaignCode}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Response Flow */}
              <div style={{ display: "flex", justifyContent: "center", gap: 40, marginTop: 10 }}>
                {waves.EM1 && waves.EM2 && (
                  <div style={{ fontSize: 9, color: COLORS.textDim }}>
                    → {em1Sent > 0 ? ((1 - em2Sent / em1Sent) * 100).toFixed(0) : 0}% responded
                  </div>
                )}
              </div>
            </div>
          );
        })}
        
        {/* Campaign Code Reference */}
        <div style={{ padding: 14, background: "#1e293b", borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 10 }}>📋 Campaign Codes (for MC lookup)</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, fontSize: 10 }}>
            {journey.campaigns.sort((a,b) => (a.campaignCode || '').localeCompare(b.campaignCode || '')).slice(0, 12).map(c => (
              <div key={c.campaignCode} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontFamily: "monospace", color: COLORS.accent, fontWeight: 600 }}>{c.campaignCode}</span>
                <span style={{ color: COLORS.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {(c.campaignName || '').replace(/\[U\]\s*/, '').slice(0, 18)}
                </span>
              </div>
            ))}
          </div>
          {journey.campaigns.length > 12 && (
            <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 8 }}>+{journey.campaigns.length - 12} more</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PLACEHOLDER TAB
// ─────────────────────────────────────────────────────────────────────────────
function PlaceholderTab({ title, icon, description }) {
  return (
    <div style={{ 
      padding: 60, 
      textAlign: "center", 
      background: COLORS.card, 
      border: `1px solid ${COLORS.cardBorder}`, 
      borderRadius: 8 
    }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{icon}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 13, color: COLORS.textMuted }}>{description}</div>
      <div style={{ marginTop: 24, fontSize: 11, color: COLORS.textDim }}>
        Data pipeline in development
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────
function getSalesValue(summary, window) {
  if (!summary) return 0;
  const val = window === '30d' ? summary.totalSales30d 
    : window === '60d' ? (summary.totalSales60d || summary.totalSales30d * 1.3) 
    : summary.totalSales90d;
  return (val || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function getSampleData() {
  return {
    summary: {
      totalCampaigns: 270,
      totalSent: 78600000,
      avgOpenRate: 18.4,
      avgClickRate: 2.1,
      totalSales30d: 4521,
      totalSales60d: 6230,
      totalSales90d: 8142,
      dataAsOf: "2026-04-08"
    },
    campaigns: [
      { campaignCode: "034272", campaignName: "[U] 2026 HMA JAN EV SUPPORT_HR_EM1", campaignType: "EV Support", groupCampaign: "[G] 2026 HMA EV SUPPORT", firstSendDate: "2026-02-05", lastSendDate: "2026-02-05", sent: 247910, delivered: 243000, opened: 48200, clicked: 5120, openRate: 19.8, clickRate: 2.1, sales30d: 312, sales60d: 445, sales90d: 521 },
      { campaignCode: "034396", campaignName: "[U] 2026 HMA FEB EV SUPPORT_HR_EM2", campaignType: "EV Support", groupCampaign: "[G] 2026 HMA EV SUPPORT", firstSendDate: "2026-02-23", lastSendDate: "2026-02-23", sent: 79686, delivered: 78100, opened: 14200, clicked: 1580, openRate: 18.2, clickRate: 2.0, sales30d: 98, sales60d: 142, sales90d: 178 },
      { campaignCode: "034447", campaignName: "[U] 2026 HMA MAR EV SUPPORT_HR_EM3", campaignType: "EV Support", groupCampaign: "[G] 2026 HMA EV SUPPORT", firstSendDate: "2026-03-15", lastSendDate: "2026-03-15", sent: 212606, delivered: 208000, opened: 38500, clicked: 4100, openRate: 18.5, clickRate: 2.0, sales30d: 267, sales60d: 378, sales90d: 445 },
      { campaignCode: "034270", campaignName: "[U] 2026 HMA JAN EV SUPPORT_CO_EM1", campaignType: "EV Support", groupCampaign: "[G] 2026 HMA EV SUPPORT", firstSendDate: "2026-02-05", lastSendDate: "2026-02-05", sent: 180000, delivered: 176500, opened: 32000, clicked: 3200, openRate: 18.1, clickRate: 1.8, sales30d: 145, sales60d: 210, sales90d: 265 },
      { campaignCode: "035100", campaignName: "[U] 2026 HMA MAR MSP_HR_EM1", campaignType: "MSP", groupCampaign: "[G] 2026 HMA MSP", firstSendDate: "2026-03-18", lastSendDate: "2026-03-18", sent: 320000, delivered: 314000, opened: 62000, clicked: 7100, openRate: 19.7, clickRate: 2.3, sales30d: 412, sales60d: 580, sales90d: 710 },
    ]
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// AUDIENCE TAB COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function AudienceTab({ audienceData, mobile }) {
  if (!audienceData) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>👥</div>
        <div>Loading audience data...</div>
      </div>
    );
  }

  const CHART_COLORS = ['#0ea5e9', '#22c55e', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];

  // Prepare data for charts
  const ageData = audienceData.ageBands?.slice(0, 7) || [];
  const incomeData = audienceData.incomeBands?.slice(0, 8) || [];
  const affinityData = audienceData.vehicleAffinity?.slice(0, 10) || [];
  const segmentData = audienceData.vehicleSegments?.slice(0, 8) || [];
  const lifestyleData = audienceData.lifestyleInterests?.slice(0, 10) || [];

  return (
    <div>
      {/* Summary Banner */}
      <div style={{ 
        padding: 20, 
        background: `linear-gradient(135deg, ${COLORS.card} 0%, #1e293b 100%)`,
        border: `1px solid ${COLORS.cardBorder}`, 
        borderRadius: 8, 
        marginBottom: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 16
      }}>
        <div>
          <div style={{ fontSize: 11, color: COLORS.textDim, textTransform: "uppercase", marginBottom: 4 }}>Acxiom PersonicX Database</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.text }}>
            {audienceData.summary?.totalHouseholdsFormatted || audienceData.summary?.totalHouseholds?.toLocaleString() || "18.8M"}
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>Households with demographic enrichment</div>
        </div>
        <div style={{ display: "flex", gap: 24 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.success }}>{audienceData.inMarketSignals?.intendToPurchasePct || 11.2}%</div>
            <div style={{ fontSize: 10, color: COLORS.textDim }}>Intend to Purchase</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.accent }}>{audienceData.inMarketSignals?.evInterestPct || 4.7}%</div>
            <div style={{ fontSize: 10, color: COLORS.textDim }}>EV Interest</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.warning }}>{audienceData.loyaltyProfile?.brandSwitcherPct || 54.8}%</div>
            <div style={{ fontSize: 10, color: COLORS.textDim }}>Brand Switchers</div>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 16 }}>
        
        {/* Age Distribution */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 16 }}>📊 Age Distribution</div>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ageData} layout="vertical">
                <XAxis type="number" tick={{ fill: COLORS.textDim, fontSize: 10 }} />
                <YAxis dataKey="band" type="category" width={50} tick={{ fill: COLORS.textMuted, fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 4 }}
                  labelStyle={{ color: COLORS.text }}
                  formatter={(val) => [`${val.toLocaleString()} (${ageData.find(a => a.count === val)?.pct}%)`, 'Count']}
                />
                <Bar dataKey="count" fill={COLORS.accent} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Income Distribution */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 16 }}>💰 Income Distribution</div>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={incomeData} layout="vertical">
                <XAxis type="number" tick={{ fill: COLORS.textDim, fontSize: 10 }} />
                <YAxis dataKey="band" type="category" width={70} tick={{ fill: COLORS.textMuted, fontSize: 9 }} />
                <Tooltip 
                  contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 4 }}
                  labelStyle={{ color: COLORS.text }}
                  formatter={(val) => [val.toLocaleString(), 'Count']}
                />
                <Bar dataKey="count" fill={COLORS.success} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Vehicle Brand Affinity */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 16 }}>🚗 Vehicle Brand Affinity (Top 10)</div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={affinityData} layout="vertical">
                <XAxis type="number" tick={{ fill: COLORS.textDim, fontSize: 10 }} />
                <YAxis dataKey="brand" type="category" width={80} tick={{ fill: COLORS.textMuted, fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 4 }}
                  labelStyle={{ color: COLORS.text }}
                  formatter={(val, name, props) => [`${val.toLocaleString()} (${props.payload.pct}%)`, 'Affinity']}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {affinityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.brand === 'Hyundai' ? COLORS.accent : entry.brand === 'Kia' ? COLORS.success : CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Vehicle Segment Preferences */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 16 }}>🎯 Vehicle Segment Preferences</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {segmentData.map((seg, i) => (
              <div key={seg.segment} style={{ 
                flex: "1 1 45%",
                padding: 12, 
                background: "#1e293b", 
                borderRadius: 6,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span style={{ fontSize: 11, color: COLORS.textMuted }}>{seg.segment}</span>
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{seg.pct}%</span>
                  <div style={{ fontSize: 9, color: COLORS.textDim }}>{(seg.count / 1000000).toFixed(1)}M</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* In-Market Signals */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 16 }}>🔥 In-Market Signals</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[
              { label: "Intent to Purchase", value: audienceData.inMarketSignals?.intendToPurchase, pct: audienceData.inMarketSignals?.intendToPurchasePct, color: COLORS.success },
              { label: "New Car Buyer", value: audienceData.inMarketSignals?.newCarBuyer, pct: audienceData.inMarketSignals?.newCarBuyerPct, color: COLORS.accent },
              { label: "EV Interest", value: audienceData.inMarketSignals?.evInterest, pct: audienceData.inMarketSignals?.evInterestPct, color: "#06b6d4" },
              { label: "Hybrid Interest", value: audienceData.inMarketSignals?.hybridInterest, pct: audienceData.inMarketSignals?.hybridInterestPct, color: "#22c55e" },
              { label: "Luxury Move-up", value: audienceData.inMarketSignals?.luxuryMoveup, pct: audienceData.inMarketSignals?.luxuryMoveupPct, color: COLORS.purple },
              { label: "SUV Move-up", value: audienceData.inMarketSignals?.suvMoveup, pct: audienceData.inMarketSignals?.suvMoveupPct, color: COLORS.warning },
              { label: "Green Living", value: audienceData.inMarketSignals?.greenLiving, pct: audienceData.inMarketSignals?.greenLivingPct, color: "#84cc16" },
              { label: "Auto Enthusiast", value: audienceData.inMarketSignals?.autoEnthusiast, pct: audienceData.inMarketSignals?.autoEnthusiastPct, color: COLORS.error },
            ].map(signal => (
              <div key={signal.label} style={{ padding: 10, background: "#1e293b", borderRadius: 6, borderLeft: `3px solid ${signal.color}` }}>
                <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 4 }}>{signal.label}</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: signal.color }}>{signal.pct || 0}%</span>
                  <span style={{ fontSize: 10, color: COLORS.textDim }}>{((signal.value || 0) / 1000000).toFixed(1)}M</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Loyalty Profile */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 16 }}>🎭 Loyalty Profile</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* Brand Loyalty */}
            <div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8 }}>Brand Loyalty</div>
              <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                <div style={{ 
                  flex: audienceData.loyaltyProfile?.brandLoyalPct || 45, 
                  height: 24, 
                  background: COLORS.accent, 
                  borderRadius: "4px 0 0 4px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 600, color: "#fff"
                }}>
                  {audienceData.loyaltyProfile?.brandLoyalPct || 45}%
                </div>
                <div style={{ 
                  flex: audienceData.loyaltyProfile?.brandSwitcherPct || 55, 
                  height: 24, 
                  background: COLORS.warning, 
                  borderRadius: "0 4px 4px 0",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 600, color: "#fff"
                }}>
                  {audienceData.loyaltyProfile?.brandSwitcherPct || 55}%
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}>
                <span style={{ color: COLORS.accent }}>● Loyal</span>
                <span style={{ color: COLORS.warning }}>● Switcher</span>
              </div>
            </div>
            {/* Segment Loyalty */}
            <div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8 }}>Segment Loyalty</div>
              <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                <div style={{ 
                  flex: audienceData.loyaltyProfile?.segmentLoyalPct || 38, 
                  height: 24, 
                  background: COLORS.purple, 
                  borderRadius: "4px 0 0 4px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 600, color: "#fff"
                }}>
                  {audienceData.loyaltyProfile?.segmentLoyalPct || 38}%
                </div>
                <div style={{ 
                  flex: audienceData.loyaltyProfile?.segmentSwitcherPct || 62, 
                  height: 24, 
                  background: COLORS.success, 
                  borderRadius: "0 4px 4px 0",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 600, color: "#fff"
                }}>
                  {audienceData.loyaltyProfile?.segmentSwitcherPct || 62}%
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}>
                <span style={{ color: COLORS.purple }}>● Loyal</span>
                <span style={{ color: COLORS.success }}>● Switcher</span>
              </div>
            </div>
          </div>
        </div>

        {/* Lifestyle Interests */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16, gridColumn: mobile ? "1" : "1 / -1" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 16 }}>🎨 Lifestyle Interests (Top 10)</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {lifestyleData.map((item, i) => (
              <div key={item.interest} style={{ 
                padding: "8px 14px", 
                background: "#1e293b", 
                borderRadius: 20,
                display: "flex",
                alignItems: "center",
                gap: 8
              }}>
                <span style={{ fontSize: 11, color: COLORS.textMuted }}>{item.interest}</span>
                <span style={{ 
                  fontSize: 11, 
                  fontWeight: 700, 
                  color: COLORS.text,
                  background: CHART_COLORS[i % CHART_COLORS.length],
                  padding: "2px 6px",
                  borderRadius: 10
                }}>
                  {item.pct}%
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Current Vehicle Ownership */}
        {audienceData.currentVehicles && (
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16, gridColumn: mobile ? "1" : "1 / -1" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 16 }}>🚙 Current Vehicle Ownership (Top Makes)</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {audienceData.currentVehicles.slice(0, 12).map((v, i) => (
                <div key={v.make} style={{ 
                  padding: "10px 16px", 
                  background: "#1e293b", 
                  borderRadius: 6,
                  textAlign: "center",
                  minWidth: 80
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{v.make}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: CHART_COLORS[i % CHART_COLORS.length] }}>{v.pct}%</div>
                  <div style={{ fontSize: 9, color: COLORS.textDim }}>{(v.count / 1000000).toFixed(1)}M</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Data Timestamp */}
      <div style={{ marginTop: 16, fontSize: 10, color: COLORS.textDim, textAlign: "right" }}>
        Acxiom data as of: {audienceData.dataAsOf || "—"}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SAMPLE AUDIENCE DATA
// ─────────────────────────────────────────────────────────────────────────────
function getSampleAudienceData() {
  return {
    dataAsOf: "2026-04-08",
    summary: {
      totalHouseholds: 18795167,
      totalHouseholdsFormatted: "18,795,167"
    },
    ageBands: [
      { band: "18-24", count: 1250000, pct: 6.6 },
      { band: "25-34", count: 3200000, pct: 17.0 },
      { band: "35-44", count: 3800000, pct: 20.2 },
      { band: "45-54", count: 3600000, pct: 19.2 },
      { band: "55-64", count: 3400000, pct: 18.1 },
      { band: "65-74", count: 2400000, pct: 12.8 },
      { band: "75+", count: 1145167, pct: 6.1 }
    ],
    incomeBands: [
      { band: "<$25K", count: 1500000, pct: 8.0 },
      { band: "$25-50K", count: 3200000, pct: 17.0 },
      { band: "$50-75K", count: 3800000, pct: 20.2 },
      { band: "$75-100K", count: 3400000, pct: 18.1 },
      { band: "$100-125K", count: 2600000, pct: 13.8 },
      { band: "$125-150K", count: 1800000, pct: 9.6 },
      { band: "$150-200K", count: 1400000, pct: 7.4 },
      { band: "$200K+", count: 1095167, pct: 5.8 }
    ],
    vehicleAffinity: [
      { brand: "Toyota", count: 2850000, pct: 15.2 },
      { brand: "Ford", count: 2640000, pct: 14.1 },
      { brand: "Honda", count: 2340000, pct: 12.4 },
      { brand: "Chevrolet", count: 2100000, pct: 11.2 },
      { brand: "Hyundai", count: 1890000, pct: 10.1 },
      { brand: "Nissan", count: 1650000, pct: 8.8 },
      { brand: "Kia", count: 1420000, pct: 7.6 },
      { brand: "Subaru", count: 980000, pct: 5.2 },
      { brand: "BMW", count: 720000, pct: 3.8 },
      { brand: "Mercedes-Benz", count: 680000, pct: 3.6 }
    ],
    vehicleSegments: [
      { segment: "Compact SUV", count: 4100000, pct: 21.8 },
      { segment: "Mid-size SUV", count: 3200000, pct: 17.0 },
      { segment: "Compact CUV", count: 2800000, pct: 14.9 },
      { segment: "New Regular", count: 2400000, pct: 12.8 },
      { segment: "Used Vehicle", count: 2100000, pct: 11.2 },
      { segment: "Hybrid", count: 1450000, pct: 7.7 },
      { segment: "Electric", count: 890000, pct: 4.7 },
      { segment: "New Luxury", count: 720000, pct: 3.8 }
    ],
    inMarketSignals: {
      intendToPurchase: 2100000, intendToPurchasePct: 11.2,
      newCarBuyer: 1850000, newCarBuyerPct: 9.8,
      evInterest: 890000, evInterestPct: 4.7,
      hybridInterest: 1450000, hybridInterestPct: 7.7,
      luxuryMoveup: 980000, luxuryMoveupPct: 5.2,
      suvMoveup: 1340000, suvMoveupPct: 7.1,
      greenLiving: 3200000, greenLivingPct: 17.0,
      autoEnthusiast: 2400000, autoEnthusiastPct: 12.8
    },
    loyaltyProfile: {
      brandLoyal: 8500000, brandLoyalPct: 45.2,
      brandSwitcher: 10295167, brandSwitcherPct: 54.8,
      segmentLoyal: 7200000, segmentLoyalPct: 38.3,
      segmentSwitcher: 11595167, segmentSwitcherPct: 61.7
    },
    lifestyleInterests: [
      { interest: "Auto Enthusiast", count: 2400000, pct: 12.8 },
      { interest: "Green Living", count: 3200000, pct: 17.0 },
      { interest: "Travel International", count: 2100000, pct: 11.2 },
      { interest: "Golf", count: 1200000, pct: 6.4 },
      { interest: "Investments", count: 1800000, pct: 9.6 },
      { interest: "Camping/Hiking", count: 1650000, pct: 8.8 },
      { interest: "Exercise/Aerobic", count: 1420000, pct: 7.6 },
      { interest: "Dog Owner", count: 2800000, pct: 14.9 },
      { interest: "Fishing", count: 980000, pct: 5.2 },
      { interest: "Boating/Sailing", count: 720000, pct: 3.8 }
    ],
    currentVehicles: [
      { make: "FORD", count: 2100000, pct: 11.2 },
      { make: "TOYOTA", count: 1980000, pct: 10.5 },
      { make: "CHEVROLET", count: 1850000, pct: 9.8 },
      { make: "HONDA", count: 1720000, pct: 9.2 },
      { make: "NISSAN", count: 1200000, pct: 6.4 },
      { make: "HYUNDAI", count: 1050000, pct: 5.6 },
      { make: "JEEP", count: 890000, pct: 4.7 },
      { make: "RAM", count: 780000, pct: 4.1 },
      { make: "GMC", count: 720000, pct: 3.8 },
      { make: "KIA", count: 680000, pct: 3.6 },
      { make: "SUBARU", count: 620000, pct: 3.3 },
      { make: "BMW", count: 450000, pct: 2.4 }
    ]
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// DELIVERABILITY TAB COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function DeliverabilityTab({ campaigns, mobile }) {
  const [sortConfig, setSortConfig] = useState({ col: 'bounceRate', dir: -1 });
  
  // Calculate deliverability metrics
  const metrics = useMemo(() => {
    const totalSent = campaigns.reduce((s, c) => s + (c.sent || 0), 0);
    const totalDelivered = campaigns.reduce((s, c) => s + (c.delivered || 0), 0);
    const totalBounced = campaigns.reduce((s, c) => s + (c.bounced || 0), 0);
    const avgDeliveryRate = campaigns.length > 0 
      ? campaigns.reduce((s, c) => s + (c.deliveryRate || 0), 0) / campaigns.length 
      : 0;
    
    return {
      totalSent,
      totalDelivered,
      totalBounced,
      avgDeliveryRate,
      bounceRate: totalSent > 0 ? (totalBounced / totalSent * 100) : 0
    };
  }, [campaigns]);
  
  // Campaigns with bounce rate calculated, sorted
  const sortedCampaigns = useMemo(() => {
    const withBounceRate = campaigns.map(c => ({
      ...c,
      bounceRate: c.sent > 0 ? (c.bounced / c.sent * 100) : 0
    }));
    
    const { col, dir } = sortConfig;
    return withBounceRate.sort((a, b) => {
      let va = a[col], vb = b[col];
      if (typeof va === 'string') return dir * va.localeCompare(vb);
      return dir * ((va || 0) - (vb || 0));
    });
  }, [campaigns, sortConfig]);
  
  // Bounce rate distribution
  const bounceDistribution = useMemo(() => {
    const buckets = [
      { range: '0-0.5%', min: 0, max: 0.5, count: 0 },
      { range: '0.5-1%', min: 0.5, max: 1, count: 0 },
      { range: '1-2%', min: 1, max: 2, count: 0 },
      { range: '2-3%', min: 2, max: 3, count: 0 },
      { range: '3%+', min: 3, max: 100, count: 0 },
    ];
    
    campaigns.forEach(c => {
      const rate = c.sent > 0 ? (c.bounced / c.sent * 100) : 0;
      const bucket = buckets.find(b => rate >= b.min && rate < b.max);
      if (bucket) bucket.count++;
    });
    
    return buckets;
  }, [campaigns]);
  
  // Monthly trend
  const monthlyTrend = useMemo(() => {
    const byMonth = {};
    campaigns.forEach(c => {
      const key = `${c.year}-${c.month}`;
      if (!byMonth[key]) byMonth[key] = { month: key, sent: 0, bounced: 0 };
      byMonth[key].sent += c.sent || 0;
      byMonth[key].bounced += c.bounced || 0;
    });
    
    return Object.values(byMonth)
      .map(m => ({ ...m, bounceRate: m.sent > 0 ? (m.bounced / m.sent * 100) : 0 }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [campaigns]);
  
  // Sort header component
  const SortHeader = ({ col, label, align = "left" }) => (
    <th 
      onClick={() => setSortConfig(prev => ({ col, dir: prev.col === col ? -prev.dir : -1 }))}
      style={{ 
        padding: "10px 12px", 
        textAlign: align, 
        fontWeight: 600, 
        color: sortConfig.col === col ? COLORS.text : COLORS.textMuted,
        cursor: "pointer",
        userSelect: "none",
        fontSize: 10
      }}>
      {label} {sortConfig.col === col ? (sortConfig.dir === -1 ? "↓" : "↑") : ""}
    </th>
  );

  return (
    <div>
      {/* Summary Stats */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: mobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)", 
        gap: 12, 
        marginBottom: 24 
      }}>
        {[
          { label: "Total Sent", value: metrics.totalSent, color: COLORS.text },
          { label: "Delivered", value: metrics.totalDelivered, color: COLORS.success },
          { label: "Bounced", value: metrics.totalBounced, color: "#f87171" },
          { label: "Delivery Rate", value: `${metrics.avgDeliveryRate.toFixed(2)}%`, color: COLORS.success },
          { label: "Bounce Rate", value: `${metrics.bounceRate.toFixed(2)}%`, color: "#f87171" },
        ].map((stat, i) => (
          <div key={i} style={{ 
            padding: 16, 
            background: COLORS.card, 
            border: `1px solid ${COLORS.cardBorder}`, 
            borderRadius: 8,
            textAlign: "center"
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", marginBottom: 4 }}>
              {stat.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: stat.color }}>
              {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
            </div>
          </div>
        ))}
      </div>
      
      {/* Charts Row */}
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {/* Bounce Rate Trend */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, marginBottom: 16 }}>📈 Bounce Rate Trend by Month</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthlyTrend}>
              <XAxis dataKey="month" tick={{ fill: COLORS.textDim, fontSize: 10 }} />
              <YAxis tick={{ fill: COLORS.textDim, fontSize: 10 }} tickFormatter={v => `${v.toFixed(1)}%`} />
              <Tooltip 
                contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6 }}
                formatter={(v) => [`${v.toFixed(2)}%`, 'Bounce Rate']}
              />
              <Bar dataKey="bounceRate" fill="#f87171" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Bounce Distribution */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, marginBottom: 16 }}>📊 Campaign Bounce Rate Distribution</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={bounceDistribution} layout="vertical">
              <XAxis type="number" tick={{ fill: COLORS.textDim, fontSize: 10 }} />
              <YAxis dataKey="range" type="category" tick={{ fill: COLORS.textDim, fontSize: 10 }} width={60} />
              <Tooltip 
                contentStyle={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6 }}
                formatter={(v) => [`${v} campaigns`, 'Count']}
              />
              <Bar dataKey="count" fill={COLORS.accent} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Campaign Table - Worst Bounce Rates */}
      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.cardBorder}`, fontSize: 12, fontWeight: 600, color: COLORS.text }}>
          📋 Campaigns by Deliverability ({sortedCampaigns.length})
        </div>
        <div style={{ maxHeight: 400, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#1e293b", position: "sticky", top: 0, zIndex: 1 }}>
                <SortHeader col="campaignCode" label="Code" />
                <SortHeader col="campaignName" label="Campaign" />
                <SortHeader col="sent" label="Sent" align="right" />
                <SortHeader col="delivered" label="Delivered" align="right" />
                <SortHeader col="bounced" label="Bounced" align="right" />
                <SortHeader col="bounceRate" label="Bounce %" align="right" />
                <SortHeader col="deliveryRate" label="Delivery %" align="right" />
              </tr>
            </thead>
            <tbody>
              {sortedCampaigns.slice(0, 100).map((c, i) => (
                <tr key={c.campaignCode || i} style={{ 
                  background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)",
                  borderLeft: c.bounceRate > 2 ? "3px solid #f87171" : "3px solid transparent"
                }}>
                  <td style={{ padding: "10px 12px", color: COLORS.accent, fontFamily: "monospace", fontSize: 10, fontWeight: 600 }}>
                    {c.campaignCode}
                  </td>
                  <td style={{ padding: "10px 12px", color: COLORS.text, maxWidth: 200 }}>
                    <div style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {c.campaignName}
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: COLORS.textMuted }}>{c.sent?.toLocaleString()}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: COLORS.success }}>{c.delivered?.toLocaleString()}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: c.bounced > 0 ? "#f87171" : COLORS.textDim }}>{c.bounced?.toLocaleString()}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600, color: c.bounceRate > 2 ? "#f87171" : c.bounceRate > 1 ? COLORS.warning : COLORS.textMuted }}>
                    {c.bounceRate.toFixed(2)}%
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600, color: c.deliveryRate >= 99 ? COLORS.success : COLORS.textMuted }}>
                    {c.deliveryRate?.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LINKS TAB COMPONENT (Link Analysis)
// ─────────────────────────────────────────────────────────────────────────────
function LinksTab({ campaigns, links, mobile, brandFilter }) {
  const [sortConfig, setSortConfig] = useState({ col: 'clicks', dir: -1 });
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Categorize URL helper
  const categorize = (url) => {
    const u = (url || '').toLowerCase();
    if (u.includes('twitter') || u.includes('facebook') || u.includes('instagram') || u.includes('tiktok') || u.includes('youtube')) return 'Social Media';
    if (u.includes('testdrive') || u.includes('test-drive')) return 'Test Drive';
    if (u.includes('offers') || u.includes('deal') || u.includes('incentive')) return 'Offers & Deals';
    if (u.includes('build') && !u.includes('/build/')) return 'Build & Price';
    if (u.includes('inventory') || u.includes('search-inventory')) return 'Inventory Search';
    if (u.includes('request-quote') || u.includes('get-a-quote')) return 'Request Quote';
    if (u.includes('ev-lineup') || u.includes('electrified') || u.includes('electric')) return 'EV / Electrified';
    if (u.includes('assurance')) return 'Hyundai Assurance';
    if (u.includes('genesis') && u.includes('experience')) return 'Genesis Experience';
    if (u.includes('vehicles/') || u.includes('vehicle/')) return 'Vehicle Page';
    if (u.includes('suvs') || u.includes('sedans')) return 'Segment Page';
    if (u.includes('hyundaiusa.com/us/en?') || u.includes('headerlogo') || u.includes('genesisusa.com/?')) return 'Homepage';
    if (u.includes('genesisusa.com')) return 'Genesis.com';
    if (u.includes('hyundaiusa.com')) return 'Hyundai.com';
    if (u.includes('unsubscribe') || u.includes('optout') || u.includes('preference')) return 'Unsubscribe';
    if (u.includes('privacy') || u.includes('terms') || u.includes('legal')) return 'Legal/Privacy';
    return 'Other';
  };
  
  // Extract placement from URL
  const getPlacement = (url) => {
    const u = (url || '').toLowerCase();
    const m = u.match(/loc_([a-zA-Z0-9]+)/i);
    if (m) {
      const loc = m[1].toLowerCase();
      if (loc.includes('hero')) return 'Hero';
      if (loc.includes('header')) return 'Header';
      if (loc.includes('second')) return '2nd Module';
      if (loc.includes('third')) return '3rd Module';
      if (loc.includes('fourth')) return '4th Module';
      if (loc.includes('fifth')) return '5th Module';
      if (loc.includes('footer')) return 'Footer';
      if (loc.includes('cta')) return 'CTA';
      return loc.slice(0, 15);
    }
    if (u.includes('header')) return 'Header';
    if (u.includes('hero')) return 'Hero';
    if (u.includes('footer')) return 'Footer';
    if (u.includes('cta')) return 'CTA';
    return 'Unknown';
  };
  
  // Shorten URL for display
  const shortUrl = (url) => {
    try {
      const parsed = new URL(url);
      return parsed.hostname.replace('www.', '') + parsed.pathname.replace(/\/$/, '').slice(0, 40);
    } catch {
      return (url || '').slice(0, 50);
    }
  };
  
  // Category colors
  const catColors = {
    'Offers & Deals': '#059669', 'Homepage': '#0ea5e9', 'Vehicle Page': '#8b5cf6',
    'Test Drive': '#f59e0b', 'Build & Price': '#ec4899', 'Inventory Search': '#06b6d4',
    'Request Quote': '#14b8a6', 'EV / Electrified': '#38bdf8', 'Social Media': '#a78bfa',
    'Hyundai Assurance': '#84cc16', 'Genesis.com': '#94a3b8', 'Hyundai.com': '#0ea5e9',
    'Segment Page': '#fb923c', 'Genesis Experience': '#c084fc', 'Unsubscribe': '#f87171',
    'Legal/Privacy': '#64748b', 'Other': '#475569',
  };
  
  // Filter links by brand
  const filteredByBrand = useMemo(() => {
    if (!links || links.length === 0) return [];
    if (brandFilter === 'ALL') return links;
    return links.filter(l => l.brand === brandFilter);
  }, [links, brandFilter]);
  
  // Enrich links with category and placement
  const enrichedLinks = useMemo(() => {
    return filteredByBrand.map(l => ({
      ...l,
      category: categorize(l.linkName),
      placement: getPlacement(l.linkName),
      shortName: shortUrl(l.linkName),
    }));
  }, [filteredByBrand]);
  
  // Get unique categories
  const categories = useMemo(() => {
    const cats = [...new Set(enrichedLinks.map(l => l.category))].sort();
    return ['All', ...cats];
  }, [enrichedLinks]);
  
  // Filter and sort
  const tableData = useMemo(() => {
    let filtered = enrichedLinks;
    
    if (categoryFilter !== 'All') {
      filtered = filtered.filter(l => l.category === categoryFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(l => 
        l.linkName?.toLowerCase().includes(term) ||
        l.campaignCode?.toLowerCase().includes(term)
      );
    }
    
    const { col, dir } = sortConfig;
    return [...filtered].sort((a, b) => {
      if (col === 'clicks' || col === 'totalClicks') return dir * ((a[col] || 0) - (b[col] || 0));
      return dir * String(a[col] || '').localeCompare(String(b[col] || ''));
    });
  }, [enrichedLinks, categoryFilter, searchTerm, sortConfig]);
  
  // Aggregations
  const totalClicks = enrichedLinks.reduce((s, l) => s + (l.clicks || 0), 0);
  const uniqueLinks = new Set(enrichedLinks.map(l => l.linkName)).size;
  
  const categoryAgg = useMemo(() => {
    const agg = {};
    enrichedLinks.forEach(l => {
      agg[l.category] = (agg[l.category] || 0) + (l.clicks || 0);
    });
    return Object.entries(agg)
      .map(([name, clicks]) => ({ name, clicks }))
      .sort((a, b) => b.clicks - a.clicks);
  }, [enrichedLinks]);
  
  const placementAgg = useMemo(() => {
    const agg = {};
    enrichedLinks.forEach(l => {
      agg[l.placement] = (agg[l.placement] || 0) + (l.clicks || 0);
    });
    return Object.entries(agg)
      .map(([name, clicks]) => ({ name, clicks }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 8);
  }, [enrichedLinks]);
  
  const maxCatClicks = categoryAgg.length > 0 ? categoryAgg[0].clicks : 1;
  const maxPlaceClicks = placementAgg.length > 0 ? placementAgg[0].clicks : 1;
  
  // Sort header helper
  const SortHeader = ({ col, label, align = "left" }) => (
    <th 
      onClick={() => setSortConfig(prev => ({ col, dir: prev.col === col ? -prev.dir : -1 }))}
      style={{ 
        padding: "10px 12px", 
        textAlign: align, 
        fontWeight: 600, 
        color: sortConfig.col === col ? COLORS.text : COLORS.textMuted,
        cursor: "pointer",
        userSelect: "none",
        fontSize: 10
      }}>
      {label} {sortConfig.col === col ? (sortConfig.dir === -1 ? "↓" : "↑") : ""}
    </th>
  );
  
  // No data state
  if (!links || links.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>No Link Data Available</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, maxWidth: 400, margin: "0 auto" }}>
          Run <code style={{ background: "#1e293b", padding: "2px 6px", borderRadius: 3 }}>Process_SFMC_First_Party_Campaigns</code> notebook v1.4+ to populate link click data from <code style={{ background: "#1e293b", padding: "2px 6px", borderRadius: 3 }}>dm.dih_hyperlink_clicked_mig</code>.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary Stats */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: mobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", 
        gap: 12, 
        marginBottom: 24 
      }}>
        {[
          { label: "Total Link Clicks", value: totalClicks, color: COLORS.accent },
          { label: "Unique Links", value: uniqueLinks, color: COLORS.text },
          { label: "Link Categories", value: categoryAgg.length, color: COLORS.success },
          { label: "Campaigns w/Links", value: new Set(enrichedLinks.map(l => l.campaignCode)).size, color: COLORS.warning },
        ].map((stat, i) => (
          <div key={i} style={{ 
            padding: 16, 
            background: COLORS.card, 
            border: `1px solid ${COLORS.cardBorder}`, 
            borderRadius: 8,
            textAlign: "center"
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", marginBottom: 4 }}>
              {stat.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: stat.color }}>
              {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
            </div>
          </div>
        ))}
      </div>
      
      {/* Charts Row */}
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {/* Clicks by Category */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, marginBottom: 16 }}>📊 Clicks by Category</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {categoryAgg.slice(0, 8).map(cat => (
              <div key={cat.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 100, fontSize: 10, color: COLORS.textMuted, textAlign: "right", flexShrink: 0 }}>
                  {cat.name}
                </div>
                <div style={{ flex: 1, height: 20, background: "#1e293b", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ 
                    width: `${(cat.clicks / maxCatClicks * 100)}%`, 
                    height: "100%", 
                    background: catColors[cat.name] || COLORS.accent,
                    borderRadius: 4,
                    transition: "width 0.3s"
                  }} />
                </div>
                <div style={{ width: 60, fontSize: 10, fontWeight: 600, color: COLORS.text, textAlign: "right" }}>
                  {cat.clicks.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Clicks by Placement */}
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, marginBottom: 16 }}>📍 Clicks by Placement</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {placementAgg.map(place => (
              <div key={place.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 80, fontSize: 10, color: COLORS.textMuted, textAlign: "right", flexShrink: 0 }}>
                  {place.name}
                </div>
                <div style={{ flex: 1, height: 20, background: "#1e293b", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ 
                    width: `${(place.clicks / maxPlaceClicks * 100)}%`, 
                    height: "100%", 
                    background: COLORS.success,
                    borderRadius: 4,
                    transition: "width 0.3s"
                  }} />
                </div>
                <div style={{ width: 60, fontSize: 10, fontWeight: 600, color: COLORS.text, textAlign: "right" }}>
                  {place.clicks.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div style={{ 
        display: "flex", 
        flexWrap: "wrap",
        gap: 12, 
        marginBottom: 16,
        alignItems: "center"
      }}>
        {/* Category Filter */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: COLORS.textMuted }}>Category:</span>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              background: COLORS.card,
              border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: 6,
              padding: "6px 10px",
              color: COLORS.text,
              fontSize: 11,
              cursor: "pointer"
            }}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        
        {/* Search */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <input
            type="text"
            placeholder="Search links or campaign codes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              background: COLORS.card,
              border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: 6,
              padding: "8px 12px",
              color: COLORS.text,
              fontSize: 11
            }}
          />
        </div>
        
        {/* Result count */}
        <div style={{ fontSize: 11, color: COLORS.textMuted }}>
          Showing {tableData.length.toLocaleString()} of {enrichedLinks.length.toLocaleString()} links
        </div>
      </div>
      
      {/* Links Table */}
      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, overflow: "hidden" }}>
        <div style={{ maxHeight: 500, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#1e293b", position: "sticky", top: 0, zIndex: 1 }}>
                <SortHeader col="linkName" label="Link URL" />
                <SortHeader col="category" label="Category" />
                <SortHeader col="placement" label="Placement" />
                <SortHeader col="campaignCode" label="Campaign" />
                <SortHeader col="brand" label="Brand" />
                <SortHeader col="clicks" label="Clicks" align="right" />
              </tr>
            </thead>
            <tbody>
              {tableData.slice(0, 500).map((link, i) => (
                <tr key={`${link.campaignCode}-${link.linkName}-${i}`} style={{ 
                  background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)",
                  borderLeft: `3px solid ${catColors[link.category] || COLORS.accent}`
                }}>
                  <td style={{ padding: "10px 12px", maxWidth: 300 }}>
                    <div style={{ 
                      fontFamily: "monospace", 
                      fontSize: 10, 
                      color: COLORS.accent,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }} title={link.linkName}>
                      {link.shortName || link.linkName}
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{ 
                      display: "inline-block",
                      padding: "2px 8px",
                      background: `${catColors[link.category] || COLORS.accent}22`,
                      color: catColors[link.category] || COLORS.accent,
                      borderRadius: 4,
                      fontSize: 9,
                      fontWeight: 600
                    }}>
                      {link.category}
                    </span>
                  </td>
                  <td style={{ padding: "10px 12px", color: COLORS.textMuted, fontSize: 10 }}>
                    {link.placement}
                  </td>
                  <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: 10, color: COLORS.textDim }}>
                    {link.campaignCode}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{ 
                      fontSize: 9,
                      fontWeight: 600,
                      color: link.brand === 'HMA' ? '#3b82f6' : '#a855f7'
                    }}>
                      {link.brand}
                    </span>
                  </td>
                  <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600, color: COLORS.success }}>
                    {link.clicks?.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {tableData.length > 500 && (
          <div style={{ padding: 12, textAlign: "center", fontSize: 11, color: COLORS.textMuted, borderTop: `1px solid ${COLORS.cardBorder}` }}>
            Showing first 500 of {tableData.length.toLocaleString()} results. Use filters to narrow down.
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WAVE PERFORMANCE TAB COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function WavePerformanceTab({ campaigns, mobile }) {
  const [expandedJourneys, setExpandedJourneys] = useState(new Set());
  const [expandedWaves, setExpandedWaves] = useState(new Set());
  
  // Parse wave and audience from campaign name
  const parseWave = (name) => {
    if (name.includes('_EM3') || name.includes('EM3')) return 'EM3';
    if (name.includes('_EM2') || name.includes('EM2')) return 'EM2';
    if (name.includes('_EM1') || name.includes('EM1') || name.includes('_EM')) return 'EM1';
    return 'EM1';
  };
  
  const parseAudience = (name) => {
    if (name.includes('_CO_') || name.includes('_CO ') || name.endsWith('_CO')) return 'CO';
    if (name.includes('_HR_') || name.includes('_HR ') || name.endsWith('_HR')) return 'HR';
    if (name.includes('_PO_') || name.includes('_PO ') || name.endsWith('_PO')) return 'PO';
    return 'ALL';
  };
  
  // Group campaigns by journey → wave → individual
  const journeyData = useMemo(() => {
    const byJourney = {};
    
    campaigns.forEach(c => {
      const journey = c.groupCampaign || 'Ungrouped';
      const wave = parseWave(c.campaignName);
      const audience = parseAudience(c.campaignName);
      
      if (!byJourney[journey]) {
        byJourney[journey] = {
          name: journey,
          waves: { EM1: { campaigns: [], totals: null }, EM2: { campaigns: [], totals: null }, EM3: { campaigns: [], totals: null } },
          totals: { delivered: 0, clicked: 0, optout: 0 }
        };
      }
      
      // Add to wave
      byJourney[journey].waves[wave].campaigns.push({
        ...c,
        audience,
        wave,
        clickRate: c.delivered > 0 ? (c.clicked / c.delivered * 100) : 0,
        optoutRate: c.delivered > 0 ? (c.optout / c.delivered * 100) : 0
      });
      
      // Add to journey totals
      byJourney[journey].totals.delivered += c.delivered || 0;
      byJourney[journey].totals.clicked += c.clicked || 0;
      byJourney[journey].totals.optout += c.optout || 0;
    });
    
    // Calculate wave totals for each journey
    Object.values(byJourney).forEach(j => {
      ['EM1', 'EM2', 'EM3'].forEach(w => {
        const waveCamps = j.waves[w].campaigns;
        if (waveCamps.length > 0) {
          const delivered = waveCamps.reduce((s, c) => s + (c.delivered || 0), 0);
          const clicked = waveCamps.reduce((s, c) => s + (c.clicked || 0), 0);
          const optout = waveCamps.reduce((s, c) => s + (c.optout || 0), 0);
          j.waves[w].totals = {
            delivered,
            clicked,
            optout,
            clickRate: delivered > 0 ? (clicked / delivered * 100) : 0,
            optoutRate: delivered > 0 ? (optout / delivered * 100) : 0
          };
        }
      });
      
      // Calculate journey totals rates
      j.totals.clickRate = j.totals.delivered > 0 ? (j.totals.clicked / j.totals.delivered * 100) : 0;
      j.totals.optoutRate = j.totals.delivered > 0 ? (j.totals.optout / j.totals.delivered * 100) : 0;
    });
    
    // Sort by total delivered desc
    return Object.values(byJourney).sort((a, b) => b.totals.delivered - a.totals.delivered);
  }, [campaigns]);
  
  // Grand totals
  const grandTotals = useMemo(() => {
    const delivered = campaigns.reduce((s, c) => s + (c.delivered || 0), 0);
    const clicked = campaigns.reduce((s, c) => s + (c.clicked || 0), 0);
    const optout = campaigns.reduce((s, c) => s + (c.optout || 0), 0);
    return {
      delivered,
      clicked,
      optout,
      clickRate: delivered > 0 ? (clicked / delivered * 100) : 0,
      optoutRate: delivered > 0 ? (optout / delivered * 100) : 0
    };
  }, [campaigns]);
  
  const toggleJourney = (name) => {
    setExpandedJourneys(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  
  const toggleWave = (key) => {
    setExpandedWaves(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  
  // Styled row component
  const MetricRow = ({ label, data, level = 0, expandable = false, expanded = false, onToggle, highlight = false }) => {
    const indent = level * 20;
    const bgColor = level === 0 ? "#1e3a5f" : level === 1 ? "#1e293b" : "transparent";
    const fontWeight = level <= 1 ? 600 : 400;
    
    return (
      <tr 
        onClick={expandable ? onToggle : undefined}
        style={{ 
          background: highlight ? "rgba(14, 165, 233, 0.15)" : bgColor,
          cursor: expandable ? "pointer" : "default",
          borderBottom: `1px solid ${COLORS.cardBorder}`
        }}
      >
        <td style={{ padding: "10px 12px", paddingLeft: 12 + indent, color: COLORS.text, fontWeight }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {expandable && (
              <span style={{ fontSize: 10, color: COLORS.textDim, width: 16 }}>
                {expanded ? "▼" : "▶"}
              </span>
            )}
            {!expandable && level > 0 && <span style={{ width: 16 }} />}
            {label}
          </span>
        </td>
        <td style={{ padding: "10px 12px", textAlign: "right", color: COLORS.text, fontWeight }}>
          {data.delivered?.toLocaleString()}
        </td>
        <td style={{ padding: "10px 12px", textAlign: "right", color: COLORS.accent, fontWeight }}>
          {data.clicked?.toLocaleString()}
        </td>
        <td style={{ padding: "10px 12px", textAlign: "right", color: COLORS.accent, fontWeight }}>
          {data.clickRate?.toFixed(2)}%
        </td>
        <td style={{ padding: "10px 12px", textAlign: "right", color: data.optout > 0 ? "#f87171" : COLORS.textDim, fontWeight }}>
          {data.optout?.toLocaleString()}
        </td>
        <td style={{ padding: "10px 12px", textAlign: "right", color: data.optoutRate > 0.5 ? "#f87171" : COLORS.textDim, fontWeight }}>
          {data.optoutRate?.toFixed(2)}%
        </td>
      </tr>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>Wave Performance by Journey</div>
        <div style={{ fontSize: 11, color: COLORS.textDim }}>
          Metrics by wave (EM1 → EM2 → EM3) progression • Click to expand • Opens excluded per privacy policy
        </div>
      </div>
      
      {/* Summary Stats */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: mobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)", 
        gap: 12, 
        marginBottom: 24 
      }}>
        {[
          { label: "Total Delivered", value: grandTotals.delivered, color: COLORS.text },
          { label: "Total Clicks", value: grandTotals.clicked, color: COLORS.accent },
          { label: "Click Rate", value: `${grandTotals.clickRate.toFixed(2)}%`, color: COLORS.accent },
          { label: "Total Opt-Outs", value: grandTotals.optout, color: "#f87171" },
          { label: "Opt-Out Rate", value: `${grandTotals.optoutRate.toFixed(2)}%`, color: "#f87171" },
        ].map((stat, i) => (
          <div key={i} style={{ 
            padding: 16, 
            background: COLORS.card, 
            border: `1px solid ${COLORS.cardBorder}`, 
            borderRadius: 8,
            textAlign: "center"
          }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", marginBottom: 4 }}>
              {stat.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: stat.color }}>
              {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
            </div>
          </div>
        ))}
      </div>
      
      {/* Main Table */}
      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ background: "#0f172a" }}>
              <th style={{ padding: "12px", textAlign: "left", fontWeight: 600, color: COLORS.textMuted, width: "35%" }}>
                Journey / Wave / Campaign
              </th>
              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600, color: COLORS.textMuted }}>Delivered</th>
              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600, color: COLORS.textMuted }}>Clicks</th>
              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600, color: COLORS.textMuted }}>Click Rate</th>
              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600, color: COLORS.textMuted }}>Opt-Outs</th>
              <th style={{ padding: "12px", textAlign: "right", fontWeight: 600, color: COLORS.textMuted }}>Opt-Out Rate</th>
            </tr>
          </thead>
          <tbody>
            {journeyData.map((journey, ji) => {
              const journeyExpanded = expandedJourneys.has(journey.name);
              const hasWaves = Object.values(journey.waves).some(w => w.totals);
              
              return (
                <Fragment key={journey.name}>
                  {/* Journey Row */}
                  <MetricRow 
                    label={journey.name}
                    data={journey.totals}
                    level={0}
                    expandable={hasWaves}
                    expanded={journeyExpanded}
                    onToggle={() => toggleJourney(journey.name)}
                  />
                  
                  {/* Wave Rows (if expanded) */}
                  {journeyExpanded && ['EM1', 'EM2', 'EM3'].map(wave => {
                    const waveData = journey.waves[wave];
                    if (!waveData.totals) return null;
                    
                    const waveKey = `${journey.name}-${wave}`;
                    const waveExpanded = expandedWaves.has(waveKey);
                    
                    return (
                      <Fragment key={waveKey}>
                        {/* Wave Total Row */}
                        <MetricRow 
                          label={wave}
                          data={waveData.totals}
                          level={1}
                          expandable={waveData.campaigns.length > 0}
                          expanded={waveExpanded}
                          onToggle={() => toggleWave(waveKey)}
                        />
                        
                        {/* Campaign Rows (if wave expanded) */}
                        {waveExpanded && waveData.campaigns
                          .sort((a, b) => b.delivered - a.delivered)
                          .map((camp, ci) => (
                            <MetricRow 
                              key={camp.campaignCode}
                              label={
                                <span>
                                  <span style={{ color: COLORS.accent, fontFamily: "monospace", fontSize: 9, marginRight: 8 }}>
                                    {camp.campaignCode}
                                  </span>
                                  {camp.campaignName.replace(/\[U\]\s*/, '').slice(0, 40)}
                                  <span style={{ 
                                    marginLeft: 8, 
                                    padding: "2px 6px", 
                                    background: camp.audience === 'CO' ? "#f59e0b" : camp.audience === 'HR' ? "#22c55e" : "#8b5cf6",
                                    color: "#fff",
                                    borderRadius: 3,
                                    fontSize: 8,
                                    fontWeight: 600
                                  }}>
                                    {camp.audience}
                                  </span>
                                </span>
                              }
                              data={camp}
                              level={2}
                            />
                          ))
                        }
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            })}
            
            {/* Grand Total Row */}
            <MetricRow 
              label="Grand Total"
              data={grandTotals}
              level={0}
              highlight={true}
            />
          </tbody>
        </table>
      </div>
      
      {/* Legend */}
      <div style={{ marginTop: 16, display: "flex", gap: 16, fontSize: 10, color: COLORS.textDim }}>
        <span><span style={{ padding: "2px 6px", background: "#f59e0b", color: "#fff", borderRadius: 3, marginRight: 4 }}>CO</span> Current Owners</span>
        <span><span style={{ padding: "2px 6px", background: "#22c55e", color: "#fff", borderRadius: 3, marginRight: 4 }}>HR</span> Hand Raisers</span>
        <span><span style={{ padding: "2px 6px", background: "#8b5cf6", color: "#fff", borderRadius: 3, marginRight: 4 }}>PO</span> Previous Owners</span>
      </div>
    </div>
  );
}

// ── Default export wraps dashboard in auth gate ─────────────────────────
export default function FirstPartyINNsight() {
  return (
    <AuthGate>
      {({ user, logout }) => (
        <FirstPartyINNsightInner user={user} logout={logout} />
      )}
    </AuthGate>
  );
}
